import type { Translation } from '../i18n-types';

const en = {
	common: {
		actions: 'Actions',
		retry: 'Retry',
		showMore: 'Show more',
		showLess: 'Show less',
		loading: 'Loading',
		search: 'Search',
		clean: 'Clean',
		today: 'Today',
		filter: 'Filter',
		noData: 'No data',
		noFilteredData: 'No data is matching specified filters',
		currency: 'Currency',
		dataLoadFailed: 'Failed to load data',
		date: 'Date',
		dateFrom: 'Date from',
		dateTo: 'Date to',
		title: 'Title',
		status: 'Status',
		error: 'Error',
		loadMore: 'Load more',
		amount: 'Amount',
		yes: 'Yes',
		no: 'No',
		unknown: 'Unknown',
		continue: 'Continue',
		cancel: 'Cancel',
		confirm: 'Confirm',
		back: 'Back',
		download: 'Download',
		localeSwitcher: 'EN',
		edit: 'Edit',
		save: 'Save',
		saveChanges: 'Save changes',
		saveChangesAndClose: 'Save changes and close',
		saveAndClose: 'Save and close',
		select: 'Select',
		close: 'Close',
		delete: 'Delete',
		remove: 'Remove',
		removeAndDelete: 'Remove and delete',
		client: 'Client',
		creationDate: 'Creation date',
		updatedAt: 'Last update',
		updatedDate: 'Date last updated',
		periodicity: 'Periodicity',
		periodicityMap: {
			daily: 'Daily',
			dailyShort: 'day',
			weekly: 'Weekly',
			weeklyShort: 'week',
			monthly: 'Monthly',
			monthlyShort: 'month',
			annually: 'Annually',
			annuallyShort: 'year',
			semiAnnually: 'Semi-annually',
			semiAnnuallyShort: 'half year',
			quarterly: 'Quarterly',
			quarterlyShort: 'quarter',
			indefinite: 'Indefinite',
		},
		cancelProcess: 'Cancel process',
		sortCode: 'Sort code',
		account: 'Account',
		accountNumber: 'Account number',
		bankCode: 'Bank code',
		invalidFormat: 'Invalid format',
		unexpectedApiError:
			'Unexpected error occurred during communication with servers',
		emptyCharacter: '-',
		forwardSlash: '/',
		clearAllFilters: 'Clear all filters',
		from: 'From',
		to: 'To',
		tryAgain: 'Try again',
		pid: 'PID',
		user: 'User',
		pageNotFound: {
			title: 'Page not found',
			description:
				'The page you are looking for does not exist. Please check the your URL address.',
		},
		genders: {
			male: 'male',
			female: 'female',
			unknown: 'unknown',
		},
		sendForApproval: 'Send for approval',
		accountProductCodes: {
			accountProtected: 'Protected account',
			currentAccountBasic: 'Current account',
			currentAccountChild: 'Child current account',
			currentAccountSecondary: 'Secondary current account',
			currentAccountShared: 'Shared current account',
			savingsAccountBasic: 'Basic savings account',
			savingsAccountCashReserve: 'Cash reserve savings account',
			savingsAccountChild: 'Child savings account',
			savingsAccountGoal: 'Goal savings account',
			savingsAccountGoalChild: 'Child goal savings account',
			savingsAccountShared: 'Shared savings account',
			savingsGoalShared: 'Shared goal savings account',
			termDeposit: 'Term deposit',
		},
		placeholders: {
			select: 'Select',
		},
		oneYear: 'year',
		twoToFourYears: 'years',
		years: 'years',
		dateCompose: {
			separator: ', ',
			year: '{0} {{years|year|years}}',
			month: '{0} {{months|month|months}}',
		},
		phone: {
			phonePrefix: 'Prefix',
			phonePrefixSearch: 'Search prefix',
			phoneNumberWithoutPrefix: 'Phone number',
		},
		showClient: 'Show client',
		showAllClientRequests: 'Show all client requests',
		showAllClientActivations: 'Show all client activations',
		showAllClientVerifications: 'Show all client verifications',
		showAccountDetail: 'Show account detail',
		advancedSearch: {
			inputs: {
				lastName: 'Last name',
				firstName: 'First name',
				identityCardNumber: 'Identity card number',
				birthNumber: 'Birth number',
				birthday: 'Birthday',
				email: 'Email',
				pidUuid: 'PID',
			},
		},
		additionalData: 'Additional data',
		descriptiveNumber: ' d.n. {houseNumber}',
		registrationNumber: ' r.n. {evidenceNumber}',
		errors: {
			unknownTechnicalError: 'Unknown technical error',
			foundErrors: 'Found errors',
		},
		actionButton: 'Actions',
		create: 'Create',
		type: 'Type',
		content: 'Content',
		send: 'Send',
		update: 'Update',
		documentType: {
			idCard: 'Identity card',
			passport: 'Passport',
		},
		functionUnderDevelopment: 'Function under development',
		noContent: 'No content',
		pan: 'Card number',
		email: 'E-mail',
		pidUuid: 'Client PID',
		currencyPlural: {
			single: 'currency component',
			few: 'currency components',
			plural: 'currency components',
		},
		charactersPlural: {
			single: 'character',
			few: 'characters',
			plural: 'characters',
		},
		companies: {
			partners: 'Partners',
		},
		advisorRoleRefresh: {
			dialogTitle: 'Update client rights',
			rightsCouldNotBeUpdated: 'Rights could not be updated',
			rightsUpdated: 'Rights updated',
			unableToUpdateRightsUsingTheGivenDocument:
				'Unable to update rights using the given document',
			dialogDescription:
				'Downloads current client rights settings from PFS and updates access in Dashboard.',
			searchClientByPid: 'Search client by PID',
			searchClientByDocument: 'Search client by document',
			documentNumber: 'Document number',
			documentCountry: 'Document country',
			documentType: 'Document type',
		},
		notSpecified: 'Not specified',
		noKeep: 'No, keep',
		yesProceed: 'Yes, {action}',
		noDocuments: 'No documents',
	},
	authGuard: {
		dataNotAccessible:
			"The request cannot be fulfilled. Contact the bank's support.",
	},
	loginPage: {
		companyName: 'Partners',
		productName: 'Back Office',
		login: 'Login',
		description:
			'To log in, use the Partners app on your mobile phone or your Microsoft account.',
		timer: 'Code expires in {seconds}',
		timerExpired:
			'QR code expired! To renew the QR code, please reload the page.',
		refreshQrCode: 'Refresh QR code',
		microsoftLogin: 'Log in with Microsoft account',
		fipLocalLogin: 'FIP Login',
		e2eLogin: 'E2E Login',
		failedToGenerateQrCode: 'QR code generation failed',
		insufficientRightsDialog: {
			title: 'Insufficient rights',
			description:
				'You do not have sufficient permissions to access the application. If you need access, please contact your supervisor.',
			confirmationButton: 'OK',
		},
	},
	notFoundPage: {
		title: 'This page does not exist',
	},
	appLayout: {
		searchClient: 'Search client',
		searchTerminHint: 'Name, email, date of birth',
		noClientFound: 'No client found',
		lastSeenClients: 'Last seen clients',
		logout: 'Logout',
		profileMenu: {
			profile: 'Profile',
			serviceSettings: 'Service settings',
			logout: 'Logout',
		},
		fipFooter: {
			infoLineForAdvisors: 'Info line for advisors:',
			infoLineHours: '(Mo to Fr 8-20h)',
			emailSupport: 'Email support:',
		},
		advancedClientSearch: {
			label: 'Advanced search',
			tabs: {
				client: 'Client',
				account: 'Account',
			},
			accountSearch: {
				inputLabel: 'Number',
				inputPlaceholder: 'Search by account number, account ID or IBAN ...',
			},
		},
	},
	securedRouterLayout: {
		tokenExpiredDialog: {
			title: 'You were logged out',
			descriptionBank:
				'Unexpected logout occurred, or you tried to load a page without logging in first..',
			descriptionFip:
				'Unexpected logout occurred. To login again, refresh FIP and page with Dashboard.',
			login: 'Log in',
		},
	},
	fipSideNavigationComponent: {
		home: 'Home',
		clientSelect: 'Client select',
	},
	fipHomePage: {
		pageTitle: 'What we solve together',
		nothingThatNeedsYourAttention: 'Nothing that needs your attention',
		missingCertification: {
			bankAndLoan: {
				title: "You don't have a valid banking and loan certification.",
				description:
					"For bank clients, banking data won't be available. To access the requested data, complete the missing certification at Partners Financial Services.",
			},
			loan: {
				title: "You don't have a valid loan certification.",
				description:
					"For bank clients, loan data won't be available. To access the requested data, complete the missing certification at Partners Financial Services.",
			},
			bank: {
				title: "You don't have a valid banking certification.",
				description:
					"For bank clients, banking data won't be available. To access the requested data, complete the missing certification at Partners Financial Services.",
			},
		},
	},
	fipClientSelectPage: {
		pageTitle: 'Client selection',
		noFoundOtherPossibleReasonsBtn: 'Other possible reasons',
		noFoundOtherPossibleReasons: {
			dialogTitle: 'Possible reasons for not finding a client',
			noAccessToClient: {
				title: "You don't have access to the client",
				description:
					"You can only search for clients you have access to within FIP. If the client is not found in FIP, they won't be visible in the Dashboard either.",
			},
			missingRoleForClient: {
				title: "You don't have the required role for the client",
				description:
					'If you find the client in FIP, your ability to view them in the Dashboard also depends on your assigned role (e.g., Guardian). Advisors with the roles Administrator, Guardian, Full Sharing, Task Sharing, or Bubble have access. Advisors with the Structure role are restricted.',
			},
			dataNotUpdated: {
				title: 'FIP data is not up to date',
				description:
					"Did you grant yourself access to the client in FIP today, but can't find them in the Dashboard? Most likely, the FIP data hasn't been synced to the Dashboard yet (it updates once daily). If you need immediate access, update the permissions manually via the “Client selection” page using the “Actions” – “Update client rights” option.",
			},
			stillNotFound: {
				description:
					"If you're an advisor with the Administrator or Guardian role for the specific client and still can't find them, please contact support.",
			},
		},
	},
	clientLayout: {
		riskProfile: {
			medium: 'Risk profile: medium',
			high: 'Risk profile: high',
			unacceptable: 'Risk profile: unacceptable',
		},
		pep: 'PEP',
		withoutGroupConsent: 'Without group consent',
		invalidPersonalData: 'Invalid personal data',
		employee: 'Employee',
		deathClient: 'Client died',
		advisor: 'Advisor:',
		notAssigned: 'Not assigned',
		advisorId: 'ID',
		contacts: 'Contacts',
		advisorResidence: 'Advisor residence',
		viewReasonDialog: {
			title: 'Client is employee',
			explanation:
				'Specify a reason for showing a client, who is Partners employee.',
			reasonLabel: 'Reason',
			noteLabel: 'Note',
			reasons: {
				clientService: 'Client service',
				cooperationWithAuthorities: 'Cooperation with authorities',
				amlOrFraud: 'AML / Fraud',
				internalControlOrAudit: 'Internal control / Audit',
				issueOrIncident: 'Solving problem / incident',
				other: 'Other',
			},
		},
		doNotHavePermissionsToSeeClientDialog: {
			title: 'Missing permissions',
			explanation:
				"You don't have permissions to see the client. Contact helpdesk if you need access.",
		},
		nationalNumber: {
			shortcuts: {
				birthNumber: 'b.n.:',
				insuranceNumber: 'i.n.:',
			},
			tooltips: {
				birthNumber: 'Birth number',
				insuranceNumber: 'Insurance number',
				unknown: 'Unknown national identifier type',
				unspecified: 'Unspecified national identifier type',
				nationalIdentifier: 'National identifier',
			},
		},
		tooltips: {
			email: 'E-mail',
			permanentAddress: 'Permanent address',
			phoneNumber: 'Phone number',
		},
		bankIdEventType: {
			login: 'Login',
			userInfo: 'User information',
			profile: 'Person identification',
			sign: 'Document signature',
			notification: 'Notification',
			consentedScopes: 'Consents granted',
			noEventTypeChosen: 'No event type chosen',
			all: 'All',
		},
	},
	clientOverviewPage: {
		title: 'Overview',
		notifications: 'Notifications',
		lastEventsTile: {
			title: 'Last events',
			noEvents: 'No events',
			moreEvents: 'More events',
			failedToLoadEvents: 'Failed to load events',
		},
		mobileAppTile: {
			title: 'Application',
			noApplications: 'No valid activation',
			lastUsage: 'Last usage: {lastUsageDate}',
			moreActivations: 'More activations',
			failedToLoadActivations: 'Failed to load activations',
		},
		packageTile: {
			title: 'Package',
			packagePricePerMonth: '{price}/mth',
			noPackage: 'No package',
			morePackages: 'More packages',
			clientRole: 'Client role: {role}',
			failedToLoadPackages: 'Failed to load packages',
		},
		accountsTile: {
			title: 'Accounts',
			noAccounts: 'No active accounts',
			moreAccounts: 'More accounts',
			failedToLoadAccounts: 'Failed to load accounts',
			multiCurrencyIndicator: '{baseTitle} (MM)',
		},
		cardsTile: {
			title: 'Cards',
			noCards: 'No acttive cards',
			moreCards: 'More cards',
			failedToLoadCards: 'Failed to load cards',
		},
		loansTile: {
			title: 'Loans',
			noLoans: 'No active loans',
			moreLoans: 'More loans',
			failedToLoadLoans: 'Failed to load loans',
		},
		notificationTile: {
			noData: 'No notifications',
			failedToLoadNotifications: 'Notifications failed to load',
			header: {
				failedToLoadSomeNotifications: 'Part of notifications failed to load',
			},
			dialog: {
				dialogTitle: 'Notifications',
			},
			nextNotifications: 'More notifications',
			notifications: {
				clientDataEmailMissing: {
					title: 'Missing email',
					text: 'Client has no email address registered',
				},
				clientDataPhoneMissing: {
					title: 'Missing phone',
					text: 'Client has no phone number registered',
				},
				clientDataPermanentAddressMissing: {
					title: 'Missing permanent address',
					text: 'Client has no permanent address registered',
				},
				clientApplicationsBlocked: {
					title: 'Application blocked',
					text: 'Client has all valid application activations blocked.',
				},
				clientBankIdentityBlocked: {
					title: 'Bank identity blocked',
					text: 'Client has blocked usage of bank identity',
				},
				clientNewActivationsBlocked: {
					title: 'New application activations blocked',
					text: 'Client is not able to activate the application on any device',
				},
				clientAccountsInDebet: {
					single: {
						title: 'Account in debit',
						text: 'Account {accountNumber} is in debit {amount}',
						textMultipleCurrencies:
							'Account {accountNumber} and {currencyCount} {currencyCountPlural} in debit',
					},
					few: {
						title: 'Accounts in debit',
						text: '{count} accounts in debit for a total amount of {amount}',
						textMultipleCurrencies:
							'{count} accounts and {currencyCount} {currencyCountPlural} in debit',
					},
					multiple: {
						title: 'Accounts in debit',
						text: '{count} accounts in debit for a total amount of {amount}',
						textMultipleCurrencies:
							'{count} accounts and {currencyCount} {currencyCountPlural} in debit',
					},
				},
				clientAccountsWithExecution: {
					single: {
						title: 'Distraint',
						text: 'Account {iban} has distraint for {amount}',
					},
					multiple: {
						title: 'Distraints',
						text: '{count} accounts have distraints for a total amount of {amount}',
					},
				},
				clientAccountsInTermination: {
					single: {
						title: 'Account termination',
						text: 'Account {iban} is in termination process',
					},
					few: {
						title: 'Account termination',
						text: '{count} accounts are in termination process',
					},
					multiple: {
						title: 'Account termination',
						text: '{count} accounts are in termination process',
					},
				},
				clientAccountsBlocked: {
					single: {
						title: 'Blocked account',
						text: 'Account {iban} is blocked',
					},
					few: {
						title: 'Blocked accounts',
						text: '{count} accounts are blocked',
					},
					multiple: {
						title: 'Blocked accounts',
						text: '{count} accounts are blocked',
					},
				},
				clientAccountsTermDepositWaitingForPayment: {
					title: 'Term deposit is not active',
					text: 'Account {iban} has not received the initial payment yet',
				},
				clientCardsBlocked: {
					single: {
						title: 'Blocked or inactive card',
						text: 'Card {pan} can not be used for payments',
					},
					few: {
						title: 'Blocked or inactive cards',
						text: '{count} cards can not be used for payments',
					},
					multiple: {
						title: 'Blocked or inactive cards',
						text: '{count} cards can not be used for payments',
					},
				},
				clientMobilityProcessingFailed: {
					single: {
						title: 'Stuck mobility',
						text: 'Mobility on account {selectedAccountIban} (to {mobilityDate}) is not processing',
					},
					few: {
						title: 'Stuck mobilities',
						text: '{count} mobilities {formattedAccountsText} are not processing',
					},
					multiple: {
						title: 'Stuck mobilities',
						text: '{count} mobilities {formattedAccountsText} are not processing',
					},
				},
				clientMobilityWaitingForSignature: {
					single: {
						title: 'Mobility waiting for client approval',
						text: 'Mobility on account {selectedAccountIban} (to {mobilityDate}) is waiting for signature in mobile app',
					},
					few: {
						title: 'Mobilities waiting for client approval',
						text: '{count} mobilities {formattedAccountsText} are waiting for signature in mobile app',
					},
					multiple: {
						title: 'Mobilities waiting for client approval',
						text: '{count} mobilities {formattedAccountsText} are waiting for signature in mobile app',
					},
				},
				clientMobilityProcessingFailedOrWaitingForSignature: {
					accounts: {
						single: 'on account {accountsText}',
						multiple: 'on accounts {accountsText}',
					},
				},
				clientPidBlocked: {
					title: 'PID blocked',
					text: "Client's PID is blocked",
				},
			},
		},
		pfsOnboardingApplicationsTile: {
			title: 'Onboarding from PFS',
			onboardingApplicationName: 'Application for "{productName}"',
			noOnboardingApplications: 'No applications',
			moreOnboardingApplications: 'More applications',
			failedToLoadOnboardingApplications: 'Failed to load applications',
		},
		actions: {
			verifyClientForRemoteService: 'Verify client for remote service',
		},
	},
	clientEventsPage: {
		title: 'Events',
		solver: 'Řešitel',
		noEventDetail: 'No event detail',
		eventTypes: {
			login: 'Application login',
			logout: 'Application logout',
			appActivation: 'Application activation',
			notificationSettings: 'Notification settings changed',
			unknown: 'Unknown event',
		},
		eventStates: {
			initiated: 'Initiated',
			completed: 'Completed',
			cancelled: 'Cancelled',
			failed: 'Failed',
			stepCompleted: 'Step completed',
			taskFailed: 'Task failed',
			taskSuccess: 'Task success',
			allTasksFinished: 'All tasks finished',
		},
		eventProperties: {
			deviceModel: 'Device model',
			deviceId: 'Device ID',
			deviceType: 'Device type',
			deviceOs: 'Operating system of the device',
			appVersion: 'Version of the Partners application',
		},
		taskTypes: {
			cancelProcess: 'Cancel process',
			commitActivation: 'Commit activation',
			commitUser: 'Client confirmation',
			completeProcess: 'Complete process',
			createActivation: 'Create activation',
			createProcess: 'Create process',
			expireProcess: 'Process expiration',
			importUser: 'Import client',
			initializeActivation: 'Initialize activation',
			initiateIdentityVerification: 'Initiate identity verification',
			initiateUserUpsert: 'Initiate client import',
			prepareDocumentsToSign: 'Prepare documents for signing',
			processIdentityVerification: 'Process identity verification',
			processSignedDocuments: 'Process signed documents',
			processUserUpsert: 'Process client import',
			resetDocumentsToSign: 'Reset document preparation for signing',
			resetIdentityVerification: 'Reset identity verification',
			resetUserUpsert: 'Reset client import',
			verifyIdentity: 'Verify identity',
		},
		labels: {
			step: 'Step:',
			separator: ' · ',
			deviceName: 'Device name',
			phoneNumber: 'Phone',
			activationId: 'Activation ID',
			notificationsEnabled: 'Notifications enabled',
			notificationsForPayments: 'Payment notifications',
			marketingCommunication: 'Marketing communication',
			eventId: 'Event ID',
			error: 'Error',
		},
	},
	clientEventsNewPage: {
		title: 'Events - new',
		noEvents: 'No events',
		failedToLoadEvents: 'Failed to load events',
		failedToLoadScope: 'Failed to load data',
		infoText: 'Number of found events: {eventsAmount}',
		table: {
			header: {
				status: 'Status',
			},
		},
		status: {
			success: 'Completed',
			failed: 'Failed',
		},
		communicationDetail: {
			sentCommunication: 'Sent communication',
		},
	},
	clientDetailPage: {
		title: 'Client data',
		buttons: {
			change: 'Change',
			historyOfChanges: 'History of changes',
			addToBlacklist: 'Add to blacklist',
			removeFromBlacklist: 'Remove from blacklist',
		},
		createdAt: 'Created at',
		carriedOutBy: 'Carried out by',
		advancedDataPreview: 'Advanced data view',
		personalData: 'Personal data',
		basicData: 'Basic data',
		nationalIdentifiers: 'National identifiers',
		nationalIdentifierNumber: 'Number',
		countryOfIssue: 'Country of issue',
		phone: 'Phone',
		aml: 'AML',
		additionalData: 'Additional data',
		contactData: 'Contact data',
		permanentAddress: 'Permanent address',
		contactAddress: 'Contact address',
		unknownAddress: 'Address (unknown type)',
		firstName: 'First name',
		lastName: 'Last name',
		middleName: 'Middle name',
		birthDate: 'Birth date',
		birthLocation: 'Birth place',
		gender: 'Sex',
		nationality: 'Nationality',
		titlePrefix: 'Title prefix',
		titleSuffix: 'Title suffix',
		legalCapacity: 'Legal capacity',
		legalAge: 'Legal age',
		dateOfDeath: 'Date of death',
		additionalNationalities: 'Additional nationalities',
		phoneNumber: 'Phone number',
		phoneNumberPrimary: 'Phone number (primary)',
		email: 'E-mail',
		emailPrimary: 'E-mail (primary)',
		changeEmailDialog: {
			snackbar: {
				emailChangedSuccessfully: 'Email was changed',
				emailsChangedSuccessfully: 'Emails were changed',
				emailsChangeFailedPartially: 'One of the emails was not changed',
				emailsChangeFailed: 'Emails were not changed',
				emailChangeFailed: 'Email was not changed',
			},
			title: 'Change e-mail',
		},
		changeNameDialog: {
			snackbar: {
				success: 'Name was changed',
				failed: 'Name change failed',
			},
			title: 'Change name',
		},
		changePhoneDialog: {
			snackbar: {
				phoneChangedSuccessfully: 'Phone number was changed',
				phonesChangedSuccessfully: 'Phone numbers were changed',
				phonesChangeFailedPartially: 'One of the phone numbers was not changed',
				phonesChangeFailed: 'Phone numbers were not changed',
				phoneChangeFailed: 'Phone number was not changed',
			},
			title: 'Change phone',
		},
		changeNationalIdentifierDialog: {
			snackbar: {
				success: 'National identifier was changed',
				failed: 'National identifier was not changed',
			},
			title: 'Change national identifier',
			labels: {
				type: 'Type',
			},
		},
		changeAddressDialog: {
			searchAddress: 'Search address',
			noAddressFound: 'No address found',
			snackbar: {
				success: 'Address was changed',
				failed: 'Address was not changed',
			},
			titles: {
				permanentResidence: 'Change permanent address',
				contactResidence: 'Change contact address',
				unknown: 'Change address',
			},
			tabs: {
				addressInCzechRepublic: 'Address in Czech Republic',
				addressOutsideCzechRepublic: 'Address outside Czech Republic',
			},
		},
		deleteAddressDialog: {
			title: 'Delete address?',
			descriptions: {
				permanentResidence:
					"Permanent address will be deleted. This action can't be undone.",
				contactResidence:
					"Contact address will be deleted. This action can't be undone.",
				unknown: "Address will be deleted. This action can't be undone.",
			},
			snackbar: {
				success: 'Address was deleted',
				failed: 'Address was not deleted',
			},
		},
		changeIdCardDialog: {
			title: 'Change document',
			number: 'Number',
			type: 'Type',
			issuerCountry: 'Country of issue',
			snackbar: {
				success: 'Document changed',
				failed: 'Document could not be changed',
			},
		},
		deleteIdCardDialog: {
			title: 'Delete document?',
			description:
				'Are you sure you want to delete document "{documentType} {number}", issuer country "{country}"?',
			snackbar: {
				success: 'Document deleted',
				failed: 'Document could not be deleted',
			},
		},
		addresses: 'Addresses',
		address: {
			city: 'City',
			cityPart: 'City part',
			country: 'Country',
			evidenceNumber: 'Evidence number',
			houseNumber: 'House number',
			street: 'Street',
			streetNumber: 'Street number',
			zipCode: 'Zip code',
		},
		documents: 'Documents',
		identityDocumentTypes: {
			bc: 'Birth certificate',
			ca: 'Temporary travel document',
			dl: 'Driving license',
			id: 'Identity card',
			ie: 'Form with temporary residence',
			ir: 'Residence permit',
			ix: 'Book with residence permit',
			op: 'Identity card (without chip)',
			p: 'Passport',
			ps: 'Residential label',
			unknown: 'Unknown',
			vs: 'Visa permit label',
		},
		documentNumber: 'Document number',
		issuer: 'Issuer',
		issuerCountry: 'Issuer country',
		issueDate: 'Issue date',
		validTo: 'Valid until',
		clientData: 'Client data',
		taxDomicile: 'Tax domicile',
		FATCA: 'FATCA',
		politicallyExposedPerson: 'Politically exposed person',
		clientCreationDay: 'client creation date',
		consents: 'Consents',
		consentTypes: {
			gdprConsent: 'Group marketing',
			mobileAppTermsAndConditionsConsent: 'Mobile app terms and conditions',
			thirdParties: 'Third parties',
			childOnboardingMandate: 'Child onboarding mandate',
			unknown: 'Unknown',
		},
		notGranted: 'Not granted',
		granted: 'Granted',
		revoked: 'Revoked',
		relatedPeople: 'Related people',
		relationshipTypes: {
			employeeGuardianOf: 'Ward employer',
			guardedBy: 'Guardian',
			guardedByEmployee: 'Guardian employee',
			guardianOf: 'Ward',
			childToParent: 'Parent',
			legalRepresentativeOf: 'Represented party',
			legalRepresentedBy: 'Legal representative',
			loaEmpoweredBy: 'Principal',
			loaEmpoweringWho: 'Agent',
			parentToChild: 'Child',
			unknown: 'Unknown',
		},
		documentProvingRelationship: 'Document proving relationship to {name}',
		child: 'Child',
		parent: 'Parent',
		anotherPerson: 'Another person',
		doNotHavePermissionsToToViewPerson:
			'You do not have sufficient permissions to view the person',
		identityConsent: 'Identity consent',
		marketingConsent: 'Marketing consent',
		mobileAppTermsAndConditionsConsent:
			'Mobile app terms and condition consent',
		maritalStatus: 'Marital status',
		maritalStatuses: {
			cohabitation: 'Cohabitation',
			married: 'Married',
			divorced: 'Divorced',
			registeredPartnership: 'Registered partnership',
			registeredPartnershipCancelled: 'Registered partnership cancelled',
			registeredPartnershipWidowed: 'Registered partnership widowed',
			widowed: 'Widowed',
			single: 'Single',
			unknown: 'Unknown',
			partnership: 'Partnership',
			partnershipCanceled: 'Partnership cancelled',
			lapsedMarriage: 'Lapsed marriage',
			separated: 'Separated',
		},
		riskProfile: 'Risk profile',
		riskProfiles: {
			high: 'High',
			low: 'Low',
			medium: 'Medium',
			unacceptable: 'Unacceptable',
			unknown: 'Unknown',
		},
		regularSourceOfIncome: 'Regular source of income',
		regularSourceOfIncomes: {
			alimony: 'Alimony',
			agreement: 'Employment agreement',
			businessActivities: 'Business activities',
			capitalProperty: 'Capital Assets income',
			dividends: 'Executive income, dividends',
			occupation: 'Main employment',
			occupationBrutto: 'Main employment - brutto',
			parentalLeave: 'Maternal, parental',
			partnerProfit: 'Partner profit income',
			partTime: 'Secondary employment',
			partTimeBrutto: 'Secondary employment - brutto',
			pension: 'Pension',
			prize: 'Prize',
			recurringBonuses: 'Regular bonuses',
			rent: 'Rental income',
			selfEmployment: 'Self-employed',
			selfEmploymentBrutto: 'Self-employed - brutto',
			socialBenefits: 'Social contribution',
			unknown: 'Unknown',
		},
		irregularSourceOfIncome: 'Irregular source of income',
		irregularSourceOfIncomes: {
			unknown: 'Unknown',
			unknownSimpleaIrregular: 'Unknown Simplea income',
			companyShareSale: 'Income from sale of company/share in company',
			realEstateSale: 'Income from sale of property',
			movableItemSale: 'Income from the sale of movable property',
			gift: 'Gift',
			heritage: 'Heritage',
			prize: 'Prize (e.g. lottery)',
			loanFromFinancialInstitution: 'A loan from a financial institution',
			loanOther: 'A loan from a non-financial institution',
			personalSavings: 'Personal savings',
			investmentProductsIncome: 'Income from investment products',
			otherIncome: 'Other income',
		},
		incomeInformationDialog: {
			buttonText: 'Show income information',
			title: "Client's income",
			totalIncome: 'Total income',
			regularIncomeSectionTitle: 'Regular income',
			irregularIncomeSectionTitle: 'Irregular income',
			sum: 'Summary',
		},
		companyIds: {
			bank: 'Partners Banka, a.s.',
			bankEmployee: 'Partners Banka employees (group)',
			mobApp: 'Partners Banka, a.s. (mobile application)',
			mobAppPar: 'Partners Banka, a.s. (mobile application)',
			mobAppSfs: 'Simplea Financial Services, a.s. SK (mobile application)',
			mobAppSk: 'Simplea Financial Services, a.s. (mobile application SK)',
			mobAppWlb: 'Whitelabel (mobile application)',
			pfs: 'Partners Financial Services, a.s.',
			pfsConsultant: 'Partners advisor (group)',
			rentea: 'Rentea penzijní společnost, a.s.',
			sfs: 'Simplea Financial Services, a.s. (SK)',
			simplea: 'Simplea pojišťovna, a.s',
			simpleaSk: 'Simplea pojišťovna, a.s. (SK)',
			unknown: 'Unknown',
			group: 'Group Partners, a.s.',
			loan: 'Partners Banka loans (group)',
			nonbank: 'Nebanka',
			ocp: 'Partners OCP',
			gepard: 'Gepard',
			vub: 'VUB',
		},
		clientFrom: 'Client from',
		clientTo: 'Client to',
		companies: 'Companies',
		clientNotManagedByAnyCompany:
			'The client is not currently a client of any company',
		trustLevel: {
			unverified: 'Unverified data',
			blocked: 'Invalid data',
		},
		failedToLoadHistoryOfChangesData: 'Failed to load data',
		noData: {
			noBasicInfo: 'No basic data',
			noAdditionalData: 'No additional data',
			noAddresses: 'No addresses',
			noDocuments: 'No documents',
			noClientAmlData: 'No AML data',
			noConsentAgreements: 'No consent agreements',
			noRelationshipPeople: 'No relationship people',
			noNationalIdentifier: 'No national identifier',
			noHistoryOfChanges: 'No history of changes',
		},
		verificationType: 'Verification type',
		verificationTypes: {
			unknown: 'Unknown',
			machine: 'System',
			amlOfficer: 'AML',
			bankId: 'Bank iD',
			client: 'Client',
			consultantFaceToFace: 'Advisor (in person)',
			consultantRemote: 'Advisor (remote)',
			consultantEdoklad: 'Advisor (eDoklad)',
			backofficeUser: 'Backoffice user',
			szr: 'Person register',
		},
		sourceClient: {
			unknown: 'Unknown',
			nonBank: 'Non-bank',
			bank: 'Partners Bank',
			bankEmployee: 'Partners Bank - employee',
			bankLoanProspect: 'Partners Bank - loan applicant',
			bankTest: 'Partners Bank TEST',
			pfs: 'Partners Financial Services',
			pfsConsultant: 'PFS advisor',
			pid: 'PID',
			pidTest: 'PID TEST',
			rentea: 'Rentea',
			sfs: 'Simplea Financial Services (SK)',
			simplea: 'Simplea',
			simpleaSk: 'Simplea (SK)',
			mobApp: 'Partners Bank - mobile application',
			mobAppSk: 'Partners Bank - mobile application (SK)',
			ocp: 'OCP',
			gepard: 'Gepard',
			orfeus: 'Orfeus',
			orfeusSk: 'Orfeus (SK)',
			vub: 'VUB',
		},
		nationalIdentifierTypes: {
			options: {
				unknown: 'Unknown',
				birthNumber: 'Birth number',
				insuranceNumber: 'Insurance number',
				unspecified: 'Unspecified',
			},
			labels: {
				unknown: 'Unknown',
				birthNumber: 'Birth number',
				insuranceNumber: 'Insurance number',
				unspecified: 'Number',
			},
		},
		verificationInvestigations: {
			historyOfDocumentCopiesMade: 'History of document copies made',
			noData: 'No history of document copies taken',
			investigationId: 'Scan (Zen iD)',
		},
		statusOfDataVerificationDialog: {
			title: 'Verification of data in basic registers',
			noVerifications: 'No verifications',
			columns: {
				company: 'Company',
				status: 'Status',
				statusChangedAt: 'Last status change',
				companyClientFrom: 'Client from {date}',
				companyClientTo: 'to {date}',
			},
			labels: {
				aifo: 'AIFO',
			},
			aisStatus: {
				unrated: 'Unrated',
				notForAisIdentification: 'Not for AIS identification',
				aisProcessing: 'AIS processing',
				aisIdentified: 'AIS identified',
				aisUpdated: 'AIS updated',
				aisUnsubscribed: 'AIS unsubscribed',
				aisNotIdentified: 'AIS not identified',
				aisPartiallyIdentified: 'AIS partially identified',
				aisPartiallyUpdated: 'AIS partially updated',
				unknown: 'Unknown',
			},
			actions: {
				identifyAis: {
					button: 'Start verification',
					snackbar: {
						success:
							'Verification request submitted. Processing may take tens of seconds.',
						failed: 'Failed to submit verification request.',
					},
				},
				updateAis: {
					button: 'Update verification',
					snackbar: {
						success:
							'Verification update request submitted. Processing may take tens of seconds.',
						failed: 'Failed to submit verification update request.',
					},
				},
			},
		},
		clientNationalIdentifierAddToBlacklistDialog: {
			title: 'Add to blacklist?',
			description:
				'The client will not be able to activate the application with identifier {id}.',
			confirmButton: 'Add to blacklist',
			snackbar: {
				success: 'Identifier added to blacklist',
				failed: 'Failed to add identifier to blacklist',
				invalidIdentifier:
					'Failed to add identifier to blacklist, invalid identifier',
				clientDoesNotExist:
					'Failed to add identifier to blacklist, client does not exist',
			},
		},
		clientNationalIdentifierRemoveFromBlacklistDialog: {
			title: 'Remove from blacklist?',
			description:
				'The client will be able to activate the application with identifier {id}.',
			confirmButton: 'Remove from blacklist',
			snackbar: {
				success: 'Identifier removed from blacklist',
				failed: 'Failed to remove identifier from blacklist',
				invalidIdentifier:
					'Failed to remove identifier from blacklist, invalid identifier',
				clientDoesNotExist:
					'Failed to remove identifier from blacklist, client does not exist',
			},
		},
		onBlacklistTag: 'On blacklist',
		onBlacklistTooltip:
			'Identifier on blacklist blocks the client from activating the application',
	},
	accountTermination: {
		layout: {
			terminatedProducts: 'Terminated products',
			balanceSettlement: 'Balance settlement',
			summary: 'Summary',
			cancelProcess: 'Cancel process',
			accountTermination: 'Account termination',
			cancelDialog: {
				title: 'Cancel process?',
				description: 'You will lose all filled information',
			},
		},
		useAccountTerminationReasonCategoriesHook: {
			categories: {
				onClientRequest: 'On client request',
				onBankRequest: 'On bank request',
				specificReasons: 'Specific reasons',
			},
		},
		useAccountTerminationReasonsConfigurationHook: {
			feeTooHigh: 'Too high fees for services',
			notEnoughProducts: 'Not enough products',
			interestRate: 'Interest rate',
			confusing: 'Inadequate product management through the application',
			other: 'Other',
			clientRequest: 'Termination on client request',
			aml: 'AML reasons (immediate)',
			fraud: 'Fraud (immediate)',
			tocViolation: 'Gross violation of terms and conditions (immediate)',
			insolvency: 'Insolvency',
			inheritance: 'Inheritance',
			limitationOfAutonomy: 'Limitation of autonomy',
			bankWithNoticePeriod:
				'Termination of the relationship with the client (2-month notice period)',
		},
		terminationProductsListComponent: {
			overdraft: {
				title: 'Overdraft',
				label: 'Overdraft amount',
				tooltip:
					'The set overdraft will be cancelled on the account cancellation date cancelled',
			},
			productListSection: {
				title: 'Standing orders, direct debits and SIPO',
				tooltip:
					'Set orders will be cancelled on the account cancellation date',
				directDebits: { label: 'Direct debit {labelIndex}' },
				standingOrders: { label: 'Standing order {labelIndex}' },
				sipo: { label: 'SIPO {labelIndex}' },
			},
			cards: {
				title: 'Payment cards',
				text: 'A debit card is issued for the account. The account will be cancelled after the 30-day card cancellation period has expired. The card will be automatically cancelled when the account cancellation process is complete.',
			},
			sharedAccountTerminationHint:
				'Account will be cancelled for the co-owner, who is ',
		},
		reasonDialog: {
			title: 'Termination reason',
			note: 'Note',
			terminateAllAccountsDialog: {
				title: 'Do you want to continue with single account?',
				description:
					'Account cancellation {separator} usually needs to be made on all client accounts. Do you really want to continue with only one selected account?',
				startAllAccountsTermination: 'Start cancellation of all accounts',
			},
		},
		productsPage: {
			balance: 'Balance',
			account: {
				label: 'Account name',
			},
			accountCannotBeTerminated: {
				title: 'Account cannot be terminated',
				subtitlePart1: 'Account termination',
				subtitlePart2: 'is blocked by:',
				accountTerminationReasonTitle: 'On account',
				terminationReasons: {
					blockingExists: 'Blocking exists',
					existingTermination: 'Account termination in progress to date',
				},
			},
		},
		settlementPage: {
			accountSectionTitle: 'Settlement accounts',
			accountSectionTooltip:
				'Account to which the remaining financial resources from the terminated account will be sent. When canceling the main current account, it is possible to send the funds to an account outside Partners Bank. The account must be maintained in CZK currency.',
			accountSectionHint:
				'Balance of the terminated account will be divided equally among the listed accounts',
			internalBankAccount: 'Internal bank account',
			dateSectionTitle: 'Termination date',
			dateSectionTooltip:
				'Date of account termination. The client can use the account until the termination date.',
			documentsSectionTitle: 'Documents',
			documentsSectionTooltip:
				'Documents related to the termination request (e.g. client request) can be added to the termination process. Documents attached in the process will be automatically saved among other client documents.',
			other: 'Other',
			terminationDate: 'Date of termination',
			selectDocument: 'Select document',
			uploadDocument: 'Upload document',
			sameSettlementAndTerminatedAccountError:
				'Account for balance settlement cannot be the same as terminated account',
			settlementAccountCannotBeSubAccountOfTerminatedAccount:
				'Account for balance settlement cannot be sub-account of terminated account',
			settlementAccountIsInTerminationProcess:
				'Account for balance settlement is in termination process, select other account',
			dateMustBeAfterToday: 'Date must be today at the earliest',
			documentCardComponent: {
				deleteDocumentDialog: {
					title: 'Remove document?',
					description:
						'Document will be removed from the process, but will remain uploaded in the client documents.',
				},
				deleteAndRemoveDocumentDialog: {
					title: 'Remove and delete document?',
					description:
						'Document will be removed from the process and deleted from the client documents. Action is irreversible.',
				},
			},
		},
		summaryPage: {
			recapitulationSection: {
				title: 'Recapitulation',
				settlementAccounts: 'Settlement accounts',
				reason: 'Termination reason',
				reasonNote: 'Note',
				settlementAccount: 'Settlement account',
				terminationDate: 'Termination date',
				documentsRecapitulationSubsection: {
					title: 'Documents',
				},
			},
			terminatedProductsSection: {
				title: 'Terminated products',
			},
			authorizationTypeSection: {
				title: 'Request approval method',
				internalControl: 'Internal control',
				internalControlTooltip:
					'Account termination request will be sent to internal queue for verification and completion by another bank employee',
				byClientInApp: 'By client in mobile app',
				byClientInAppTooltip:
					'Account termination request will be sent to the client in the mobile application for approval',
			},
			resultDialog: {
				success: {
					title: 'Request sent',
					description: 'Account termination request was sent for approval',
					showRequest: 'Show request',
				},
				error: {
					title: 'Request cannot be processed',
					description: 'Account termination was unsuccessful',
				},
			},
		},
	},
	incomingMobility: {
		common: {
			dateOfMobility: 'Date of mobility',
			mobilityRange: 'Range',
			transferBalance: 'Transfer current balance',
			transferAllStandingOrdersAndDirectDebits:
				'Transfer all standing orders and direct debits',
			closeAccount: 'Close account',
		},
		incomingMobilityLayout: {
			basicInformation: 'Basic information',
			documents: 'Documents',
			standingOrdersDirectDebitSipo: 'Standing orders, Direct debit and SIPO',
			recapitulation: 'Recapitulation',
			cancelProcess: 'Cancel process',
			incomingMobility: 'Incoming mobility',
		},
		createIncomingMobility: {
			createIncomingMobilityBasicInfoPage: {
				accountInfoTitle: "Client's account in another bank",
				accountInfoTooltip:
					"Enter the client's account in another bank, from which the balance will be transferred and possibly settings (see range below) to Partners. The account must be kept in CZK currency.",
				accountPrefix: 'Prefix',
				currencyCode: 'Currency code',
				mobilityDateTitle: 'Date of mobility',
				mobilityDateTooltip:
					'The date by which the mobility to the required extent is to be carried out. The earliest possible date is the 13th business day from today.',
				mobilityDateDescription1:
					'Select a working day between {datePlaceholder} and {datePlaceholder}."',
				mobilityDateDescription2:
					'If the client will also transfer payments, verify that the entered date will not affect the maturity of the orders in the other bank.',
				mobilityDateCaption: 'Choose date between {minDate} and {maxDate}',
				mobilityDateError:
					'The date must be a working day between {minDate} and {maxDate}',
				mobilityDateNotLoadedError: 'Information about date was not loaded',
				mobilityRangeTooltip:
					'Choose one of the most common ranges of mobility. If the client requires a different range, use the extra form available on WIKI',
				incomingMobilityValidationFailed: 'Validation failed',
				otherOptions: 'Other options',
				mobilityScopeMinLength:
					'You must select at least one mobility scope option',
			},
			createIncomingMobilityRecapitulationPage: {
				otherBankCurrentAccountTitle: "Client's account in another bank",
				partnersAccountTitle: 'Account in Partners',
				parametersTitle: 'Parameters',
				mobilityScope: 'Scope',
				resultDialog: {
					success: {
						title: 'Request sent',
						description: 'Account transfer request was sent for approval',
						showRequestButton: 'Show request',
					},
					error: {
						title: 'Request cannot be processed',
						description: 'Account transfer was unsuccessful',
					},
				},
			},
			bankBackOfficeGatewayErrors: {
				fixableErrors: {
					title: 'Fixable errors',
					texts: {
						validationAccountErrorMissingBirthday: 'Missing birth date',
						validationAccountErrorMissingName: 'Missing name or surname',
						validationAccountErrorMissingPrimaryIdCard:
							'Missing primary document',
						validationAccountErrorMissingNationalNumber:
							'Missing national number',
						validationAccountErrorMissingAddress:
							'Missing permanent or temporary address',
						validationAccountErrorMissingEmail: 'Missing email',
						validationAccountErrorMissingPhone: 'Missing phone',
						verificationErrorNameAndBirthday:
							'Name, surname and birth date not verified',
						verificationErrorNationalNumber: 'National number not verified',
						verificationErrorIdCard: 'Document number not verified',
						verificationErrorAddres: 'Address not verified',
						verificationErrorEmail: 'Email not verified',
						verificationErrorPhone: 'Phone not verified',
						validationDataErrorSimilarMobilityExists:
							'Mobility with the same parameters already exists in the approval/processing process',
					},
				},
				unfixableErrors: {
					title: 'Unfixable errors',
					texts: {
						validationAccountErrorClientIsNotAdult: 'Client is not an adult',
						validationAccountErrorClientIsNotOwnerOfIban:
							'Client is not the owner of the account',
						validationAccountErrorAccountIsClosing: 'Account is closing',
						validationAccountErrorAccountIsBlocked: 'Account is blocked',
						validationDataErrorAccountNotKnown: 'Unknown account',
						validationAccountErrorProductNotEligible:
							'Mobility cannot be created for this account',
						validationAccountErrorStatusNotEligible:
							'Account is in a disallowed state',
						validationAccountErrorClientNotOwner:
							'Client is not the owner of the account',
					},
				},
				unexpectedErrors: {
					title: 'Unexpected errors',
					text: 'Internal services are not available',
				},
			},
		},
		processIncomingMobility: {
			processIncomingMobilityBasicInfoPage: {
				currentClientBank: 'Current client bank',
				dateOfMobilityTooltip: 'not defined yet',
				currentClientBankHeaderTooltip: 'not defined yet',
			},
			processIncomingMobilityRecurringPaymentsPage: {
				createDialog: {
					title: 'New recurring payment',
					typeLabel: 'Type',
				},
				invalidateDialog: {
					description:
						'Do you want to continue with mobility cancellation? Action is irreversible.',
					label: 'Reason of invalidation',
				},
				paymentOrder: {
					name: 'Name',
					status: 'State',
					amount: 'Amount in Kč',
					periodicity: 'Frequency',
					sortCode: 'Sort code',
					accountNumber: 'Account number',
					bankCode: 'Bank code',
					variableSymbol: 'Variable symbol',
					constantSymbol: 'Constant symbol',
					specificSymbol: 'Specific symbol',
					messageToRecipient: 'Message to recipient',
					periodicityOptions: {
						annually: 'Annually',
						indefinite: 'Indefinite',
						monthly: 'Monthly',
						quarterly: 'Quarterly',
						semiAnnually: 'Semi annually',
						weekly: 'Weekly',
					},
					mobilityProcessStatusOptions: {
						active: 'active',
						inactive: 'inactive',
						cancel: 'cancelled',
						firstSign: 'first sign',
						waitingForSign: 'waiting for sign',
					},
					validFromDate: 'Valid from',
					validToDate: 'Valid until',
				},
				standingOrder: {
					title: 'Standing order',
					editTitle: 'Edit standing order',
					invalidateTitle: 'Delete standing order',
					executionDate: 'Execution date',
				},
				directDebit: {
					title: 'Direct debit',
					editTitle: 'Edit direct debit',
					invalidateTitle: 'Delete direct debit',
				},
				sipo: {
					title: 'SIPO',
					editTitle: 'Edit SIPO',
					invalidateTitle: 'Delete SIPO',
					connectionNumber: 'Connection number',
					messageForMe: 'Message for me',
				},
				buttons: {
					generateConfirmation: 'Vygenerovat potvrzení',
					setUp: 'Nastavit',
					invalidate: 'Zneplatnit',
					edit: 'Editovat',
					addPayment: 'Přidat příkaz',
				},
				settingUpPaymentsOrders:
					'Nastavení trvalých příkazů, svolení k inkasu a SIPO',
				sectionTitles: {
					directDebit: 'Inkaso',
					sipo: 'SIPO',
					standingOrders: 'Trvalé příkazy',
				},
			},
			incomingMobilityDocumentPage: {
				uploadDocument: 'Nahrát dokument',
			},
			incomingMobilityRecapitulationPage: {
				newAccount: 'New account at Partners Bank',
				currentClientBank: 'Current client bank',
				finishButton: 'Finished',
				dialogTitle: 'Do you want to stop mobility processing?',
				dialogDescription:
					"The end of mobility processing closes the mobility to changes and processing. The completed mobility is moved to the client's mobility history. Do you really want to end mobility processing?",
			},
		},
	},
	unexpectedErrorDialogComponent: {
		title: 'Unexpected error',
		description: 'Unexpected error occurred',
	},
	clientDocuments: {
		title: 'Documents',
		uploadDocument: 'Upload documents',
		filtersPopoverContent: {
			filterGroupTitles: {
				search: 'Search',
				category: 'Categories',
				documentsAvailableInMobileApp:
					'Documents available to client in mobile app',
				archivedDocuments: 'Archived documents',
			},
			labels: {
				allCategories: 'All',
				withoutRestriction: 'Without restriction',
			},
			searchDocument: 'Search in document name',
			tooltips: {
				documentsAvailableInMobileAppTooltip:
					'Filters documents that are currently available to the client in the {companyName} mobile application, in the "Menu" section, Bank section, "Documents" item.',
			},
		},
		horizontalMenuItems: {
			bankDocuments: 'Bank documents',
			uploadedByClientDocuments: 'Uploaded to client',
		},
		columns: {
			category: 'Category',
			fileName: 'Name',
			visibleToClient: 'Visible in application',
			createdAtDate: 'Date of creation',
			labels: {
				archived: 'Archived',
			},
		},
		noDocuments: 'No documents',
		failedToLoadDocuments: 'Failed to load documents',
		documentDetail: {
			failedToLoadBankDocument: 'Bank document failed to load.',
			sections: {
				signatures: 'Signatures',
				originalDocumentReplacement: 'Original document replacement',
				additionalInfo: 'Additional information',
			},
			labels: {
				description: 'Description',
				bankDocumentCode: 'Contract / document number',
				tags: 'Tags',
				signedByBank: 'Signed by bank',
				signedByClient: 'Signed by client',
				originalDocumentUuid: 'Original document ID',
				originalDocumentName: 'Original document name',
				originalDocumentCreatedAt: 'Original document created at',
				documentUuid: 'Document ID',
				documentOwner: 'Document owner',
				connectedWithAccount: 'Document connected with account',
				expirationDate: 'Expiration date',
			},
			buttons: {
				editDocumentInformation: 'Edit document information',
			},
		},
		bankTypes: {
			addendumToFrameworkAgreement: 'Addendum to framework agreement',
			availableAccountConfirmation: 'Available account confirmation',
			availableBalanceConfirmation: 'Available balance confirmation',
			commitmentsConfirmation: 'Commitments confirmation',
			complaintsProcedure: 'Complaints procedure',
			directDebitConfirmation: 'Direct debit confirmation',
			foreignPoliceConfirmation: 'Foreign plice confirmation',
			frameworkAgreement: 'Framework agreement',
			priceList: 'Price list',
			sipoConfirmation: 'SIPO confirmation',
			standingOrderConfirmation: 'Standing order confirmation',
			statementOfFees: 'Statement of fees',
			statementOfPayments: 'Statement of payments',
			termsAndConditions: 'Terms and conditions',
			termsAndConditionsCards: 'Terms and conditions for cards',
			termsAndConditionsCardsGoogleAndApplePay:
				'Terms and conditions for Google/Apple pay',
			termsAndConditionsPayments: 'Terms and conditions for payments',
			mobilityRequest: 'Mobility request',
			interestRates: 'Interest rates',
			personalDataProcessingInformation: 'Personal data processing information',
			permanentlyAccessibleInformation: 'Permanently accessible information',
			termsOfUseMobileApplication: 'Terms of use mobile application',
			groupMarketingConsent: 'Group marketing consent',
			informationOnTheDepositInsuranceSystem:
				'Information on the deposit insurance system',
			mobilityChangeAccount: 'Mobility - change account',
			mobilityPayers: 'Mobility - payer',
			accountTerminationBankImmediateCab:
				'Account termination - immediate - CAB',
			accountTerminationBankImmediateOther:
				'Account termination - immediate - other',
			accountTerminationBankWithNoticePeriod:
				'Account termination - notice period',
			accountTerminationLimitationOfAutonomy:
				'Account termination - limitation of autonomy',
			loanAccessorAgreement: 'Loan access agreement',
			loanAgreement: 'Loan agreement',
			loanApplicationSecured: 'Loan application - secured',
			loanApplicationUnsecured: 'Loan application - unsecured',
			loanInterestRateGuaranteeSecured: 'Interest rate guarantee - secured',
			loanInterestRateGuaranteeUnsecured: 'Interest rate guarantee - unsecured',
			loanPreContractualExplanation: 'Pre-contractual explanation',
			loanRefinancingTermination: 'Loan refinancing termination',
			loanScheduleSecured: 'Loan schedule - secured',
			loanScheduleUnsecured: 'Loan schedule - unsecured',
			exampleTemplate: 'Example template',
			cardPaymentConfirmation: 'Card payment confirmation',
			domesticPaymentConfirmation: 'Domestic payment confirmation',
			sepaPaymentConfirmation: 'SEPA payment confirmation',
			loanIzuUnsecured: 'Loan IŽU - unsecured',
			frameworkAgreementAmendment: 'Amendment to framework agreement',
			priceListAmendment: 'Amendment to price list',
			termsAndConditionsAmendment: 'Amendment to terms and conditions',
			termsAndConditionsAdjustment: 'Adjustment of terms and conditions',
			termsAndConditionsPaymentsAmendment:
				'Amendment to terms and conditions for payments',
			termsAndConditionsPaymentsAdjustment:
				'Adjustment of terms and conditions for payments',
			termsAndConditionsCardsAmendment:
				'Amendment to terms and conditions for cards',
			termsAndConditionsCardsAdjustment:
				'Adjustment of terms and conditions for cards',
			foreignPaymentConfirmation: 'Foreign payment confirmation',
			loanEuropeanStandardisedInformationSheetSecured:
				'European standardised information sheet - secured',
			loanAccessionToDebtAgreementSecured:
				'Accession to debt agreement - secured',
			loanAccessionToDebtAgreementUnsecured:
				'Accession to debt agreement - unsecured',
			loanCalculationWhenWithdrawingFromALoan:
				'Calculation when withdrawing from a loan',
			loanConfirmationOfCancellationOfApplication:
				'Confirmation of cancellation of application',
			loanConfirmationOfConsumerLoanRepayment:
				'Confirmation of consumer loan repayment',
			loanConfirmationOfConsumerLoanRepaymentAndTerminationOfTheAccessionObligation:
				'Confirmation of consumer loan repayment and termination of accession obligation',
			loanContractSecured: 'Loan contract - secured',
			loanContractUnsecured: 'Loan contract - unsecured',
			loanEuropeanStandardisedInformationSheetUnsecured:
				'European standardised information sheet - unsecured',
			loanExtraordinaryFullInstalmentCalculation:
				'Extraordinary full instalment calculation',
			loanNoticeOfEarlyRepaymentOfConsumerLoanUnsecured:
				'Notice of early repayment of consumer loan - unsecured',
			loanStandardEuropeanConsumerCreditInformationUnsecured:
				'Standard European consumer credit information - unsecured',
			loanConfirmationOfPaidInterestSecured:
				'Confirmation of paid interest - secured',
			loanEarlyRepaymentNotice: 'Early repayment notice',
			other: 'Other',
		},
	},
	appEditableTagInputsComponent: {
		addTag: 'Add tag',
	},
	client: {
		additionalCurrencies: {
			title: 'Additional currencies',
			mismatchAccountantBalanceSingle:
				'Currency component {currencyCode} has a different accounting balance',
			mismatchAccountantBalanceMultiple:
				'Currency components {currencyCodes} have a different accounting balance',
			accountant: 'accountant {amount}',
		},
	},
	clientProducts: {
		title: 'Products',
		horizontalMenuItems: {
			package: 'Package',
			accounts: 'Accounts {count}',
			cards: 'Cards {count}',
			loans: 'Loans {count}',
			bankInsurances: 'Bank Insurances {count}',
		},
		actionSections: {
			client: 'Client',
			consulting: 'Consulting',
		},
		actions: {
			createReportsForStateInstitutions:
				'Create documents for state institutions',
			createPFSAnalysis: 'Create an analysis in PFS',
			sendClientDocumentationToPFS: 'Send documentation to PFS',
			verifyClientForRemoteService: 'Verify identity remotely',
			blockPid: 'Block client',
			unblockPid: 'Unblock client',
			blockageHistory: 'Client blockage history',
		},
		blockPidDialog: {
			title: 'Block client?',
			description:
				"After blocking the client's identity, the following will also be automatically:",
			bulletPoints: {
				blockAllMobileActivations:
					'blocked all existing mobile app activations',
				blockFutureMobileActivations:
					'blocked the ability to activate mobile app in the future',
				blockBankIdentity: 'blocked bank identity',
			},
			confirm: 'Block',
			snackbar: {
				success: 'Client has been successfully blocked',
				alreadyBlocked: 'Client is already blocked',
				failed: 'Failed to block the client',
			},
		},
		unblockPidDialog: {
			title: 'Unblock client?',
			descriptionPrefix:
				'After unblocking, previous mobile app and bank identity blocks ',
			descriptionBold: 'will not',
			descriptionSuffix:
				' be automatically restored. Unblocking will need to be done manually, or the client will need to go through a new mobile app activation.',
			confirm: 'Unblock',
			snackbar: {
				success: 'Client has been successfully unblocked',
				alreadyUnblocked: 'Client is already unblocked',
				failed: 'Failed to unblock the client',
			},
		},
		blockageHistoryDialog: {
			title: 'Blockage history',
			noBlockages: 'No blockages',
			failedToLoad: 'Failed to load blockage history',
			table: {
				date: 'Date',
				action: 'Action',
				performedBy: 'Performed by',
				reason: 'Reason',
				blocked: 'Blocked',
				unblocked: 'Unblocked',
			},
			columns: {
				blockageType: 'Blockage type',
				validFrom: 'Valid from',
				validTo: 'Valid to',
			},
			blockageTypes: {
				identityFraud: 'Suspected fraud',
				unknown: 'Unknown',
			},
			activeBlockage: 'Active',
		},
		generateReportForStateInstitutionsDialog: {
			title: 'Create documents for state institutions',
			pageTitle: '{clientName} - Create documents for state institutions',
			informationToCopy: 'Information to copy',
			sections: {
				loans: {
					productCode: 'Product code: {productCode}',
					amount: 'Amount: {amount}',
					secured: 'Secured: {secured}',
					state: 'State: {state}',
					code: 'Loan code: {code}',
					loanAgreementSignedAt: 'Date signed: {loanAgreementSignedAt}',
					pastDueAmount: 'Past due amount: {pastDueAmount}',
					yesValue: 'Yes',
					noValue: 'No',
				},
				client: {
					client: 'CLIENT',
					nameAndSurname: 'Name and surname: {name}',
					dateOfBirth: 'Date of birth: {dateOfBirth}',
					permanentResidence: 'Permanent residence: {address}',
					idCardNumber: 'ID card number: {idCardNumber}',
					phoneNumber: 'Phone number: {phoneNumber}',
					email: 'E-mail: {email}',
					documentCopy: {
						header: 'Document copies made',
						updatedAt: 'Date of change: {updatedAt}',
						idCardNumber: 'Document number: {idCardNumber}',
						link: 'Show details of document copies made',
					},
				},
				bankAccounts: {
					accountNumber: 'Account number: {accountNumber}',
					status: 'Status: {status}',
					balance: 'Balance: {balance}',
					dateOfCreation: 'Date of creation: {dateOfCreation}',
					methodOfCreation: 'Method of creation: {methodOfCreation}',
					distraints: 'Executions: {hasDistraints}',
					heldTransactions: 'Blocked transactions: {hasHeldTransactions}',
					terminationSource: 'Terminated at request of: {terminationSource}',
					terminationSourceValues: {
						bank: 'Bank',
						bankCore: 'Bank (core)',
						client: 'Client',
					},
					disponents: {
						header: 'Disponents:',
						nameAndSurname: 'Name and surname: {name}',
						dateOfBirth: 'Date of birth: {dateOfBirth}',
						permanentResidence: 'Permanent residence: {address}',
						idCardNumber: 'ID card number: {idCardNumber}',
						phoneNumber: 'Phone number: {phoneNumber}',
						email: 'E-mail: {email}',
					},
					distraintFields: {
						referenceNumber: 'Reference number: {referenceNumber}',
						issuer: 'Issued by: {issuer}',
						actualAmount: 'Current amount: {actualAmount}',
						originalAmount: 'Original amount: {originalAmount}',
						issueDate: 'Issue date: {issueDate}',
						lastChangeDate: 'Last change date: {lastChangeDate}',
					},
					heldTransactionFields: {
						type: 'Type: {type}',
						amount: 'Amount: {amount}',
						creationDate: 'Creation date: {creationDate}',
					},
				},
				bankCards: {
					creditCardNumber: 'Credit card number: {creditCardNumber}',
					holdersName: 'Holders name: {holdersName}',
					cardType: 'Type of card: {cardType}',
					cardStatus: 'Status of card: {cardStatus}',
					cardActivationMethod:
						'Card activation method: {cardActivationMethod}',
					deliveryAddress: 'Delivery address: {deliveryAddress}',
					addressNotRegistered: 'Not registered',
					cardTokens: {
						noTokens: 'No  card tokens',
						tokenReferenceId: 'Card token number: {tokenReferenceId}',
						panSource: 'Tokenization method: {panSource}',
						createdAt: 'Tokenization date: {createdAt}',
						tokenStatus: 'Card token status: {tokenStatus}',
					},
				},
				applicationActivations: {
					createdAt: 'Creation date: {createdAt}',
					name: 'Name: {name}',
					processType: 'Activation type: {processType}',
					processState: 'State: {processState}',
					phoneNumber: 'Phone number: {phoneNumber}',
					bankTitle: 'Bank used for login: {bankTitle}',
					activationId: 'Activation ID: {activationId}',
				},
				bankIdentity: {
					createdAt: 'Created at: {createdAt}',
					activityUuid: 'Activity ID: {activityUuid}',
					application: 'Application: {application}',
					company: 'Company: {company}',
					id: 'ID: {id}',
					type: 'Event type: {type}',
				},
			},
			steps: {
				range: 'Rozsah',
				outputs: 'Výstupy',
			},
			subtitles: {
				client: 'Client',
				products: 'Products',
				applications: 'Applications',
				documents: 'Documents',
				bankIdentity: 'Bank identity',
				loans: 'Loans',
			},
			tooltips: {
				other:
					'Searches in client documents for the following types of documents: framework agreement, amendment to framework agreement, change of framework agreement or documents related to termination of relationship with the client.',
			},
			checkboxes: {
				client: 'Download client information',
				accounts: 'Download information about selected client accounts',
				accountDisponents: 'Download information about account managers',
				accountStatements:
					'Generate statement(s) for selected client accounts for the period',
				cards: 'Download information about cards',
				cardTokens: 'Download information about card tokens',
				applicationActivations:
					'Download mobile application activation history',
				contractualDocuments: 'Prepare list of contractual documents',
				bankIdentity: 'Download history of bank identity verifications',
				loans: 'Download loan information',
			},
			datePickers: {
				dateFrom: 'From',
				dateTo: 'To',
			},
			contentTitles: {
				client: 'Client information',
				accounts: 'Information about selected client accounts',
				cardsAndCardTokens: 'Information about cards and card tokens',
				applications: 'Mobile application activation history',
				bankIdentity: 'History of bank identity verifications',
				loans: 'Loan information',
			},
			buttons: {
				generateReports: 'Generate',
				updateFirstStep: 'Change selected options',
			},
			noData: {
				noClientData: 'No client data',
				noAccountsData: 'No accounts data',
				noCardsData: 'No card data',
				noApplicationsData: 'No applications data',
				noDocumentsData: 'No documents',
				noBankIdentityData: 'No bank identity data',
				noLoansData: 'No loan data',
			},
			failedToLoadLoansData: 'Failed to load loan data',
			failedToLoadData: 'Failed to load data',
			failedToLoadDataDateRangeNotValid:
				'The data could not be downloaded (for the selected accounts, the entered date from-to is invalid)',
			insufficientRights: {
				title: 'Report cannot be created',
				description:
					'You do not have sufficient rights to generate the report. Please contact the helpdesk.',
			},
		},
		createPFSAnalysisDialog: {
			title: 'Create a new analysis',
			errorTitle: 'Analysis cannot be created',
			successTitle: 'Analysis created',
			changeEntry: 'Change entry',
			description:
				'Creates a client in Partners Financial Services and creates a new analysis under a specific advisor.',
			advisorId: 'Advisor ID',
			advisorIdHelperText:
				'Advisor ID can be for both natural and legal persons',
			advisorFullName: 'Advisor full name',
			errors: {
				advisorWithCurrentIdDoesNotExist:
					'Advisor with given ID does not exist',
				errorClientshipDoesNotMeetCriteria:
					'The client does not meet the condition of belonging to Partners Bank and Partners Financial Services.',
				errorNationalContextDoesNotMeetCriteria:
					'The client and the advisor have different national contexts set',
				advisorOrClientDoesNotExist:
					'Unexpected error - advisor or client does not exist',
				existingAnalysisInPFS:
					'The client already has an analysis created in Partners Financial Services',
			},
			flagDescription: 'Flag “Established at the request of Partners Bank”',
			flagToggle:
				'Mark with the flag “Established at the request of Partners Banka”',
			theAnalysisWillBeCreatedWithoutTheClientsConsent:
				"The analysis will be created without the client's consent.",
			analysisCreated:
				'The client was registered in Partners Financial Services and an analysis {analysisId} was created. The advisor will be informed of the analysis by email.',
		},
		sendDocumentationToPFSDialog: {
			title: 'Send Banking Contracts to PFS',
			errorTitle: 'Contracts Cannot Be Sent',
			successTitle: 'Contracts Sent',
			description:
				'Sends the client’s contractual documentation from Partners Bank to Partners Financial Services for further processing.',
			sendDocumentationUnderTheCurrentClientGuardian:
				'Send documentation under the client’s current guardian {advisorName} (ID {advisorID})',
			unfortunatelyTheClientDoesNotHaveAnAssignedGuardian:
				'Unfortunately, the client does not have an assigned guardian',
			submitDocumentationUnderAnotherAdvisor:
				'Send documentation under another advisor',
			advisorId: 'Advisor ID',
			advisorIdHelperText:
				'Advisor ID can belong to both individuals and legal entities',
			advisorFullName: 'Advisor Name',
			errors: {
				errorClientshipDoesNotMeetCriteria:
					'Client does not meet the condition. The client must be a client of both Partners Bank and Partners Financial Services.',
				advisorWithCurrentIdDoesNotExist:
					'Advisor with the given ID does not exist',
			},
			successDescription:
				'The client’s contractual documentation has been sent to Partners Financial Services.',
			failureDescription: 'Failed to send documents',
			advisorIdHasChanged:
				'It was not possible to send the contracts under the specified advisor. The contracts were instead sent under advisor {advisorName} (ID {advisorID}).',
		},
		packagesPage: {
			anotherClient: 'Another client',
			noPackage: 'No package',
			failedToLoadPackage: 'Failed to load package',
			monthlyPrice: 'Monthly price',
			packageName: 'Package "{packageName}"',
			marketingActionsErrors: {
				fetchFailed:
					'Original package price displayed. Failed to load information about potential marketing actions',
				multipleActions:
					'Original package price displayed. Discount unclear due to multiple marketing actions running simultaneously',
				discountApplied: 'Price reduced by marketing action on package owner',
			},
			additionalData: {
				title: 'Additional data',
				advisorId: 'ID {advisorId}',
				labels: {
					bundleId: 'Bundle ID',
					productCode: 'Product code',
					productVersion: 'Product version',
					advisor: 'Advisor',
				},
			},
			clientRoles: {
				driver: 'Owner',
				passenger: 'Co-owner',
				passengerChild: 'Child',
			},
			connectedClients: {
				title: 'Connected clients',
				labels: {
					name: 'Name',
					lastname: 'Lastname',
				},
			},
		},
		loansPage: {
			filter: {
				state: {
					title: 'Loans state',
					active: 'Active',
					completed: 'Completed',
				},
			},
			state: {
				active: 'Active',
				cancelledByBank: 'Cancelled by bank',
				cancelledByBankBeforeDrawing: 'Cancelled by bank (before drawing)',
				cancelledByClient: 'Cancelled by client',
				cancellingByBank: 'In the process of cancelling (by the bank)',
				cancellingByClient: 'In the process of cancelling (by the client)',
				clientWithdrawalFromTheContract: 'Cancelled by client',
				collection: 'In collection',
				contractNotSigned: 'Cancelled (not signed)',
				contractSigned: 'Signed (awaiting drawing)',
				draft: 'Draft',
				drawingExpired: 'Not drawn (client did not meet conditions)',
				drawing: 'In the process of drawing',
				drawingPreparation: 'In preparation for drawing',
				earlyRepaid: 'Repaid early',
				earlyRepaying: 'Early repayment requested by client',
				forcedRepaid: 'Repaid (enforced by bank)',
				forcedRepayingByBank: 'Enforcement requested by bank',
				refinanced: 'Repaid (refinanced)',
				refinancing: 'Refinancing requested by client',
				repaid: 'Repaid',
				waitingForAccessorSign: 'Waiting for accessor signatures',
				waitingForDrawing: 'Waiting for drawing',
				waitingForSign: 'Waiting for signature',
			},
			productName: 'Product name',
			creditLimitAmount: 'Original loan amount',
			noActiveLoans: 'No active loans',
			noClosedLoans: 'No closed loans',
			noLoanDetail: 'No loan detail',
			stateTitle: 'State',
			loanDetail: {
				parameters: {
					nextPaymentDate: 'Next payment date',
					nextPaymentAmount: 'Next payment amount',
					remainingCount: 'Remaining installment count',
					repaidCount: 'Repaid installment count',
					remainingPrincipalAmount: 'Remaining principal amount',
					principalPaid: 'Principal paid',
					state: 'State',
					totalPastDueAmount: 'Amount overdue',
					pastDueDate: 'Due from {date}',
				},
				loan: {
					sectionTitle: 'Loan',
					creditLimitAmount: 'Original loan amount',
					totalClientPays: 'Total amount paid by client',
					purposeAmount: 'Amount of purposeful part of loan',
					nonPurposeAmount: 'Amount of non-purposeful part of loan',
					termInMonths: 'Loan term in months',
					repaymentDay: 'Repayment day of the month',
					repaymentFrequency: 'Repayment frequency',
					repaymentDayInMonths: '{0}{{. day}}',
					repaymentDateFrequencyMap: {
						day: '{0}x per day',
						week: '{0}x per week',
						month: '{0}x per month',
						quarter: '{0}x per quarter',
						year: '{0}x per year',
					},
				},
				interestRate: {
					sectionTitle: 'Interest rate',
					interestRate: 'Interest rate',
					periodFixation: 'Period fixation',
					rpsn: 'RPSN',
				},
				disposition: {
					sectionTitle: 'Disposition',
					mainDebtor: 'Main debtor',
					coDebtors: 'Co-debtors',
				},
				additionalData: {
					sectionTitle: 'Additional data',
					id: 'Loan ID',
					paymentBankAccountIban: 'Bank account for monthly payments',
					createdAt: 'Creation date',
					productCode: 'Product code',
					productVersion: 'Product version',
				},
				contract: {
					contractNumber: 'Contract number',
					loanAgreementSigned: 'Sign date',
					sectionTitle: 'Contract',
				},
				documents: {
					sectionTitle: 'Documents',
					noDocuments: 'No documents',
				},
			},
		},
		accountsPage: {
			filter: {
				state: {
					active: 'Active',
					cancelled: 'Cancelled',
				},
			},
			columns: {
				type: 'Type',
				iban: 'Account number',
				balance: 'Balance',
				status: 'Status',
			},
			availableBalance: {
				adjustedBalance: '~ {adjustedBalance}',
				adjustedBalanceTooltip: 'Displayed balance is an approximate',
				hiddenBalance: '****,- {currency}',
				hiddenBalanceTooltip:
					'Displaying the balance is not allowed for your role',
				accountant: 'accountant {amount}',
			},
			multiCurrency: {
				tag: 'Multi-currency',
				overviewIndicator: 'MM',
			},
			smartReserve: {
				tag: 'Smart reserve',
			},
			buttons: {
				generateConfirmation: 'Generate confirmation',
				changePaymentLimits: 'Change',
				transactionHistory: 'Transaction history',
				mobilityRequests: 'Mobility requests',
				cancelAccount: 'Cancel',
				markAccountAsBasic: 'Mark as basic',
				blockAccount: 'Block',
			},
			accountDetail: {
				sectionTitles: {
					paymentLimits: 'Payment limits',
					interestRate: 'Interest rate',
					interestRateNextMonth: 'Interest rate - next month',
					dispositions: 'Dispositions',
					additionalData: 'Additional data',
					smartReserve: 'Smart reserve',
					childToBasicTransition: 'Child to basic account transition',
					termDepositParameters: 'Parameters',
					savingGoal: 'Saving goal',
				},
				buttons: {
					details: 'Details',
				},
				smartReserve: {
					couldNotLoadData: 'Failed to load smart reserve information',
					enabled: 'Enabled',
					settingRoundingUpToNearestWholeNumber:
						'Round-up setting / Fixed extra amount',
					linkedAccount: 'Linked account',
					previouslySet:
						'Smart reserve had previously been set to {targetAmount} to account {sourceAccount}',
					previouslySetFromSingleAccount:
						'Smart reserve had previously been set to {targetAmount} from account {currentAccounts}',
					previouslySetFromMultipleAccounts:
						'Smart reserve had previously been set to {targetAmount} from accounts {currentAccounts}',
				},
				labels: {
					accountName: 'Account name',
					accountBalanceToDate: 'Balance to date',
					accountNumber: 'Account number',
					coreId: 'DCS account ID',
					iban: 'IBAN',
					ccIban: 'Currency Cloud IBAN (SEPA payments)',
					ccIbanSwift: 'SWIFT IBAN',
					ccSwiftCode: 'BIC (SWIFT)',
					accountCurrency: 'Account currency',
					creationDate: 'Creation date',
					closeDate: 'Close date',
					terminationReason: 'Termination reason',
					accountCreated: 'Account created',
					basicAccountIndex: 'Basic account index',
					dailyLimitAmount: 'Daily limit amount',
					unlimitedLimit: 'Unlimited',
					leftToExhaust: 'Left to exhaust',
					rate: 'Rate',
					interestRateWithBonus: 'Interest rate including bonus',
					bonusAppliedToBalance: 'Bonus applied to balance',
					bonusAppliedToBalanceWithMaxThresholdAmount:
						'up to {maxThresholdAmount} including',
					bonusApplied: 'Bonus applied',
					bonusAppliedToEntireBalance: 'On entire balance',
					bonusAssignedBasedOnCampaign: 'Bonus assigned based on campaign',
					interestRateBasic: 'Interest rate',
					appliedToBalance: 'Applied to balance',
					appliedToBalanceWithMaxThresholdAmount: 'over {maxThresholdAmount}',
					applied: 'Applied',
					interestRateNextMonth: 'Interest rate next month',
					multipleCampaignRatesOverlap:
						'There is an overlap of multiple campaign rates',
					unableToDetermine:
						'Unable to determine, there are multiple campaign rates',
					plannedBonusNextMonth: 'Planned bonus for next month',
					accountId: 'Account ID',
					productCode: 'Product code',
					productVersion: 'Product version',
					rateType: {
						base: 'Base',
						reduced: 'Reduced',
					},
					depositAmount: 'Deposit amount',
					durationInMonths: 'Duration (months)',
					maturityDate: 'Maturity date',
					savingGoalTargetAmount: 'Target amount',
					savingGoalFulfillment: 'Goal fulfillment',
				},
				tooltips: {
					ccIbanSwift:
						'SWIFT IBAN (International Bank Account Number) is an account number in international format. The client needs to know it for payments when someone sends them a payment from abroad.',
					ccSwiftCode:
						'BIC (Bank Identifier Code) is an international code that identifies the bank for foreign payments. It is used together with SWIFT IBAN.',
				},
				hints: {
					failedToLoadPaymentLimits: 'Failed to load limits',
					failedToLoadInterestRate: 'Failed to load interest rate',
					failedToLoadFutureInterestRate:
						'Failed to load data for displaying the conditions for obtaining the basic rate next month',
				},
				sweep: {
					sectionTitle: 'Minimum balance monitoring',
					labels: {
						enabled: 'Enabled',
						targetAmount: 'Minimum balance',
						sourceAccount: 'Savings account from which money is transferred',
					},
					hints: {
						failedToLoad: 'Failed to load minimum balance monitoring settings',
						previouslySet:
							'Minimum balance monitoring had previously been set to {targetAmount} to account {sourceAccount}',
					},
				},
				accountDispositionTypes: {
					owner: 'Owner',
					ownerChild: 'Owner - child',
					disponent: 'Disponent',
					disponentPoa: 'Attorney',
				},
				futureInterestRateReasoning: {
					fulfilledByBundle: 'Fulfilled condition: paid package',
					fulfilledByFreshFirstSavingsAccount:
						'Fulfilled condition: savings account until {firstSavingsAccountDate}',
					fulfilledByNumberOfCardPaymentsAndSweep:
						'Fulfilled condition: card payments {numberOfCardPayments}/{requiredNumberOfCardPayments}, minimum balance monitoring enabled',
					fulfilledByOther: 'Fulfilled condition: other condition',
					notFulfilled:
						'Not fulfilled condition: card payments {numberOfCardPayments}/{requiredNumberOfCardPayments}',
					notFulfilledWithSweep:
						'Not fulfilled condition: card payments {numberOfCardPayments}/{requiredNumberOfCardPayments}, {sweepStatus} minimum balance monitoring',
					sweepEnabled: 'enabled',
					sweepDisabled: 'disabled',
				},
				accountTransitionProcess: {
					labelFromChild: 'Transition from child account',
					labelToMain: 'Transition to main current account',
					fetchFailed: 'Failed to load data',
					inProgress: 'In progress',
					done: 'Done',
					aborted: 'Process aborted',
					tooltipStatus: 'Transition status',
					tooltipCreatedAt: 'Created at',
					tooltipUpdatedAt: 'Last updated at',
					statuses: {
						draft: 'Draft',
						readyToTransition: 'Ready to transition',
						signed: 'Transition confirmed',
						waitingForAml: 'Waiting for AML',
						documentsSigned: 'Documents signed',
						challengesClosed: 'Challenges closed',
						disponentClosed: 'Disponent closed',
						cardClosed: 'Card closed',
						accountTransitioned: 'Account transitioned',
						bundleCreated: 'Bundle created',
						cardCreated: 'Card created',
						transitioned: 'Transition done',
						aborted: 'Transition aborted',
						unknown: 'Unknown status',
					},
				},
				childToBasicTransition: {
					restartButton: 'Restart processing',
					prepareButton: 'Create new request',
					restartSuccess: 'Transition restarted successfully',
					restartError: 'Failed to restart transition',
					prepareSuccess: 'Transition request created',
					prepareError: 'Failed to create transition request',
				},
			},
			smartReserveDialog: {
				title: 'Smart reserve',
				settingRoundingUpToNearestWholeNumber:
					'Setting rounding up to the nearest',
				fixedAmountInAdditionToSavings: 'Fixed amount in addition to savings',
				state: 'State',
				smartReserveSavingsState: {
					pendingActivation: 'Pending activation',
					active: 'Active',
					pendingChange: 'Pending change',
					pendingTermination: 'Pending termination',
					terminated: 'Terminated',
				},
				savingsAreMadeFromCurrentAccounts: 'Savings are made from {type}',
				smartReserveSavingsType: {
					basic: 'current accounts',
					child: 'child account',
					shared: 'shared account',
				},
				savingsAreMadeOnAnAccount: 'Savings are made on an account',
				additionalData: {
					sectionTitle: 'Additional data',
					savingsId: 'Savings ID',
					lastSignatureOperationId: 'Last signature operation ID',
					creationDate: 'Creation date',
					terminationDate: 'Termination date',
				},
			},
			changePaymentLimitsDialog: {
				title: 'Change payment limits',
				inputLabel: 'New daily limit',
				inputPlaceholder: 'New daily limit amount (Kč)',
				toggleLabel: 'Enable unlimited limit',
				toggleCaption:
					'For an unlimited period up to the maximum allowed amount',
				hint: 'This operation will be sent to the client for approval in the mobile application',
				snackbar: {
					success: 'Change of limit was sent to the client for approval',
					error: 'Payment limits could not be changed',
				},
				paymentLimitValidation: {
					max: 'Maximal allowed limit is {maxPaymentLimit} Kč',
					min: 'Minimal allowed limit is {minPaymentLimit} Kč',
				},
			},
			generateConfirmationDialog: {
				title: 'Generate confirmation',
				selectDocumentTypePlaceholder: 'Select document type',
				selectDocumentTypeLabel: 'Document type',
				selectDocumentMediaTypePlaceholder: 'Select confirmation format',
				selectDocumentMediaTypeLabel: 'Confirmation format',
				selectDocumentYearLabel: 'Year',
				selectDocumentCurrencyLabel: 'Currency',
				currencyCreatedAtCaption: 'Currency component created {createdAt}',
				bankDocumentType: {
					AVAILABLE_ACCOUNT_CONFIRMATION: 'Confirmation of account management',
					AVAILABLE_BALANCE_CONFIRMATION: 'Confirmation of balance',
					COMMITMENTS_CONFIRMATION: 'Confirmation of commitments',
					DIRECT_DEBIT_CONFIRMATION: 'Confirmation of direct debit',
					EXAMPLE: 'Example',
					FOREIGN_POLICE_CONFIRMATION: 'Confirmation for foreign police',
					SIPO_CONFIRMATION: 'Confirmation of SIPO',
					STANDING_ORDER_CONFIRMATION: 'Confirmation of standing order',
					STATEMENT_OF_FEES: 'Fee statement',
					STATEMENT_OF_PAYMENTS: 'Transaction statement',
					TRANSACTION_CONFIRMATION: 'Payment confirmation',
					UNSUPPORTED: 'Unsupported',
				},
				errorMessages: {
					dateMustBeTodayOrEarlier: 'Date must be today or earlier',
					dateMustBeLaterThanFrom: 'Date must be later than from date',
					dateMustBeLaterThanAccountCreation:
						'Date must be later than currency component creation date',
					dateFromIsRequired: "Date 'from' is required",
					dateToIsRequired: "Date 'to' is required",
				},
				snackbar: {
					generationError: 'Failed to generate document',
					generationSuccess: 'Document was generated',
					downloadError: 'Document could not be downloaded',
				},
				buttons: {
					generate: 'Generate',
				},
			},
			markAccountAsBasicDialog: {
				title: 'Mark account as basic?',
				description: "Account '{accountNumber}' will be marked as 'basic'.",
				snackBar: {
					success: 'Account marked as basic',
					error: 'Marking the account failed',
				},
				buttons: {
					mark: 'Mark',
				},
			},
			blockAccountDialog: {
				title: 'Block account?',
				descriptionPart1:
					'Account {accountNumber} will be temporarily blocked for ',
				descriptionPart2: 'outgoing payments. ',
				descriptionPart3:
					'Payments from this account will be blocked, including standing orders, direct debits, sipo and card payments.',
				noteLabel: 'Note',
				hintPart1: 'By setting, you will change the currently set block ',
				hintPart2Blocked: 'for incoming and outgoing payments ',
				hintPart2Inblocked: 'for incoming payments ',
				hintPart3: 'on the account.',
				snackBar: {
					success:
						'Account blocking request submitted. Processing may take a few seconds.',
					error: 'Failed to submit account blocking request.',
				},
				buttons: {
					set: 'Block',
				},
			},
			noOpenAccounts: 'No active accounts',
			noClosedAccounts: 'No closed accounts',
			failedToLoadAccounts: 'Failed to load accounts',
			distraints: {
				infoPanel: {
					headline: 'Execution on account ({count})',
					oneDistraint: 'Current blocking amount {countText}',
					moreDistraints: 'Total amount of all blockages {countText}',
					detailButton: 'Detail',
				},
				issuedToAnotherClientInfoPanel: {
					headline: 'Execution issued against the client {clientName}',
					showClientButton: 'Show client',
				},
				dialogTitle: 'Overview of executions on the account {accountNumber}',
				table: {
					tableHeader: {
						distraintOrderReferenceNumber: 'File number and serial number',
						distraintOrderDate: 'Issue date',
						originalCurrentBlockingAmount: 'Original / Current blocking amount',
						issuedToAnotherClient: 'Issued to another client',
					},
					tableContent: {
						distraintOrderIssuerName: 'Published by',
						actualHoldDate: 'Date of last block change',
						additionalData: 'Additional data',
						externalId: 'Execution ID in Zauzoo',
						coreDistraintHoldId: 'ID blokační transakce v DCS',
						accountDistraintUuid: 'Block ID on BAR',
					},
				},
			},
			terminationProcess: {
				infoPanel: {
					prepareToClose:
						'Account cancellation request pending {terminationSource} {terminationReasonCode}',
					lastAborted:
						'Last aborted account cancellation request {terminationSource} {terminationReasonCode}',
					accountCancelled:
						'Account cancelled {terminationSource} {terminationReasonCode}',
					detailButton: 'Detail',
					failedToLoad: 'Any account cancellation requests could not be loaded',
				},
				terminationSource: {
					client: `at the client's initiative`,
					bank: 'at the initiative of the bank',
					bankCore: 'at the initiative of the bank (core)',
				},
				terminationReasonCode: {
					feeTooHigh: 'Too high fees for services',
					notEnoughProducts: 'Not enough products',
					interestRate: 'Interest rate',
					confusing: 'Inadequate product management through the application',
					other: 'Other',
					clientRequest: 'Termination on client request',
					aml: 'AML reasons (immediate)',
					fraud: 'Fraud (immediate)',
					tocViolation: 'Gross violation of terms and conditions (immediate)',
					insolvency: 'Insolvency',
					inheritance: 'Inheritance',
					limitationOfAutonomy: 'Limitation of autonomy',
					bankWithNoticePeriod:
						'Termination of the relationship with the client (2-month notice period)',
					endOfDistraint: 'Termination of execution',
				},
				cancellationDate:
					'Planned cancellation at the end of day {expectedEndDate}',
				terminationDialog: {
					termination: 'Termination',
					accountCancellationRequirements:
						'Requirements for account {accountNumber} cancellation',
					tableHeader: {
						labels: {
							requirement: 'Requirement',
						},
						requirement:
							'{termination} {terminationSource} - {terminationReasonCode}',
						status: {
							aborted: 'Canceled',
							closed: 'Completed',
							prepareToClose: 'Preparing for cancellation',
							readyToClose: 'Awaiting cancellation',
						},
					},
					detail: {
						note: 'Note',
						settlementAccounts: 'Účty pro vypořádání',
						documents: {
							sectionTitle: 'Documents',
							noDocuments: 'No documents',
						},
						additionalInfo: {
							sectionTitle: 'Additional info',
							id: 'Request ID',
							terminationOriginTitle: 'Termination origin',
							terminationOrigin: {
								be: 'BE',
								boa: 'BOA',
								faa: 'FAA',
								ma: 'Mobile application',
							},
							createdAt: 'Creation date',
							signatureOperationId: 'Signature operation ID',
						},
					},
				},
			},
		},
		cardsPage: {
			filter: {
				state: {
					active: 'Active',
					cancelled: 'Completed',
				},
			},
			columns: {
				type: 'Type',
				cardNumber: 'Card number',
				status: 'Status',
				validTo: 'Valid to',
			},
			validToText: 'Valid to ',
			blockedPin: 'Blocked PIN',
			noOpenCards: 'No active cards',
			noClosedCards: 'No closed cards',
			failedToLoadCards: 'Failed to load cards',
			noCardDetail: 'No card detail',
			cardDetail: {
				parameters: {
					name: 'Name',
					category: 'Category',
					cardDesign: 'Card design',
					creationDate: 'Creation date',
					activationDate: 'Activation date',
					cardBlocked: 'Card blocked FAU/Police',
					tokens: 'Number of tokens (active / total)',
					noTokens: 'None',
				},
				dailyLimit: {
					sectionTitle: 'Daily limit',
					amountOfTheLimit: 'Amount of the limit',
					remainsToExhaust: 'It remains to exhaust',
				},
				serviceSettings: {
					sectionTitle: 'Card settings',
					atmTransaction: 'ATM transactions',
					contactlessPayments: 'Contactless payments',
					onlinePayments: 'Online payments',
					magneticStripePayments: 'Magnetic stripe payments',
					currencyConversionPaymentsDCC: 'Currency conversion payments (DCC)',
					allow: 'Allow',
					disallow: 'Disallow',
					clickToPay: {
						title: 'Click To Pay',
						unknownState: 'Unknown state',
						visaStatus: {
							activating: 'Activating',
							active: 'Active',
							disabling: 'Disabling',
							disabled: 'Disabled',
						},
						tooltip: {
							registeredData: 'Registered data of the client in Click To Pay:',
							name: 'Name: {name}',
							email: 'E-mail: {email}',
							phone: 'Phone number: {phone}',
							address: 'Address: {address}',
							lastUpdated: 'Last updated: {lastUpdated}',
						},
					},
				},
				manageTokens: {
					sectionTitle: 'Tokens',
					titleTooltip:
						'A card token is a special code created when a card is added to a phone or application. It replaces the actual card details and is used for payments. The merchant receives only this code, not the sensitive card information, which increases security.',
					manageTokensButton: 'Token overview',
				},
				cardDelivery: {
					sectionTitle: 'Card delivery',
					address: 'Address',
					expressDelivery: 'Express delivery',
					cardUndeliveredReturnDate: 'Date of return',
					cardUndeliveredReturnCode: 'Reason for return',
					cardUndeliveredReturnCodes: {
						customerHasDied: 'Customer has died',
						customerHasMoved: 'Customer has moved',
						insufficientAddress: 'Insufficient address',
						otherReason: 'Unknown reason',
						unclaimed: 'Customer did not claim',
						undelivered: 'Customer did not accept',
						unknownCustomer: 'Customer is not known at the address',
					},
				},
				additionalData: {
					sectionTitle: 'Additional data',
					cardToAccount: 'Card to account',
					cardId: 'Card ID',
					freezeDate: 'Freeze date',
					terminationDate: 'Termination date',
					terminationType: 'Reason for termination',
					replacementBankCardUuid: 'Replacement card number',
					terminationTypes: {
						accountTransition: 'Account transition',
						client: 'Client request',
						bank: 'Bank decision',
						closingAccount: 'Closed account',
						chargebackFraud: 'Chargeback fraud',
						disposed: 'Authorization completed',
						expired: 'Expired',
						replacedDamaged: 'Replacement - damaged',
						replacedLost: 'Replacement - lost',
						replacedStolen: 'Replacement - stolen',
						fraud: 'Fraud',
						undelivered: 'Undelivered to holder',
						unknown: 'Unknown',
					},
				},
				actionButtons: {
					freezeCard: 'Block',
					unfreezeCard: 'Unblock',
					activateCard: 'Activate',
					unblockPinCard: 'Unlock PIN',
					servicesSettingsCard: 'Change services settings',
					changeLimitCard: 'Change',
					terminationCard: 'Terminate',
					historyOfChangesClickToPay: 'History of changes click to pay',
				},
				dialogs: {
					freezeDialog: {
						title: 'Block card?',
						description:
							'The card {pan} will be temporarily blocked. The client will not be able to use the card.',
						freezeButton: 'Block',
						cardFreezed: 'The card has been blocked',
						cardCouldntBeFreezed: 'The card could not be blocked',
					},
					unfreezeDialog: {
						title: 'Unblock card?',
						description:
							'The card {pan} will be unblocked. The client will be able to use it again.',
						unfreezeButton: 'Unblock',
						cardUnfreezed: 'The card has been unblocked',
						cardCouldntBeUnfreezed: 'The card could not be unblocked',
					},
					activateCardDialog: {
						title: 'Activate card?',
						description: 'Card {pan} will be activated and ready to use.',
						activateButton: 'Activate',
						cardActivated: 'The card has been activated',
						cardCouldntBeActivated: 'The card could not be activated',
					},
					unblockPinCardDialog: {
						title: 'Unlock card PIN?',
						description:
							'Client will be able to use the PIN of card {pan} again.',
						unblockPinButton: 'Unlock PIN',
						cardUnblockedPin: 'The PIN has been unblocked',
						couldntUnblockCardPin: 'Unable to unlock PIN',
					},
					servicesSettingsCardDialog: {
						title: 'Change card settings',
						description:
							'The change of settings takes place immediately without confirmation by the client',
						servicesSettingsConfirmButton: 'Set up',
						cardServicesSettingsChanged:
							'The service settings on the card have been changed',
						unableToSetServices:
							'Failed to change service settings. Check the current card settings and adjust them if necessary',
						atmTransactions: {
							title: 'ATM transactions',
							description: 'Withdrawals from ATMs and payment terminals.',
						},
						contactlessTransactions: {
							title: 'Contactless transactions',
							description: 'Transaction by touching the card to the terminal.',
						},
						internetPayments: {
							title: 'Payments via the Internet',
							description: 'Electronic payments in e-shops.',
						},
						magneticStripePayments: {
							title: 'Magnetic strip',
							description: 'Payments by swiping the card through the terminal.',
						},
						dccTransactions: {
							title: 'Currency Transfer Payments (DCC)',
							description:
								'Payments abroad with high currency conversion fees. By turning it off, these payments will be rejected and the client will save.',
						},
					},
					changeLimitDialog: {
						title: 'Change payment limits',
						newDailyLimit: 'New daily limit',
						newValueDailyLimit: 'New daily limit amount (CZK)',
						paymentLimitValidation: {
							max: 'Maximum limit allowed is {maxPaymentLimit} Kč',
							min: 'The minimum limit allowed is {minPaymentLimit} Kč',
						},
						changeWarning:
							'The limit change takes place immediately without confirmation by the client',
						cardLimitSet: 'The limit on the card has been changed',
						unableToSetCardLimit: 'The limit on the card could not be changed',
						confirmButton: 'Set up',
					},
					terminationDialog: {
						title: 'Terminate card?',
						description:
							"The card {pan} will be permanently terminated. This action can't be undone. The client may have to apply for a new card.",
						terminationButton: 'Terminate',
						cardTerminated: 'The card has been terminated',
						cardCouldntBeTerminated: 'Failed to terminate card',
					},
					manageTokensCardDialog: {
						title: 'Tokens overview for card {pan}',
						noTokens: 'No tokens',
						labels: {
							deviceType: {
								unknown: 'Unknown type',
								mobilePhone: 'Mobile phone',
								tablet: 'Tablet',
								watch: 'Watch',
								mobilePhoneOrTablet: 'Mobile phone or tablet',
								pc: 'PC',
								householdDevice: 'Household device',
								wearableDevice: 'Wearable device',
								automobileDevice: 'Car',
							},
							panSource: {
								keyEntered: 'Key entered',
								onFile: 'On file',
								mobileBankingApp: 'Mobile banking app',
								chipDip: 'Chip & Dip',
								contactlessTap: 'Contactless tap',
								token: 'Token',
							},
							cardTokenStatus: {
								active: 'Active',
								suspended: 'Suspended',
								inactive: 'Inactive',
								deactivated: 'Deactivated',
							},
							operationSystem: {
								ios: 'iOS',
								android: 'Android',
								other: 'Unknown',
							},
						},
						table: {
							columnLabels: {
								cardToken: 'Token',
								cardTokenStatus: 'Status',
								device: 'Device',
							},
							detailLabels: {
								activationDate: 'Activation date',
								deviceType: 'Device type',
								source: 'Source',
								operationSystemType: 'Operating system',
								stepUpMethods: 'Token activation method',
								declineReason: 'Token decline reason',
								tokenReferenceId: 'Token ID',
								createdAt: 'Date created',
								suspendedDate: 'Freeze date',
								terminatedDate: 'Termination date',
							},
							buttons: {
								freeze: 'Block',
								unfreeze: 'Unblock',
								cancel: 'Cancel',
								activate: 'Activate',
							},
							stepUpMethods: {
								appToApp: 'Application',
								customerService: 'Call center',
								otpEmail: 'Email',
								otpSms: 'SMS',
								outboundCall: 'Outbound call',
								unknown: 'Unknown',
							},
							declineReason: {
								fraudSuspicionCase1: 'Suspicious case 1',
								fraudSuspicionCase2: 'Suspicious case 2',
								fraudSuspicionCase3: 'Suspicious case 3',
								fraudSuspicionCase4: 'Suspicious case 4',
								fraudSuspicionCaseUnknown: 'Unknown type of fraud',
								maxActiveAndroidTokensReached:
									'Maximum number of Android tokens reached',
								maxActiveIosTokensReached:
									'Maximum number of iOS tokens reached',
								maxIncorrectAttemptsCountExceeded:
									'Maximum number of attempts reached',
								suspendedOrTerminatedCard: 'Suspended or terminated card',
								invalidExpirationDate: 'Invalid card expiration date',
								cvv2Failure: 'Invalid CVV code',
								unknown: 'Unknown',
								invalidCardType: 'Invalid card type',
								requestorNotSupported: 'Requestor not supported',
								minimalAge: 'Insufficient client age',
								checkEligibilityInvalidExpirationDate:
									'Invalid expiration date during eligibility check',
							},
						},
						confirmationDialog: {
							freeze: {
								title: 'Block token?',
								description:
									'The client will temporarily not be able to make payments using the token {tokenReferenceId} on the card {cardPan}.',
								infoBox:
									'After blocking, the client will receive a notification on their device.',
								snackBar: {
									success:
										'Token blocking request submitted. Processing may take a few seconds.',
									error: 'Failed to submit token blocking request.',
								},
							},
							unfreeze: {
								title: 'Unblock token?',
								description:
									'The client will be able to make payments using the token {tokenReferenceId} on the card {cardPan} again.',
								infoBox:
									'After unblocking, the client will receive a notification on their device.',
								snackBar: {
									success:
										'Token unblocking request submitted. Processing may take a few seconds.',
									error: 'Failed to submit token unblocking request.',
								},
							},
							cancel: {
								title: 'Cancel token?',
								description:
									'Token {tokenReferenceId} on the card {cardPan} will be permanently canceled. This action is irreversible.',
								infoBox:
									'After cancelling, the client will receive a notification on their device.',
								snackBar: {
									success:
										'Token cancellation request submitted. Processing may take a few seconds.',
									error: 'Failed to submit token cancellation request.',
								},
							},
							activate: {
								title: 'Activate token?',
								description:
									'Token {tokenReferenceId} on the card {cardPan} will be activated and the client will be able to use it for payments.',
								infoBox:
									'After activating, the client will receive a notification on their device.',
								snackBar: {
									success:
										'Token activation request submitted. Processing may take a few seconds.',
									error: 'Failed to submit token activation request.',
								},
							},
						},
					},
				},
			},
			cardCategory: {
				plastic: 'Plastic',
				virtual: 'Virtual',
				virtualOnetime: 'Virtual onetime',
			},
			cardDesign: {
				virtual: 'Virtual',
				founder: 'Founder',
				disposable: 'Disposable',
				standardDarkBlue: 'Dark Blue',
				standardTurquoise: 'Turquoise',
				standardWhite: 'White',
				standardCoral: 'Coral',
				kidsThumbUp: 'Kids',
				kidsGirl: 'Kids',
				kidsFlight: 'Kids',
				kidsSkate: 'Kids',
				commonLo: 'Common',
				commonVe: 'Common',
				virtualCoral: 'Virtual Coral',
				virtualWhite: 'Virtual White',
				virtualDarkBlue: 'Virtual Dark Blue',
				virtualGrey: 'Virtual Grey',
				virtualKidsCoral: 'Virtual Coral (kids)',
				virtualKidsDarkBlue: 'Virtual Dark Blue (kids)',
				virtualKidsTurquoise: 'Virtual Turquoise (kids)',
				virtualKidsWhite: 'Virtual White (kids)',
				disposableGrey: 'Disposable Grey',
				disposableKids: 'Disposable (kids)',
				virtualXmas2024: 'Virtual Christmas 2024',
				virtualCoralXmas2024: 'Virtual Coral Christmas 2024',
				virtualWhiteXmas2024: 'Virtual White Christmas 2024',
				virtualDarkBlueXmas2024: 'Virtual Dark Blue Christmas 2024',
				virtualGreyXmas2024: 'Virtual Grey Christmas 2024',
				virtualKidsCoralXmas2024: 'Virtual Coral Christmas 2024 (kids)',
				virtualKidsDarkBlueXmas2024: 'Virtual Dark Blue Christmas 2024 (kids)',
				virtualKidsTurquoiseXmas2024: 'Virtual Turquoise Christmas 2024 (kids)',
				virtualKidsWhiteXmas2024: 'Virtual White Christmas 2024 (kids)',
				unknownDesign: 'Unknown Design',
			},
		},
		clickToPayPage: {
			pageTitle: '{name} - Change history: Click To Pay on the card {cardPan}',
			noClickToPayHistory: 'No Click To Pay changes',
			failedToLoadClickToPayHistory: 'Data could not be loaded',
			table: {
				header: {
					type: 'Action',
					status: 'Status',
					updatedAt: 'Last updated',
				},
				detail: {
					errorMessage: 'Error message',
					createdAt: 'Created at',
					actionInitializedAt: 'Processing start date',
					processedAt: 'Processing date',
					requestTraceUuid: 'Event ID',
				},
			},
			filter: {
				type: 'Type',
				status: 'Status',
				errorMessage: 'Error message',
				requestTraceUuid: 'Event ID',
				actionInitializedAt: 'Processing start date from',
				processedAt: 'Processing date from',
			},
			types: {
				create: 'Automatic activation',
				block: 'Blocking',
				unblock: 'Unblocking',
				terminate: 'Cancellation',
				customerUpdate: 'Client update',
				customerTerminate: 'Client cancellation',
				addressUpdate: 'Address update',
				customerOptIn: 'Client activation',
				customerOptOut: 'Cancellation by the client',
			},
			statuses: {
				pending: 'Waiting for processing',
				processing: 'Processing in progress',
				processingInVisa: 'Processing in progress (Visa)',
				success: 'Completed',
				failed: 'Processing failed',
				obsolete: 'Irrelevant actions',
			},
		},
		accountStatuses: {
			active: 'Active',
			inblocked: 'Blocked (incoming payments)',
			outblocked: 'Blocked (outgoing payments)',
			blocked: 'Blocked',
			closed: 'Closed',
			readyToClose: 'Ready to close',
			foreclosure: 'Foreclosure',
			inProcessInterference: 'In the process of interference',
			waitingForPayment: 'Waiting for payment',
		},
		accountCreationChannels: {
			online: 'Online',
			fip: 'With advisor in FIP',
		},
		paymentCardTypes: {
			common: 'Shared card',
			disposable: 'One-time virtual card',
			kids: 'Kids card',
			premium: 'Premium card',
			standard: 'Payment card',
			virtual: 'Virtual card',
		},
		paymentCardsStatuses: {
			unactivated: 'Waiting to be activated',
			active: 'Active',
			suspended: 'Suspended',
			terminated: 'Terminated',
		},
		bankInsurancesPage: {
			filter: {
				labels: {
					status: 'Contract status',
					type: 'Contract type',
				},
				status: {
					active: 'Active',
					terminated: 'Terminated',
					withdrawn: 'Withdrawn',
				},
				type: { travel: 'Travel insurance' },
			},
			productName: 'Name',
			longTermTravelInsurance: 'Long-term travel insurance',
			noActiveInsurances: 'No active insurance',
			noClosedInsurances: 'No closed insurance',
			noInsuranceDetail: 'No detail',
			stateTitle: 'State',
			inProcessOfTermination: 'In the process of termination',
			detail: {
				parameters: {
					sectionTitle: 'Insurance parameters',
					insuranceType: 'Insurance type',
					insuranceAmount: 'Insurance amount',
					paidFromAccount: 'Paid from account',
					paymentDate: 'Payment date',
					paymentDateValue: 'Last day of the month',
				},
				contract: {
					sectionTitle: 'Contract',
					contractNumber: 'Contract number',
					contractDate: 'Contract date',
					terminationDate: 'Termination date',
					minimalValidTill: 'Minimal validity until',
				},
				pricePeriod: {
					monthly: 'mo.',
					quarterly: 'quarter',
					semiAnnual: 'half-year',
					annual: 'year',
				},
				activeTermination: {
					title: 'Active request for annual travel insurance cancellation',
					dateOfTermination:
						'Cancellation date: {terminationDate} (up to 24 hours before cancellation, the request can be revoked from the mobile application).',
				},
				actions: {
					transactionHistory: 'Transaction history',
					terminateInsurance: 'Terminate insurance',
					terminateDialog: {
						title: 'Cancel insurance',
						reasonLabel: 'Reason for cancellation',
						reasonTextLabel: 'Note',
						reasonTextRequired: 'Note is required when selecting other reason',
						confirmButton: 'Confirm',
						snackBar: {
							success:
								'Annual travel insurance will be terminated as of {terminationDate}',
							error: 'Failed to submit insurance cancellation request',
						},
						reasonCodes: {
							bankRequest: 'Cancellation by the bank',
							clientRequest: 'Client complaint',
							statutoryPeriod:
								'Product cancellation within 2 months of signing',
							other: 'Other reason',
						},
					},
				},
			},
		},
	},
	clientCryptoAccountPage: {
		title: 'Crypto',
		tabAccount: 'Account',
		status: 'Status',
		createdAt: 'Created at',
		closedAt: 'Closed at',
		closeReason: 'Close reason',
		noAccount: 'No account',
		notEligibleForCreation:
			'Client does not meet requirements for account creation',
		notEligibleForTransactions:
			'Client does not meet requirements for future transactions',
		reason: 'Reason: {reason}',
		detailsSection: 'Crypto account details',
		cryptoAccountUuid: 'Crypto account ID',
		coreAccountId: 'DCS account ID',
		linkedBankAccount: 'Linked bank account',
		balancesSection: 'Cryptocurrency balances',
		statuses: {
			active: 'Active',
			closing: 'Closing',
			closed: 'Closed',
		},
		closeReasons: {
			cancellation14: 'Cancellation in 14 day period',
			unknown: 'Unknown',
		},
		syncBalancesButton: 'Synchronize balances',
		syncBalancesSnackbar: {
			success: 'Balances synchronized',
			error: 'Failed to synchronize balances',
		},
		rejectReasons: {
			ageLimit: 'Underage',
			deceased: 'Client deceased',
			legalCapacity: 'Legally incapable',
			riskProfile: 'Risk profile',
			basicAccountUnavailable: 'Basic account unavailable',
			basicAccountStatus: 'Disallowed basic account status',
			basicAccountOverdraft: 'Basic account in overdraft',
			distraintProcedure: 'Distraint procedure',
			pep: 'Politically exposed person',
			unknown: 'Unknown',
		},
	},
	clientMarketingActionsPage: {
		title: 'Marketing actions',
		failedToLoadMarketingActions: 'Failed to load marketing actions',
		noMarketingActions: 'No marketing actions',
		unknownAction: 'Unknown action',
		labels: {
			validFrom: 'Valid from',
			validTo: 'Valid to',
		},
		setupMarketingActionButton: 'Setup action',
		editMarketingActionButton: 'Change',
		columns: {
			marketingAction: 'Action',
			status: 'Status',
			validity: 'Validity',
			benefit: 'Benefit',
		},
		affectedCampaignType: {
			bundleDiscount: 'bundle discount',
			interestRate: 'interest rate',
			other: 'Other',
		},
		filters: {
			titles: {
				actionType: 'Action type',
				actionStatus: 'Action status',
			},
			labels: {
				actionStatus: {
					active: 'Active',
					scheduled: 'Scheduled',
					expired: 'Expired',
					failed: 'Failed',
					canceled: 'Canceled',
				},
			},
		},
		actionType: {
			bundleDiscount: 'Bundle discount',
			interestRate: 'Better interest rate',
			other: 'Other discount',
		},
		marketingActionDetail: {
			unknownDetail: 'Unknown detail',
			failedToLoadMarketingActionDetail:
				'Failed to load marketing action detail',
			additionalData: {
				sectionTitle: 'Additional Data',
				labels: {
					actionId: 'Action ID',
					specificationId: 'Specification ID',
					creationDate: 'Creation date',
					appliedToClients: {
						label: 'Impact on other clients',
						values: {
							individual: 'No (individual action)',
							bundle: 'Yes (all clients in the bundle)',
							other: 'Other',
						},
					},
				},
			},
			brokerageDetail: {
				sectionTitle: 'Impact on brokerage',
				labels: {
					brokerageDiscountPercent: 'Impact amount',
					brokerageDiscountType: {
						label: 'Brokerage type',
						values: {
							acquisition: 'Acquisition',
							subsequent: 'Subsequent',
							other: 'Other',
						},
					},
				},
			},
			bundleDiscountDetail: {
				sectionTitle: 'Bundle detail',
				labels: {
					currentPrice: 'Bundle price with applied discount',
					originalPrice: 'Original bundle price',
				},
			},
			interestRateDetail: {
				sectionTitle: 'Interest Rate',
				labels: {
					currentRate: 'Current interest rate',
					appliesUpTo: 'Applies to amount',
					historyButton: 'Interest rate history',
					historyDialogTitle: 'Interest rate history',
					rate: 'Interest rate',
				},
			},
			affectedProductsDetail: {
				affectedProducts: 'Action applied to products',
				affectedProductTypes: 'Action applied to product types',
				additionalDataLoadFailed: 'Additional data could not be loaded',
				affectedProductGroupType: {
					label: 'Action applied to product group',
					values: {
						bundle: 'Bundle',
						savings: 'Savings accounts',
						current: 'Current accounts',
						other: 'Other',
					},
				},
				labels: {
					productType: 'Product type',
					accountNumber: 'Account number',
					accountName: 'Account name',
				},
			},
		},
		tooltips: {
			notDriverClient:
				'Bundle discount action setup is available only for the client who is the owner of the bundle.',
			hasActiveBundleDiscount: 'Cannot set another bundle discount action',
		},
		setMarketingActionDialog: {
			title: 'Set marketing action',
			setButton: 'Set',
			free: 'Free',
			params: 'Parameters',
			hint: 'Currently only bundle discount actions can be set',
			labels: {
				action: 'Action',
				actionPackagePrice: 'Action package price:',
				advisorCommissionImpact: 'Impact on advisor commission:',
				validityDate: 'Validity date',
				date: 'Date',
			},
			snackbar: {
				success: 'Marketing action was set',
				error: 'Marketing action could not be set',
			},
			validToOptions: {
				unlimited: 'Unlimited',
				oneYear: '12 months (to {validToDate})',
				ownDate: 'Own date',
			},
		},
		editMarketingActionDialog: {
			title: 'Change marketing campaign settings',
			editButton: 'Save',
			newActionValidity: 'New action validity',
			labels: {
				action: 'Action',
				validityDate: 'Validity date',
				date: 'Date',
			},
			snackbar: {
				success: 'Marketing campaign has been changed',
				error: 'Failed to change marketing campaign',
			},
			validToOptions: {
				unlimited: 'Unlimited',
				oneYear: '12 months',
				ownDate: 'Own date',
			},
		},
		campaignUsageStatusDialog: {
			title: 'Member Get Member Action Usage Status',
			noAvailableActions: 'No available actions for the client',
			failedToLoad: 'Failed to load data',
			noCampaignUsageDetail: 'No campaign usage details available',
			columns: {
				campaignName: 'Campaign Name',
				duration: 'Duration',
				successfulRecommendations: 'Successful recommendations',
			},
			additionalData: {
				sectionTitle: 'Additional Data',
				labels: {
					campaignCode: 'Campaign ID',
				},
			},
			recommendationCode: 'Recommendation code',
			clientsWhoUsedAction: 'Clients who used the action',
			clientDataWarning: 'Client names must not be communicated to the client',
			clientPid: 'Client PID',
			usageDate: 'Usage date',
		},
		actionsButton: {
			title: 'Actions',
			usageStatusAction: 'Member Get Member Action Usage Status',
		},
	},
	clientApplications: {
		title: 'Applications',
		failedToLoadActivations: 'Failed to load activations',
		horizontalMenuItems: {
			validActivations: 'Valid activations {validActivationsCount}',
			activationsHistory: 'Activations history {activationHistoryCount}',
			psd2ConnectedBanks: 'Connected banks {psd2ConnectedBanksCount}',
			identityVerification: 'Identity verification {identityVerificationCount}',
		},
		clientActivationsHistory: {
			noHistoricActivations: 'No historic activations',
		},
		clientValidActivationsPage: {
			noValidActivations: 'No valid activations',
			applicationsTable: {
				additionalData: 'Additional data',
				columns: {
					deviceName: 'Device name',
					appVersion: 'Application version',
					lastUsed: 'Last used',
				},
				statuses: {
					active: 'Active',
					blocked: 'Blocked',
					created: 'Created',
					pendingCommit: 'Pending creation',
					removed: 'Removed',
				},
				detail: {
					activationDate: 'Activation date',
					enabledNotifications: 'Enabled notifications',
					deviceId: 'Device ID',
					deviceFingerprint: 'Fingerprint',
				},
				buttons: {
					block: 'Block',
					unblock: 'Unblock',
					remove: 'Remove',
				},
				confirmationDialog: {
					block: {
						title: 'Block activation?',
						description:
							'Activation on device {deviceName} will be temporarily blocked. The client will not be able to log into the application.',
						snackBar: {
							success: 'Activation was blocked',
							error: 'An error occurred while blocking the activation',
						},
					},
					unblock: {
						title: 'Unblock activation?',
						description:
							'Activation on device {deviceName} will be unblocked. The client will be able to log into the application again.',
						snackBar: {
							success: 'Activation was unblocked',
							error: 'An error occurred while unblocking the activation',
						},
					},
					remove: {
						title: 'Remove activation?',
						description:
							'Activation on device {deviceName} will be permanently removed. This action is irreversible. If the client does not have any other registered devices, they will have to go through a new application activation process.',
						snackBar: {
							success: 'Activation was removed',
							error: 'An error occurred while removing the activation',
						},
					},
				},
			},
		},
		clientApplicationsNewActivations: {
			status: {
				newAppActivationsAreBlocked: 'New app activations are blocked',
				newAppActivationsAreBlockedTooltip:
					'The client cannot perform new activations of the mobile application on either the existing or new device. The client can only use valid activations (if they are not already blocked).',
			},
			blockDialog: {
				title: 'Block new activations?',
				description:
					'The client will temporarily not be able to activate the application on an existing or new device.',
				blockButton: 'Block',
				snackbar: {
					success: 'New application activations are blocked',
					error: {
						default: 'Failed to block new application activations',
						notAcceptable:
							'Failed to block new application activations, PID not found.',
						badGateway:
							'Failed to block new application activations, bad request.',
						conflict:
							'Failed to block new application activations, already blocked.',
					},
				},
			},
			unblockDialog: {
				title: 'Unblock new activations?',
				description:
					'The client will be able to perform new application activations on an existing or new device.',
				unblockButton: 'Unblock',
				snackbar: {
					success: 'New application activations have been unblocked',
					error: {
						default: 'Failed to unblock new application activations',
						notAcceptable:
							'Failed to unblock new application activations, PID not found.',
						badGateway:
							'Failed to unblock new application activations, bad request.',
						conflict:
							'Failed to unblock new application activations, already unblocked.',
					},
				},
			},
			actions: {
				sectionTitle: 'Activations',
				blockFutureActivations: 'Block future app activations',
				unblockFutureActivations: 'Unblock future app activations',
			},
		},
		clientPsd2ConnectedBanksPage: {
			noPsd2ConnectedBanks: 'No connected banks',
			failedToLoadPsd2ConnectedBanks: 'Failed to load connected banks',
			connectedBanksTable: {
				bank: 'Bank',
				bankListNames: {
					airbank: 'Air Bank a.s.',
					creditas: 'Banka CREDITAS a.s.',
					csas: 'Česká spořitelna, a.s.',
					csob: 'Československá obchodní banka, a. s.',
					fio: 'Fio banka, a.s.',
					jtb: 'J&T BANKA, a.s., pobočka zahraničnej banky',
					kb: 'Komerční banka, a.s.',
					mbank: 'mBank S.A., organizační složka',
					mockCobs: 'Mock Cobs banka',
					moneta: 'MONETA Money Bank, a.s.',
					raiffeisen: 'Raiffeisenbank a.s.',
					unicredit: 'UniCredit Bank Czech Republic and Slovakia, a.s.',
					unknownBank: 'Unknown bank',
				},
				detail: {
					revokeButton: 'Revoke',
					revokeDialog: {
						revokeConnectionTitle: 'Revoke connection?',
						revokeConnectionDescription:
							"Connection with {bankName} will be revoked. This action can't be undone. If the client wants to use the link, they will need to go through a new link setup.",
						snackbarSuccess: 'Connection was revoked',
						snackbarError: 'Failed to revoke the connection',
					},
				},
				reloadBankListNames: {
					reloadButton: 'Update list',
					snackbarSuccess: 'List updated',
					snackbarError: 'Failed to update the list',
				},
			},
		},
	},
	clientBankIdentity: {
		title: 'Bank identity',
		clientCanFullUseBankIdentity: 'The client can fully use the bank identity',
		clientCanPartialUseBankIdentity:
			'The client can partially use the bank identity',
		clientCantUseBankIdentity: 'The client cannot use the bank identity',
		conditions: {
			clientMeetsConditions:
				'The client meets the conditions for using bank identity',
			clientMeetsConditionsTooltip:
				'Checked conditions: 1) Client is currently a bank client, 2) Client is over 15 years old, 3) Client is identified in person registry.',
			clientCanUseBankIdentityInMobileApp:
				'The client has allowed to use bank identity in the mobile application',
			clientDataMeetConditionsForAllBankIdentityServices:
				'Client data meets the conditions for all bank identity services',
			clientDataMeetConditionsForAllBankIdentityServicesTooltip:
				'If some client data is missing or not sufficiently verified, the use of some/all banking identity services may be restricted.',

			clientHasCreatedBankIdentityAndItIsActive:
				'The client has created and active bank identity',
		},
		notEligibleReasons: {
			clientAgeUnderLimit: 'age under 15',
			clientIsNotBankClient: 'not a bank client',
			clientHasNoBankAifo: 'not identified in the person registry',
		},
		setDisabled: {
			set: 'set',
			disabled: 'Disabled',
		},
		bankIdInNiaErrors: {
			isBlocked: 'Identity is blocked',
			isNotActive: 'Identity was not created',
		},
		impactOnBankIdServicesTitle: 'Impact on individual Bank iD services',
		impactOnBankIdServices: {
			bankIdConnect: 'Bank iD Connect',
			bankIdIdentify: 'Bank iD Identify',
			bankIdIdentifyAml: 'Bank iD Identify Aml',
			bankIdIdentifyPlus: 'Bank iD Identify Plus',
			bankIdSign: 'Bank iD Sign',
		},
		bankIdServicesOverviewTitle: 'Overview of Bank iD Services',
		bankIdServicesDescription: {
			bankIdConnect:
				'A service for logging into online services using bank identity without the need to create new login credentials, e.g., logging into customer accounts on private portals, government administration, or companies.',
			bankIdIdentify:
				"Basic identity verification that enables access to online services and portals, e.g., logging into the Citizen's Portal, confirming identity on e-shops, or accessing online government services.",
			bankIdIdentifyPlus:
				'Extended identity verification with the ability to legally sign documents digitally, e.g., concluding contracts online, signing mortgages, insurance, or legal documents with full legal validity.',
			bankIdIdentifyAml:
				'A service for identity verification that meets anti-money laundering and counter-terrorism financing requirements, e.g., registering on cryptocurrency exchanges, investment firms, or other financial institutions.',
			bankIdSign:
				'A service for digitally signing documents with legal validity, allowing contracts to be concluded and important documents to be signed online without the need for a physical signature.',
		},
		showErrorDataButton: 'Show error data',
		errorDataTitle: 'Incorrect data for use in banking identity',
		errorData: {
			name: {
				title: 'Name',
				text: 'Missing or unverified name data in basic registers',
			},
			email: {
				title: 'E-mail',
				text: 'Missing email address',
			},
			phoneNumber: {
				title: 'Phone',
				text: 'Missing phone number',
			},
			birthdate: {
				title: 'Date of Birth',
				text: 'Missing or unverified birth data in basic registers',
			},
			addresses: {
				title: 'Addresses',
				text: 'Missing or unverified permanent address data in basic registers',
			},
			titles: {
				title: 'Titles',
				text: 'Missing title prefix / suffix',
			},
			gender: {
				title: 'Gender',
				text: 'Missing gender data',
			},
			birthNumber: {
				title: 'Birth number',
				text: 'Missing or unverified birth number data in basic registers',
			},
			paymentAccounts: {
				title: 'Payment Accounts',
				text: 'Missing account number in Partners Bank',
			},
			birthPlaceNationality: {
				title: 'Place of Birth',
				text: 'Missing or unverified place of birth data in basic registers',
			},
			maritalStatus: {
				title: 'Marital Status',
				text: 'Missing marital status data',
			},
			idCards: {
				title: 'iD Cards',
				text: 'Missing or unverified iD card data in basic registers or Bank iD',
			},
			legalStatus: {
				title: 'Legal Status / Competence',
				text: 'Missing data on legal competence or adulthood',
			},
			verification: {
				title: 'Verification',
				text: 'Missing client data verification',
			},
		},
		errorDataLoading: 'Failed to retrieve bank identity data',
		blockBankIdentityDialog: {
			title: 'Block bank identity?',
			description:
				'The client will temporarily not be able to log in to third-party services via the Partners bank identity.',
			blockButton: 'Block',
			snackbar: {
				success:
					'Bank identity blocking request submitted. Processing may take a few seconds.',
				error: 'Failed to submit bank identity blocking request.',
			},
		},
		reconcileClientDataInNiaDialog: {
			title: 'Verify identity?',
			description:
				'A new client identity verification will be initiated in NIA.',
			reconcileButton: 'Verify identity',
			snackbar: {
				success: 'Identity verification in NIA initiated',
				error: 'Identity verification in NIA failed',
			},
		},
		unblockBankIdentityDialog: {
			title: 'Unblock bank identity?',
			description:
				'The client will be able to log in to third-party services via the Partners bank identity again.',
			unblockButton: 'Unblock',
			snackbar: {
				success:
					'Bank identity unblocking request submitted. Processing may take a few seconds.',
				error: 'Failed to submit bank identity unblocking request.',
			},
		},
		historyOfUse: {
			title: 'History of use',
			failedToLoadHistory: 'Failed to load history',
			noHistory: 'No history',
			tabs: {
				authoritiesAndTheState: 'Authorities and the state',
				privateCompanies: 'Private companies',
			},
			authoritiesAndTheStateTab: {
				tableHeader: {
					type: 'Type',
					date: 'Date',
				},
				tableContent: {
					identityOfTheCitizen: 'Identity of the citizen',
				},
				filter: {
					eventTimeFrom: 'Event time from',
					eventTimeTo: 'Event time to',
					dateOfUse: 'Date of use',
				},
			},
			privateCompaniesTab: {
				table: {
					header: {
						clientProviderName: 'Company',
						type: 'Event type',
						createdAt: 'Date',
					},
					companies: {
						single: '{count} company',
						few: '{count} companies',
						plural: '{count} companies',
					},
				},
				eventType: {
					login: 'Login',
					userInfo: 'User information',
					profile: 'Person identification',
					sign: 'Document signature',
					notification: 'Notification',
					consentedScopes: 'Consents granted',
					all: 'All',
				},
				filter: {
					dateOfUse: 'Date of use',
					type: 'Event type',
				},
				signDetail: {
					signObjectSection: {
						sectionTitle: 'Signed statement',
						hashLabel: 'Hash',
						valueLabel: 'Value',
					},
					documentSection: {
						sectionTitle: 'Signed document',
						titleLabel: 'Document title',
						createdLabel: 'Creation date',
					},
					documentsSection: {
						sectionTitle: 'Signed documents',
						envelopeNameLabel: 'Envelope name',
						envelopeHashLabel: 'Envelope hash',
						titleLabel: 'Document title',
						createdLabel: 'Creation date',
						documentDetailsLabel: 'Document details',
					},
					documentDialog: {
						dialogTitle: 'Document information',
						titleLabel: 'Document title',
						authorLabel: 'Author',
						createdLabel: 'Creation date',
						subjectLabel: 'Document content',
						idLabel: 'Document ID (external)',
						pagesLabel: 'Number of pages',
						hashLabel: 'Hash',
						closeLabel: 'Close',
					},
				},
				notificationDetail: {
					affectedDataSection: {
						sectionTitle: 'Affected data',
						affectedDataLabel: 'Affected data',
					},
					informedCompaniesSection: {
						sectionTitle: 'Informed companies',
						clientNameLabel: 'Company',
						clientTypeLabel: 'Company type',
						clientIdLabel: 'Company ID',
					},
				},
				consentedScopesDetail: {
					affectedScopeSection: {
						sectionTitle: 'Affected scope',
						affectedScopesLabel: 'Affected scopes',
					},
				},
				affectedClaims: {
					titlePrefix: 'Title before name',
					titleSufix: 'Title after name',
					givenName: 'First name',
					familyName: 'Last name',
					middleName: 'Middle name',
					gender: 'Gender',
					birthdate: 'Date of birth',
					birthnumber: 'Birth number',
					age: 'Age',
					majority: 'Majority',
					dateOfDeath: 'Date of death',
					birthplace: 'Place of birth',
					birthcountry: 'Country of birth',
					primaryNationality: 'Nationality',
					maritalstatus: 'Marital status',
					addresses: 'Addresses',
					idcards: 'ID cards',
					email: 'Email',
					emailVerified: 'Email verified',
					phoneNumber: 'Mobile phone',
					phoneNumberVerified: 'Mobile phone verified',
					pep: 'Politically exposed person',
					limitedLegalCapacity: 'Limited legal capacity',
				},
				scopes: {
					openId: 'OpenID Connect',
					offlineAccess: 'Offline access',
					profileAddresses: 'Addresses',
					profileBirthdate: 'Date of birth',
					profileBirthNumber: 'Birth number',
					profileBirthplaceNationality: 'Place of birth and nationality',
					profileEmail: 'Email address',
					profileGender: 'Gender',
					profileIdCards: 'ID cards',
					profileLegalStatus: 'Legal status',
					profileLocale: 'Locale',
					profileMaritalStatus: 'Marital status',
					profileName: 'Name',
					profilePaymentAccounts: 'Payment accounts',
					profilePhoneNumber: 'Phone number',
					profileTitles: 'Titles',
					profileUpdatedAt: 'Last updated',
					profileZoneInfo: 'Timezone',
					profileVerification: 'Verification status',
					notificationClaimsUpdated: 'Claims updated notification',
				},
			},
			progressSection: {
				sectionTitle: 'Progress',
				eventType: {
					attempt: 'Verification started',
					attemptCancelled: 'Verification cancelled (in app)',
					attemptConfirmed: 'Verification confirmed (in app)',
					succeeded: 'Verification completed',
					failed: 'Verification error',
					canceled: 'Verification cancelled (unknown PID)',
					unknown: 'Unknown step',
				},
				accordionContent: {
					deviceName: 'Device name',
					verificationInstrument: 'Verification instrument ID',
					createdAt: 'Creation date',
					pidUuid: 'Client PID',
					bsi: 'BSI',
					flowType: 'Type of process',
				},
				failedReason: {
					clientNotFound: 'Client not found',
					optOut: 'Client has bank identity disabled',
					missingVerificationInstrument:
						'Client has no registered verification instrument (application and document)',
					missingBsi: 'Client not identified in NIA',
					unknown: 'Unknown error',
				},
			},
			additionalDataSection: {
				sectionTitle: 'Additional data',
				activityUuid: 'Activity ID',
				sessionCode: 'Session code',
			},
			devicesUsedSection: {
				sectionTitle: 'Devices used',
				deviceName: 'Device name',
				pidUuid: 'Client PID',
			},
			infoAboutUserSection: {
				sectionTitle: 'Information about the client',
				name: 'Name',
				familyName: 'Family name',
				email: 'E-mail',
				phoneNumber: 'Phone',
				birthdate: 'Date of birth',
				nationalities: 'Nationalities',
			},
			companySection: {
				sectionTitle: 'Company',
				clientName: 'Application name',
				clientType: 'Company type',
				clientId: 'Company ID',
				clientTypes: {
					unknown: 'Unknown',
					bank: 'Bank',
					financialCompany: 'Financial company licensed by the CNB',
					amlCompany: 'Non-financial company with statutory AML obligations',
					publicEntity: 'Public entity',
					nonprofitOrganization: 'Non-profit organization',
					otherCompany: 'Other',
				},
			},
		},
	},
	clientTransactions: {
		title: 'Transactions',
		horizontalMenuItems: {
			transactionsHistory: 'History',
			transactionsFuture: 'Future {count}',
			transactionsStanding: 'Standing orders {count}',
			transactionsSIPO: 'SIPO {count}',
			transactionsDirectDebits: 'Direct debits {count}',
		},
		accountSelectFilter: {
			text: 'Account:',
			subtitles: {
				activeAccounts: 'Active accounts',
				closedAccounts: 'Closed accounts',
			},
		},
		generateTransactionConfirmation: {
			button: 'Generate confirmation',
			disabledTooltip:
				'Generating confirmation for this transaction type is under development',
			snackbar: {
				success: 'Confirmation was generated',
				error: 'Confirmation could not be generated',
				downloadError: 'Confirmation could not be downloaded',
			},
		},
		counterPartyPaymentType: {
			cardTransaction: 'Card transaction',
			block: 'Block',
			other: 'Transaction',
			zpsTransfer: 'Foreign payment',
			zpsFee: 'Foreign payment fee',
			exchange: 'Currency conversion',
			transferConversion: 'Transfer',
		},
		paymentType: {
			block: 'Block',
			cardCashAdvance: 'Cash withdrawal',
			cardCashback: 'Card cashback',
			cardMoneyTransfer: 'Incoming payment to card',
			cardPayment: 'Payment with card',
			cardRefund: 'Cash return to card',
			cardReturn: 'Cash return to card',
			cardWithdrawal: 'Card withdrawal',
			cardSuperSavingAccount: 'Round-up savings from card payment',
			collection: 'Collection',
			directDebit: 'Direct debit',
			holdExecution: 'Hold execution',
			holdFauOffice: 'Hold FAU',
			holdLivingWage: 'Hold living wage',
			holdPolice: 'Hold police',
			holdRiskAml: 'Hold risk/AML',
			holdSettlement: 'Hold settlement',
			instalment: 'Instalment',
			instantPayment: 'Instant payment',
			negativeInterestCorrection: 'Negative interest correction',
			negativeInterest: 'Negative interest',
			positiveInterestCorrection: 'Positive interest correction',
			positiveInterest: 'Positive interest',
			productPackageFee: 'Product package fee',
			standingOrder: 'Standing order',
			sipo: 'Sipo',
			sweep: 'Minimum balance monitoring',
			transfer: 'Transfer',
			withholdingTax: 'Withholding tax',
			sepaTransfer: 'SEPA transfer',
			cardAccountFunding: 'Card payment',
			oneTimeFee: 'Onetime fee',
			zpsTransfer: 'Foreign payment',
			zpsFee: 'Foreign payment fee',
			exchange: 'Currency conversion',
			transferConversion: 'Transfer',
			balanceRealignment: 'Balance realignment',
			bankInsuranceFee: 'Bank insurance fee',
			cryptoAssetsPurchase: 'Crypto purchase',
			cryptoAssetsSale: 'Crypto sale',
			cryptoAssetsPurchaseFee: 'Crypto purchase fee',
			cryptoAssetsSaleFee: 'Crypto sale fee',
			tdInitPayment: 'Term deposit setup',
			tdFinalTransfer: 'Term deposit termination',
		},
		clientTransactionsHistoryPage: {
			noTransactions: 'No transactions',
			failedToLoadTransactions: 'Failed to load transactions',
			tokenTransaction: 'Token',
			currencyExchangeTopLine: '{fromCurrency} → {toCurrency}',
			directions: {
				incoming: 'Incoming',
				outgoing: 'Outgoing',
			},
			paymentGroups: {
				cardTransactions: 'Card transactions',
				transfers: 'Transfers',
				other: 'Other',
			},
			filter: {
				titles: {
					search: 'Search',
					direction: 'Direction',
					paymentGroups: 'Payment groups',
					card: 'Card',
					transactionStatus: 'Transaction status',
					transactionDate: 'Transaction date',
					transactionAmount: 'Transaction amount',
				},
				tooltips: {
					search:
						'Searches for text in transaction parameters like counterparty name, account number, variable / specific / constant symbol, note for me or message for recipient.',
				},
				labels: {
					fullTextSearch: 'Search in transaction',
					card: 'Card',
					dateFrom: 'Date from',
					dateTo: 'Date to',
					originalCounterpartyNameTooltip:
						'Original counterparty name: {originalName}',
					clientCounterpartyNameTooltip: 'Client counterparty name: {name}',
					systemCounterpartyNameTooltip: 'System counterparty name: {name}',
				},
				noData: {
					cards: 'No cards',
				},
				failedToLoadData: {
					cards: 'Failed to load cards',
				},
				noCardChosen: 'None',
			},
			transactionStatus: {
				cancelledByBank: 'Cancelled by bank',
				holded: 'Held',
				partialRealized: 'Partially realized',
				readyForRealization: 'Ready for realization',
				realized: 'Realized',
				reverted: 'Reverted',
				cancelledByClient: 'Cancelled by client',
				expired: 'Expired',
				rejected: 'Rejected',
				manuallyReleased: 'Manually released',
			},
			transactionStatusReason: {
				declinedAtmDisabled: 'Unauthorized ATM withdrawals',
				declinedCashBackInForeignCurrency:
					'Unauthorized cash back in foreign currency',
				declinedContactlessDisabled: 'Unauthorized contactless payments',
				declinedDailyLimitExceeded: 'Daily limit exceeded',
				declinedDccBlocked: 'Unauthorized DCC',
				declinedEcommerceDisabled: 'Unauthorized online payments',
				declinedFraudInvestigation: 'Suspicion of fraud',
				declinedFrozenCard: 'Frozen card',
				declinedInsufficientFounds: 'Insufficient funds on the account',
				declinedMagStripeDisabled: 'Unauthorized use of the magnetic stripe',
				declinedMaximalLimitExceeded: 'Maximum limit exceeded',
				declinedMccBlocked: 'Unauthorized MCC',
				declinedTerminatedCard: 'Terminated card',
				declinedUnactivatedCard: 'Unactivated card',
				declinedUnexpectedProblem: 'Unexpected problem',
				declinedSoftDecline:
					'New transaction with extra client confirmation required',
				distraintTermination: 'Distraint termination',
				expired: 'Expiration',
				expiredCardHold: 'Expiration of card hold',
				expiredInsufficientBalance: 'Insufficient available balance',
				manuallyReleased: 'Manually released',
				rejectedFraudDetection: 'Suspicion of fraud',
				reverted: 'Returned',
				unknown: 'Unknown',
				invalidCVV2: 'Invalid CVV code',
				invalidExpirationDate: 'Invalid - expiration',
				invalidPin: 'Invalid PIN',
				pinNotAssigned: 'PIN not assigned',
			},
			walletType: {
				applePay: 'Apple pay',
				googlePay: 'Google pay',
				garminPay: 'Garmin pay',
				fidesmoPay: 'Fidesmo pay',
				swatchPay: 'Swatch pay',
				clickToPay: 'Click to pay',
				other: 'Other',
			},
			transactionDetail: {
				noTransactionsDetail: 'No transaction detail',
				failedToLoadTransactionDetail: 'Failed to load transaction detail',
				accountBalance: 'balance {balance}',
				labels: {
					originAccount: 'From account',
					targetAccount: 'To account',
					bankCode: 'SWIFT/BIC / bank code',
					status: 'Transaction status',
					variable: 'Variable symbol',
					constant: 'Constant symbol',
					specific: 'Specific symbol',
					message: 'Message for recipient',
					noteForMe: 'Note for me',
					paymentReference: 'Payment reference',
					feeType: 'Fee type',
					shaValue: 'SHA',
					valutaDate: 'Valuta date',
					realizationDate: 'Realization date',
					paymentCategory: 'Payment category',
				},
				counterpartyInfo: {
					title: 'Recipient information',
					labels: {
						recipientName: 'Recipient / Company name',
						street: 'Street',
						city: 'City',
						postcode: 'Postcode',
						country: 'Country',
						province: 'Province',
					},
				},
				zpsCounterpartyInfo: {
					outgoingTitle: 'Recipient information',
					incomingTitle: 'Sender information',
					labels: {
						recipientName: 'Recipient / Company name',
						senderName: 'Sender / Company name',
						street: 'Street',
						city: 'City',
						postcode: 'Postcode',
						country: 'Country',
						province: 'Province',
						additionalInfo: 'Additional information',
					},
				},
				counterpartyBankInfo: {
					title: 'Recipient bank information',
					labels: {
						bankName: 'Name',
						address: 'Address',
						country: 'Country',
						accountNumberType: 'Account type',
					},
				},
				additionalData: {
					title: 'Additional data',
					labels: {
						transactionId: 'Transaction ID',
						cardPan: 'Card',
						transactionStatusReason: 'Transaction status reason',
						approvedBy: 'Approved by',
						dueDate: 'Due date',
						source: 'Source',
						originalCounterpartyName: 'Original counterparty name',
						clientCounterpartyName: 'Client counterparty name',
					},
					requestedOperation: {
						createPaymentOrder: 'Mobile application',
						createPaymentOrderFromFip: 'FIP advisor',
						createPsd2PaymentOrder: 'PSD2',
					},
					approverNames: {
						approved: '{date}, {approver}',
					},
				},
				conversion: {
					title: 'Conversion',
					labels: {
						originalAmount: 'Original amount',
						exchangeRate: 'Exchange rate',
						ecbDeviation: 'ECB deviation',
					},
				},
				paymentCategory: {
					categoryGroup: 'Category - group',
					category: 'Category',
					categoryGroups: {
						consumption: 'Consumption',
						housingCosts: 'Housing costs',
						financialIndependence: 'Investments and financial independence',
						shortTermReserve: 'Insurance and short-term reserve',
					},
					internalCategories: {
						atm: 'ATM withdrawal',
						buildingSavings: 'Building savings',
						consumptionOther: 'Other consumption',
						digitalSubscriptions: 'Subscriptions and digital services',
						education: 'Education',
						energy: 'Energy',
						entertainment: 'Entertainment',
						fashion: 'Clothing and fashion',
						groceries: 'Groceries',
						healthBeauty: 'Health and beauty',
						houseGarden: 'House and garden',
						householdFees: 'Household fees',
						householdOther: 'Other housing',
						investment: 'Investment',
						investmentsOther: 'Other investments and financial independence',
						lifeInsurance: 'Life insurance',
						mortgage: 'Mortgage',
						nonlifeInsurance: 'Non-life insurance',
						rent: 'Rent',
						restaurant: 'Restaurant',
						retirementSavings: 'Retirement savings',
						services: 'Services',
						shortTimeOther: 'Other insurance and short-term reserve',
						shortTimeReserve: 'Savings',
						transportation: 'Transportation',
						vacation: 'Vacation',
						vice: 'Vice',
					},
				},
				buttons: {
					unblockTransaction: 'Unblock transaction',
				},
				unblockTransactionDialog: {
					title: 'Unblock transaction?',
					description:
						'Transaction for the amount of {amount} will be unblocled.',
					unblockButton: 'Unblock',
					snackbar: {
						success: 'Transaction was unblocked',
						error: 'Transaction could not be unblocked',
					},
				},
			},
		},
		clientTransactionsFuturePage: {
			noTransactions: 'No transactions',
			failedToLoadTransactions: 'Failed to load transactions',
			transactionDetail: {
				noTransactionsDetail: 'No transaction detail',
				failedToLoadTransactionDetail: 'Failed to load transaction detail',
				labels: {
					originAccount: 'From account',
					targetAccount: 'To account',
					message: 'Message for recipient',
				},
				additionalData: {
					title: 'Additional data',
					labels: {
						transactionId: 'Transaction ID',
					},
				},
			},
		},
		clientStandingOrdersPage: {
			noActiveStandingOrders: 'No active standing orders',
			noCancelledStandingOrders: 'No cancelled standing orders',
			failedToLoadStandingOrders: 'Failed to load standing orders',
			filter: {
				active: 'Active',
				cancelled: 'Cancelled',
			},
			columns: {
				paymentOrder: 'Payment order',
				amount: 'Amount',
				processStatus: 'Status',
			},
			processStatus: {
				active: 'Active',
				inactive: 'Inactive',
				cancelled: 'Cancelled',
				expired: 'Expired',
			},
			standingOrderType: {
				regular: 'Standing order',
				savingGoal: 'Saving goal',
				pocketMoney: 'Pocket money',
			},
			standingOrderDetail: {
				labels: {
					nextPaymentDate: 'Next payment date',
					recipientMessage: 'Message for recipient',
					messageForSender: 'Note for me',
					paymentCodes: {
						variable: 'Variable symbol',
						specific: 'Specific symbol',
						constant: 'Constant symbol',
					},
					recipientAccountNumber: 'Recipient account number',
				},
				additionalInfo: {
					sectionTitle: 'Additional data',
					labels: {
						standingOrderId: 'Standing order ID',
						firstPaymentDate: 'First payment date',
						recipientCategory: 'Category',
					},
				},
				validity: {
					sectionTitle: 'Validity',
					labels: {
						validFrom: 'Valid from',
						validTo: 'Valid to',
					},
				},
			},
		},
		clientSiposPage: {
			noActiveSipos: 'No active SIPO',
			noCancelledSipos: 'No cancelled SIPO',
			failedToLoadSipos: 'Failed to load SIPO',
			filter: {
				active: 'Active',
				cancelled: 'Cancelled',
			},
			columns: {
				name: 'Name',
				amount: 'Amount',
				processStatus: 'Status',
			},
			labels: {
				amountTo: 'up to {amount}',
			},
			sipoDetail: {
				labels: {
					connectionNumber: 'Connecting number',
					messageForSender: 'Note for me',
				},
				additionalInfo: {
					sectionTitle: 'Additional data',
					labels: {
						sipoId: 'SIPO ID',
					},
				},
				validity: {
					sectionTitle: 'Validity',
					labels: {
						validFrom: 'Valid from',
						validTo: 'Valid to',
					},
				},
			},
		},
		clientDirectDebitsPage: {
			noActiveDirectDebits: 'No active direct debits',
			noCancelledDirectDebits: 'No cancelled direct debits',
			failedToLoadDirectDebits: 'Failed to load direct debits',
			filter: {
				active: 'Active',
				cancelled: 'Cancelled',
			},
			columns: {
				name: 'Name',
				amount: 'Amount',
				processStatus: 'Status',
			},
			labels: {
				amountTo: 'up to {amount}',
			},
			directDebitDetail: {
				labels: {
					recipientAccount: 'Recipient account',
					recipientMerchantName: 'Recipient name',
					messageForSender: 'Note for me',
					recipientMessage: 'Message for recipient',
				},
				additionalInfo: {
					sectionTitle: 'Additional data',
					labels: {
						directDebitId: 'Direct debit ID',
					},
				},
				validity: {
					sectionTitle: 'Validity',
					labels: {
						validFrom: 'Valid from',
						validTo: 'Valid to',
					},
				},
			},
		},
		transactionPeriodicity: {
			weekly: 'Weekly',
			monthly: 'Monthly',
			quarterly: 'Quarterly',
			semiAnnually: 'Semi-annually',
			annually: 'Annually',
		},
	},
	clientRequirements: {
		title: 'Requirements',
		bankOperationGroups: {
			payment: 'Payment',
			account: 'Account',
			card: 'Card',
			loan: 'Loan',
			standingOrder: 'Standing order',
			directDebit: 'Direct debit',
			sipo: 'SIPO',
			insurance: 'Insurance',
			crypto: 'Crypto',
		},
		pidOperationGroups: {
			application: 'Application',
			consulting: 'Consulting',
		},
		operationTextWithGroupPrefix: '{operationGroup} → {operationText}',
		operations: {
			acceptationFipDocument: 'Signing a document/contract from FIP',
			accountLimitSet: 'Change of account payment limit',
			activateCard: 'Card activation',
			activationChildApp: 'Child app activation',
			addDisponentToBankChild: 'Adding a disponent to a child account',
			backofficeClientIdentityConfirmation:
				'Identity confirmation for remote service',
			backofficeChangeConsent: 'Change of consent',
			backofficeChangeContactResidence: 'Change of contact address',
			bundleChange: 'Bundle change',
			buyCrypto: 'Buy crypto',
			cardsAcsAuthenticationApprove:
				'3D Secure payment confirmation (historical)',
			cardsTokenInappVerification: 'Card token confirmation',
			contractSharingInvitation:
				'Consent confirmation for contract sharing with another client',
			createDirectDebit: 'New direct debit',
			createChallengePaymentOrder: 'New challenge',
			createChildDirectDebit: 'New direct debit (child)',
			createChildPaymentOrder: 'New payment order (child)',
			createChildRegularStandingOrder: 'New standing order (child)',
			createCryptoAccount: 'New crypto account',
			createMobility: 'Account transfer request (mobility)',
			createPaymentOrder: 'New payment order',
			createPaymentOrderFromFip: 'New payment order (from FIP)',
			createPocketMoney: 'New pocket money',
			createPsd2SepaPayment: 'New PSD2 SEPA payment order',
			createPsd2ZpsPayment: 'New PSD2 foreign payment order',
			createProductCurrentAccountBasic: 'New current account',
			createProductCurrentAccountChild: 'New child account',
			createProductCurrentAccountSecondary: 'New secondary account',
			createProductCurrentAccountShared: 'New shared account',
			createProductsFipOnboarding: 'Product creation with advisor',
			createProductProtectedAccount: 'New protected account',
			createProductTermDeposit: 'New term deposit account',
			createProductSavingsAccountBasic: 'New savings account',
			createProductSavingsAccountChild: 'New child savings account',
			createProductSavingsAccountShared: 'New shared savings account',
			createProductSavingsGoalBasic: 'New savings goal',
			createProductSavingsGoalChild: 'New child savings goal',
			createProductSavingsGoalShared: 'New shared savings goal',
			createRegularStandingOrder: 'New standing order',
			createSavingsGoalStandingOrder: 'New standing order for savings goal',
			createSipo: 'New SIPO',
			createStandingOrderFromFip: 'New standing order (from FIP)',
			createTreasuryForeignPayment: 'New foreign payment (treasury)',
			createTreasuryPayment: 'New TREASURY payment order',
			deactivateStandingOrder: 'Pause standing order',
			deleteDirectDebit: 'Cancel direct debit',
			deletePocketMoney: 'Cancel pocket money',
			deleteSipo: 'Cancel SIPO',
			deleteStandingOrder: 'Cancel standing order',
			changeCustomAccountName: 'Change account name',
			changeProductSavingsAccountChild: 'Change child savings account',
			changeProductSavingsGoalBasic: 'Change savings goal',
			changeProductSavingsGoalChild: 'Change child savings goal',
			changeProductSavingsGoalShared: 'Change shared savings goal',
			changeSavingsAccountBasicParameters: 'Change savings account parameters',
			changeSavingsAccountSharedParameters:
				'Change shared savings account parameters',
			instrumentWithdraw: 'Sign investment withdrawal order',
			onboardingDocumentsAgreement:
				'Onboarding document acknowledgment (historical)',
			pidOnboardingDocumentsAgreement: 'Onboarding document acknowledgment',
			prepareSddPayload: 'Display sensitive card data',
			reactivateChildRegularStandingOrder: 'Reactivate standing order (child)',
			reactivateStandingOrder: 'Reactivate standing order',
			removeDisponentFromBankChild: 'Remove disponent from child account',
			stampApsLoanDocument: 'APS document (TBD)',
			terminateAccount: 'Account termination',
			updateDirectDebit: 'Update direct debit',
			updateChildDirectDebit: 'Update direct debit (child)',
			updateChildRegularStandingOrder: 'Update standing order (child)',
			updatePocketMoney: 'Update pocket money',
			updateSipo: 'Update SIPO',
			updateStandingOrder: 'Update standing order',
			pidOnboardingChildOnboardingMandate: 'Child onboarding mandate',
			terminateProductProtectedAccount4Eyes: 'Terminate protected account',
			terminateProductCurrentAccountBasic4Eyes: 'Terminate current account',
			terminateProductCurrentAccountSecondary4Eyes:
				'Terminate secondary current account',
			terminateProductCurrentAccountChild4Eyes: 'Terminate child account',
			terminateProductCurrentAccountShared4Eyes: 'Terminate shared account',
			terminateProductSavingsAccountBasic4Eyes: 'Terminate savings account',
			terminateProductSavingsAccountChild4Eyes:
				'Terminate child savings account',
			terminateProductSavingsAccountShared4Eyes:
				'Terminate shared savings account',
			cancelPaymentOrder: 'Cancel payment order',
			createSepaPayment: 'New SEPA payment order',
			changeAccountSettings: 'Change account settings',
			changeSavingsAccountChildParameters:
				'Change child savings account parameters',
			cards3DsRequest: '3D Secure payment confirmation',
			cardSetLimit: 'Set card limit',
			createPsd2PaymentOrder: 'New PSD2 payment order',
			createPsd2RegularStandingOrder: 'New PSD2 regular standing order',
			upsertSweep: 'Activate / change minimum balance monitoring',
			deactivateSweep: 'Deactivate minimum balance monitoring',
			createOdooPayment: 'New ODOO payment order',
			updatePsd2StandingOrder: 'Update PSD2 standing order',
			deletePsd2StandingOrder: 'Delete PSD2 standing order',
			createZpsPayment: 'New foreign payment order',
			createProductSupersavings: 'New smart reserve',
			changeProductSupersavings: 'Change smart reserve',
			terminateProductSupersavings: 'Terminate smart reserve',
			createPaymentTemplate: 'New payment template',
			updatePaymentTemplate: 'Update payment template',
			deletePaymentTemplate: 'Delete payment template',
			createExchangeOrder: 'New exchange order',
			eaLongtermTravelInsuranceCreate: 'New travel insurance',
			eaLongtermTravelInsuranceTermination: 'Terminate travel insurance',
			eaLongtermTravelInsuranceWithdraw: 'Withdraw from travel insurance',
			childAccountTransition: 'Child account transition',
			sellCrypto: 'Sell crypto',
			renteaContributionChange: 'Rentea contribution change',
			emailChange: 'Email change',
			phoneChange: 'Phone change',
			ocpWalletWithdraw: 'Wallet withdraw (OCP)',
			ocpNonPartnersBankAccountRegistration:
				'Non-partners bank account registration (OCP)',
			gdprConsent: 'GDPR consent',
			pidWorkflowDocument: 'Workflow document',
			ocpPartnersBankAccountRegistration:
				'Partners bank account registration (OCP)',
			deviceQrNewDeviceApproval: 'Activation approval from other device',
			identityVerification: 'Identity verification',
			unknown: 'Unknown operation',
		},
		operationStatus: {
			waitingForSign: 'Waiting',
			processing: 'Processing',
			canceled: 'Canceled',
			cancelling: 'Cancelling',
			done: 'Done',
			expired: 'Expired',
			expiring: 'Expiring',
			refused: 'Refused',
		},
		noRequirements: 'No requirements',
		failedToLoadRequirements: 'Failed to load requirements',
		horizontalMenuItems: {
			clientAndIdentity: 'Client and application',
			bankingProductsAndServices: 'Banking products and services',
		},
		filter: {
			all: 'All',
			waitingForApproval: 'Waiting for approval',
			operationType: 'Operation type',
			operationStatus: 'Operation status',
			selectOperationType: 'Type',
			noOperationTypes: 'No operation types',
			failedToLoadOperationTypes: 'Operation types could not be loaded',
		},
		requirementDetail: {
			requirementId: 'Requirement ID',
			expiration: 'Expiration date',
			document: 'Document',
			documents: 'Documents',
			acceptationFipDocument: {
				requestSource: 'Source',
				casId: 'Case ID in FIP',
				partnerName: 'Partner name',
				requestName: 'Request name',
			},
			backofficeClientIdentityConfirmation: {
				generatedByTheUser: 'Generated by the user',
				userPID: 'User PID',
				userId: 'User ID',
			},
			contractSharingInvitation: {
				inviterFullName: 'Contract owner',
				inviterPersonalId: 'Contract owner ID',
				invitedClientFullName: 'Shared with client',
				invitedClientPersonalId: 'Client ID',
			},
			addDisponentToBankChild: {
				newDisponentFullName: 'New disponent',
				newDisponentPidUuid: 'Disponent PID',
				childFullName: 'Affected child name',
				childPidUuid: 'Affected child PID',
			},
			bundleChange: {
				sourceBundleName: 'Original bundle',
				targetBundleName: 'New bundle',
			},
			createNewPaymentOrder: {
				amount: 'Amount',
				dueDate: 'Due date',
			},
			createSepaPayment: {
				amount: 'Amount',
				dueDate: 'Due date',
			},
			sweep: {
				iban: 'Current account number',
				sourceAccountUuid: 'Saving account ID',
				targetAmount: 'Minimal balance',
			},
			createPaymentOrderFromFip: {
				createdBy: 'Created by',
			},
			onboardingDocumentsAgreement: {
				documentType: 'Document type',
				documentTypes: {
					gdprConsent: 'GDPR consent',
					mobileAppTermsOfUse: 'Mobile app terms of use',
					unknown: 'Unknown',
				},
			},
			instrumentWithdraw: {
				amount: 'Amount',
				bankAccount: 'Account number',
				bankAccountCurrencyCode: 'Account currency',
				message: 'Message for recipient',
				noFees: 'No fees',
				note: 'Note',
				isin: 'ISIN',
				sections: {
					recipient: 'Recipient',
					fees: 'Fees',
				},
				withdrawalDate: 'Withdrawal date',
			},
			card3DsRequest: {
				merchantName: 'Merchant name',
				purchaseDate: 'Purchase date',
				amount: 'Amount',
				pan: 'Card number',
				referenceId: 'Request ID',
			},
			createMobility: {
				otherBankAccountNumber: 'Other bank account number',
				partnersAccountNumber: 'Partners account number',
				mobilityDate: 'Mobility date',
				mobilityDirection: 'Mobility direction',
				mobilityScope: 'Scope',
				oldMobilityScopes: {
					balanceTransferedPayments:
						'Transfer current balance and payments (without canceling account)',
					balanceTransferredCancelAccount:
						'Transfer current balance and cancel account (without transferring payments)',
					balanceTransferredPaymentsCancelAccount:
						'Transfer current balance, payments and cancel account',
				},
			},
			createZpsPayment: {
				amount: 'Amount',
				dueDate: 'Due date',
			},
			eaLongtermTravelInsuranceCreate: {
				productName: 'Name',
				productCode: 'Product code',
				paymentAccountIban: 'Payment account number',
				priceAmount: 'Insurance amount',
				pricePeriod: 'Frequency',
				validFrom: 'Valid from',
				sections: {
					insurance: 'Insurance',
				},
			},
			eaLongtermTravelInsuranceWithdraw: {
				insuranceUuid: 'Insurance ID',
				productCode: 'Product code',
				productName: 'Name',
				insuranceValidFrom: 'Valid from',
				paymentAccountIban: 'Payment account',
				withdrawalType: 'Withdrawal reason',
				withdrawalTypes: {
					coolingOffPeriod: 'Withdrawal within the 14-day period',
				},
			},
			createCryptoAccount: {
				fullName: 'Client name',
				activationDate: 'Activation date',
				sections: {
					bankDocuments: 'Bank documents',
					thirdPartyDocuments: 'Third party documents',
				},
			},
			buyCrypto: {
				amount: 'Total amount',
				dueDate: 'Purchase date',
				cryptoCurrency: 'Crypto currency',
				feeAmount: 'Fee amount',
				feeRate: 'Fee rate',
				netAmount: 'Amount purchased',
			},
			sellCrypto: {
				feeAmountPercentage: 'Fee rate',
				sellCryptoAmount: 'Amount sold',
				sellCryptoCurrency: 'Crypto currency',
			},
			accountLimitSet: {
				ownerAccountUuid: 'Account ID',
				newLimitAmount: 'New limit',
				infiniteLimit: 'Infinite limit',
			},
			createProductCurrentAccountBasic: {
				bundle: 'Package',
			},
			createProductCurrentAccountShared: {
				passengerPhoneNumber: 'Coowner phone',
			},
			createProductCurrentAccountChild: {
				childPhoneNumber: 'Child phone',
			},
			createProductsSavingsAccount: {
				sectionTitle: 'Interest rate',
				interestRate: 'Based interest rate',
				reducredInterestRate: 'Reduced interest rate',
			},
			directDebit: {
				section: {
					title: 'Affected account',
				},
			},
			standingOrder: {
				senderAccountNumber: 'Sender account number',
				standingOrderName: 'Name',
				amount: 'Amount',
				periodicity: 'Periodicity',
				recipient: 'Recipient',
			},
			cardsSetLimit: {
				newLimitAmount: 'New limit',
				oldLimitAmount: 'Old limit',
				bankCardUuid: 'Card ID',
			},
			terminateAccount: {
				accountName: 'Account name',
				terminationOrigin: 'Termination origin',
				targetIban: 'Target account number',
				accountsAffectedByTermination: 'Other terminated accounts',
			},
			prepareSddPayload: {
				operation: 'Operation',
				operations: {
					cvc2View: 'CVC2 code view',
					panView: 'Card number view',
					pinSet: 'PIN code set',
					pinView: 'PIN code view',
				},
			},
			productSuperSavings: {
				subsectionAccountsTitle: {
					single: 'Account used for saving',
					multiple: 'Accounts used for saving',
				},
				savingsAccountName: 'Saving account name',
				savingsAccountUuid: 'Saving account ID',
				accountName: 'Account name',
				accountUuid: 'Account ID',
				roundUpAmount: 'Round up to the nearest',
				additionalAmount: 'Extra saving amount',
			},
			createProductTermDeposit: {
				reservedIban: 'Reserved account',
				deposit: 'Deposit amount',
				interestRate: 'Interest rate',
				durationInMonths: 'Duration in months',
				maturityDate: 'Maturity date',
				settlementAccountIban: 'Settlement account',
			},
		},
	},
	documents: {
		category: 'Category',
		fileName: 'File name',
		tags: 'Tags',
		description: 'Description',
		bankTypes: {
			addendumToFrameworkAgreement: 'Addendum to framework agreement',
			availableAccountConfirmation: 'Available account confirmation',
			availableBalanceConfirmation: 'Available balance confirmation',
			commitmentsConfirmation: 'Commitments confirmation',
			complaintsProcedure: 'Complaints procedure',
			directDebitConfirmation: 'Direct debit confirmation',
			example: 'Example',
			foreignPoliceConfirmation: 'Foreign plice confirmation',
			frameworkAgreement: 'Framework agreement',
			priceList: 'Price list',
			sipoConfirmation: 'SIPO confirmation',
			standingOrderConfirmation: 'Standing order confirmation',
			statementOfFees: 'Statement of fees',
			statementOfPayments: 'Statement of payments',
			termsAndConditions: 'Terms and conditions',
			termsAndConditionsCards: 'Terms and conditions for cards',
			termsAndConditionsCardsGoogleAndApplePay:
				'Terms and conditions for Google/Apply pay',
			termsAndConditionsPayments: 'Terms and conditions for payments',
			transactionConfirmation: 'Transaction confirmation',
			unsupported: 'Unsupported',
			mobilityRequest: 'Mobility request',
			interestRates: 'Interest rates',
			personalDataProcessingInformation: 'Personal data processing information',
			permanentlyAccessibleInformation: 'Permanently accessible information',
			termsOfUseMobileApplication: 'Terms of use mobile application',
			groupMarketingConsent: 'Group marketing consent',
			informationOnTheDepositInsuranceSystem:
				'Information on the deposit insurance system',
			mobilityChangeAccount: 'Mobility - change account',
			mobilityPayers: 'Mobility - payer',
			accountTerminationBankImmediateCab:
				'Account termination - immediate - CAB',
			accountTerminationBankImmediateOther:
				'Account termination - immediate - other',
			accountTerminationBankWithNoticePeriod:
				'Account termination - notice period',
			accountTerminationLimitationOfAutonomy:
				'Account termination - limitation of autonomy',
			cardPaymentConfirmation: 'Card payment confirmation',
			domesticPaymentConfirmation: 'Domestic payment confirmation',
			sepaPaymentConfirmation: 'SEPA payment confirmation',
			foreignPaymentConfirmation: 'Foreign payment confirmation',
			exampleTemplate: 'Example template',
			loanInterestRateGuaranteeUnsecured: 'Interest rate guarantee - unsecured',
			loanInterestRateGuaranteeSecured: 'Interest rate guarantee - secured',
			loanIzuUnsecured: 'Loan IŽU - unsecured',
			loanScheduleUnsecured: 'Loan schedule - unsecured',
			loanScheduleSecured: 'Loan schedule - secured',
			loanEuropeanStandardisedInformationSheetSecured:
				'European standardised information sheet - secured',
			loanAccessionToDebtAgreementSecured:
				'Accession to debt agreement - secured',
			loanAccessionToDebtAgreementUnsecured:
				'Accession to debt agreement - unsecured',
			loanApplicationUnsecured: 'Loan application - unsecured',
			loanApplicationSecured: 'Loan application - secured',
			loanCalculationWhenWithdrawingFromALoan:
				'Calculation when withdrawing from a loan',
			loanConfirmationOfCancellationOfApplication:
				'Confirmation of cancellation of application',
			loanConfirmationOfConsumerLoanRepayment:
				'Confirmation of consumer loan repayment',
			loanConfirmationOfConsumerLoanRepaymentAndTerminationOfTheAccessionObligation:
				'Confirmation of consumer loan repayment and termination of accession obligation',
			loanContractSecured: 'Loan contract - secured',
			loanContractUnsecured: 'Loan contract - unsecured',
			loanEuropeanStandardisedInformationSheetUnsecured:
				'European standardised information sheet - unsecured',
			loanExtraordinaryFullInstalmentCalculation:
				'Extraordinary full instalment calculation',
			loanNoticeOfEarlyRepaymentOfConsumerLoanUnsecured:
				'Notice of early repayment of consumer loan - unsecured',
			loanPreContractualExplanation: 'Pre-contractual explanation',
			loanStandardEuropeanConsumerCreditInformationUnsecured:
				'Standard European consumer credit information - unsecured',
			loanConfirmationOfPaidInterestSecured:
				'Confirmation of paid interest - secured',
			loanAgreement: 'Loan agreement',
			loanAccessorAgreement: 'Loan access agreement',
			loanRefinancingTermination: 'Loan refinancing termination',
			loanEarlyRepaymentNotice: 'Early repayment notice',
			frameworkAgreementAmendment: 'Amendment to framework agreement',
			priceListAmendment: 'Amendment to price list',
			termsAndConditionsAmendment: 'Amendment to terms and conditions',
			termsAndConditionsAdjustment: 'Adjustment of terms and conditions',
			termsAndConditionsPaymentsAmendment:
				'Amendment to terms and conditions for payments',
			termsAndConditionsPaymentsAdjustment:
				'Adjustment of terms and conditions for payments',
			termsAndConditionsCardsAmendment:
				'Amendment to terms and conditions for cards',
			termsAndConditionsCardsAdjustment:
				'Adjustment of terms and conditions for cards',
			other: 'Other',
		},
		clientCategories: {
			accountCancellation: 'Account cancellation',
			complaints: 'Complaints',
			confirmationStatements: 'Confirmation statements',
			contractualDocumentation: 'Contractual documentation',
			drawdownDocumentation: 'Drawdown documentation',
			exceptionDocumentation: 'Exception documentation',
			incomeDocumentation: 'Income documentation',
			inheritancePowerOfAttorney: 'Inheritance power of attorney',
			other: 'Other',
			outgoingMobility: 'Outgoing mobility',
			incomingMobility: 'Incoming mobility',
			postDrawdownDocumentation: 'Post drawdown documentation',
			preContractualDocumentation: 'Pre contractual documentation',
			propertyDocumentation: 'Property documentation',
		},
		documentsTableComponent: {
			name: 'Title',
			invalidate: 'Invalidate',
			expireAt: 'Expire date',
			editDialogTitle: 'Change information about document',
			invalidated: 'Invalidated',
			invalidationDialog: {
				title: 'Document invalidation',
				description:
					'Do you wish to continue in document invalidation? This action is irreversible.',
			},
			snackbar: {
				saveSuccess: 'Document was successfully updated',
				saveError: 'Failed to update document',
			},
		},
		clientDocumentUploadComponent: {
			dialogTitle: 'Upload documents',
			cancelDialog: {
				title: 'Cancel documents upload?',
				description: 'You will lose all changes.',
			},
			showInMobileApp: 'Show in mobile application',
		},
	},
	appDocumentsUploadDialogComponent: {
		upload: 'Upload',
		stateDescriptions: {
			uploading: 'Uploading...',
			uploaded: 'Uploaded',
			error: 'Error with document',
		},
	},
	appCopyableTextComponent: {
		textWasCopied: 'The text was copied',
		textCopyFailed: 'Failed to copy the text',
	},
	appFilterButtonComponent: {
		inactive: 'Filters',
		active: 'Filters active',
	},
	appFilterSelectButtonComponent: {
		text: 'Filter:',
	},
	verifyIdentityPage: {
		start: {
			title: 'Client identity verification',
			description:
				"Generates a request to the client's mobile application. To continue the remote service, the client must confirm the request.",
			startVerification: 'Start verification',
		},
		waiting: {
			title: 'Waiting for identity confirmation',
			description:
				"The verification request was sent to the client's mobile application",
		},
		success: {
			title: 'Identity confirmed',
			description: 'It is possible to continue servicing the client',
		},
		fail: {
			title: "Identity wasn't confirmed",
			description:
				'In order to continue with the service, it is necessary to verify the client in another way',
		},
		alreadyInProgress: {
			title: 'Cannot continue',
			description:
				'The identity verification request cannot be created because there is another request that needs to be completed',
			showRequest: 'Show request',
		},
		noActiveDevice: {
			title: 'Cannot continue',
			description:
				'The identity verification request cannot be created because the client does not have any active device to perform the verification',
		},
	},
	pfsOnboardingApplications: {
		pfsOnboardingApplicationDialog: {
			title: 'Application detail',
		},
		pfsOnboardingApplicationDetail: {
			selectedAccountNumber: 'Reserved account number',
			selectedBundle: 'Bundle',
			selectedProductCode: 'Product code',
			applicationExpiration: 'Application expiration',
			applicationUuid: 'Application ID',
			fipProcessId: 'Application ID in FIP',
			virtualCardRequested: 'Virtual card requested',
			standardCardRequested: 'Plastic card requested',
			sectionTitles: {
				selectedProducts: 'Selected products',
				bundle: 'Bundle',
				account: 'Account',
			},
		},
		pfsOnboardingApplicationProducts: {
			bundleCouple: 'Bundle for couple',
			bundleFamily: 'Bundle for family',
			bundleSingle: 'Bundle for single',
			childCurrentAccount: 'Child current account',
			personalCurrentAccount: 'Current account',
			personalSavingAccount: 'Saving account',
			sharedCurrentAccount: 'Shared current account',
			sharedSavingAccount: 'Shared saving account',
		},
		pfsOnboardingApplicationStatuses: {
			accountCreated: 'Account created',
			bankProcessing: 'Bank processing',
			bankProcessingPaused: 'Bank processing paused',
			cancelledByAdvisor: 'Cancelled by advisor',
			cancelledByBank: 'Cancelled by bank',
			cancelledByClient: 'Cancelled by client',
			completed: 'Completed',
			expired: 'Expired',
			expiredNotSigned: 'Expired - not signed',
			inProgress: 'In progress',
			productActivationFailed: 'Product activation failed',
			signatureWaitingForClient: 'Waiting for client signature',
			signed: 'Signed',
		},
		pfsOnboardingApplicationBundleProductCodes: {
			bundleCouple: 'BUNDLE_COUPLE',
			bundleFamily: 'BUNDLE_FAMILY',
			bundleIndividual: 'BUNDLE_SINGLE',
		},
	},
	advisorRightsPage: {
		title: 'Advisor: Rights Settings',
		infoText: 'Number of rights found: {rightsAmount}',
		noData: 'No rights found',
		failedToLoad: 'Failed to load rights settings',
		columns: {
			right: 'Right',
		},
		roles: {
			administrator: 'Administrator',
			guardian: 'Guardian',
			shared: 'Shared',
			service: 'Service',
			structure: 'Structure',
			bubble: 'Bubble',
		},
		actions: {
			edit: 'Edit',
		},
		editDialog: {
			title: 'Change right settings',
			subtitle: 'Settings for individual advisor roles',
			rightLabel: 'Right',
			setRight: 'Set',
		},
		snackbars: {
			saveSuccess: 'Rights settings change completed',
			saveError: 'Failed to complete rights settings change',
		},
	},
	advisorClientAccessPage: {
		title: 'Advisor: Client Access',
		unknownAdvisor: 'Unknown advisor',
		columns: {
			client: 'Client',
			address: 'Address',
			phone: 'Phone',
			email: 'E-mail',
		},
		advisorIdLabel: 'Advisor ID',
		rolesLabel: 'Roles',
		rolesPlaceholder: 'Select roles...',
		rolesSelectedPlaceholder: {
			single: '{count} role selected',
			few: '{count} roles selected',
			plural: '{count} roles selected',
		},
		advisorSectionTitle: 'Advisor',
		clientsCount: 'Client count: {count}',
		searchResultSection: {
			title: 'Search results',
			noClientsText: 'No clients',
		},
		advisorClientAccessDetail: {
			labels: {
				roles: 'Roles',
				definitionUpdated: 'Definition updated',
				birthNumber: 'Birth number',
				birthDate: 'Date of birth',
				idCardNumber: 'ID card number',
				pidClient: 'Client PID',
			},
			advisorRolesSectionTitle: 'Advisor Role Settings',
			buttons: {
				updateClientRights: 'Update Client Rights',
			},
			snackbars: {
				updateSuccess: 'Update completed. Try searching for the client again.',
				updateError:
					'Update failed. Check the provided data and try again if necessary.',
			},
		},
	},
	onboardingApplicationsPage: {
		title: 'PFS Onboarding',
		noData: 'No applications',
		numberOfApplicationsFound:
			'Number of applications found: {applicationsCount}',
		labels: {
			product: 'Application type',
			status: 'Application status',
			mainClientName: 'Main applicant',
			createdAt: 'Creation date',
			expiration: 'Expiration date',
		},
		values: {
			products: {
				personalCurrentAccount: 'Current account',
			},
			statuses: {
				all: 'All',
			},
		},
		detailPage: {
			title: 'Application Detail',
			sections: {
				package: 'Package',
				account: 'Account',
				mainApplicant: 'Main Applicant',
				mobility: 'Mobility',
				advisorData: 'Advisors Data',
				additionalData: 'Additional Data',
				processingErrors: 'Processing Errors',
			},
			labels: {
				advisorDiscount: 'Advisor Discount',
				reservedAccounts: 'Reserved Accounts',
				noMobilityRequested: 'Mobility was not requested',
				noProcessingErrors: 'No processing errors',
				mobilitySelectedAccount: 'Selected Account',
				clientPidUuid: 'Main Applicant PID',
				accountNumberInAnotherBank: 'Account Number in Another Bank',
				mobilityDate: 'Mobility Date',
				mobilityStatus: 'Mobility Status',
				mobilityScopes: 'Mobility Scopes',
				applicationCreatedBy: 'Application Created By',
				applicationSignedBy: 'Application Signed By',
				statedOnPaper: 'Will Be Stated on Contract',
				applicationUuid: 'Application UUID',
				processId: 'Process ID',
				signatureExpirationDate: 'Signature Expiration Date',
				selectedSignatureMethod: 'Signature Method',
				signOperationUuid: 'Signature Operation ID',
				processingErrors: 'Processing Errors',
				errorDate: 'Error Date',
				errorDescription: 'Error Description',
			},
			format: {
				fipUserWithName: '{name} (ID {fipId})',
				fipUserIdOnly: 'ID {fipId}',
			},
			actions: {
				restartProcessing: 'Restart Processing',
			},
			snackbar: {
				restartProcessingSuccess: 'Processing restarted successfully',
				restartProcessingError: 'Failed to restart processing',
			},
		},
	},
	activateApplicationsPage: {
		title: 'Activate application',
		noActivations: 'No activations',
		failedToLoadActivations: 'Failed to load activations',
		infoText: 'Number of found activations: {activationsAmount}',
	},
	identityVerificationPage: {
		title: 'Identity verification',
		noVerifications: 'No verifications',
		failedToLoadVerifications: 'Failed to load verifications',
		infoText: 'Number of found verifications: {verificationsAmount}',
		columns: {
			verification: 'Verification',
			state: 'State',
			creationDate: 'Creation date',
			client: 'Client',
		},
		filter: {
			state: 'State',
			completionDate: 'Last update date',
			from: 'From',
			to: 'To',
			clientPidUuid: 'Client PID',
			allStates: 'All',
		},
		detail: {
			documentSection: 'Document',
			idCardType: 'Document type',
			idCardNumber: 'Document number',
			idCardCountry: 'Country of issue',
			samplesSection: 'Verified samples',
			sampleType: 'Sample type',
			sampleId: 'Sample ID',
			deviceSection: 'Device',
			deviceName: 'Device name',
			deviceBrand: 'Device brand',
			deviceId: 'Unique device ID',
			deviceIndustrialDesign: 'Device industrial design',
			progressSection: 'Progress',
			additionalInfoSection: 'Additional information',
			verificationId: 'Verification ID',
			lastUpdate: 'Last update',
			ocrInvestigationId: 'OCR Investigation ID',
			ocrProfile: 'OCR profile',
			blockageRequired: 'Blockage required',
		},
		verificationStates: {
			verificationCreated: 'Created',
			signOperationPrepared: 'Waiting for signature',
			idScanning: 'Scanning in progress',
			idScanned: 'Scanned',
			verificationSuccessful: 'Done',
			identityVerificationFailed: 'Identity verification failed',
			manualIdentityVerificationRequired: 'Manual verification',
			verificationFailed: 'Rejected',
		},
		createVerificationDialog: {
			title: 'New identity verification',
			description:
				"Creates a request in the client's mobile application to provide a photo of a document or face.",
			blockCheckboxLabel: 'Block client during the verification period',
			createButton: 'Create',
			snackbar: {
				success: 'Request created',
				successActionButton: 'Show request',
				error: 'Failed to create request',
			},
		},
		approveDialog: {
			title: 'Approve verification?',
			description:
				'Verification on device {deviceName} will be successfully completed.',
			descriptionWithoutDevice: 'Verification will be successfully completed.',
			approveButton: 'Approve',
			snackbar: {
				success: 'Verification approved',
				error: 'Failed to approve verification',
			},
		},
		rejectDialog: {
			title: 'Reject verification?',
			description: 'Verification on device {deviceName} will be rejected.',
			descriptionWithoutDevice: 'Verification will be rejected.',
			rejectButton: 'Reject',
			snackbar: {
				success: 'Verification rejected',
				error: 'Failed to reject verification',
			},
		},
		actions: {
			sectionTitle: 'Identity verification',
			newIdentityVerification: 'New identity verification',
		},
	},
	applicationsActivations: {
		columns: {
			request: 'Activation',
			processType: 'Type',
		},
		additionalData: 'Additional data',
		activationProgress: 'Activation progress',
		bankIdData: 'Bank iD',
		processId: 'Process ID',
		fingerprint: 'Fingerprint',
		activationId: 'Activation ID',
		deviceBrand: 'Device brand',
		deviceId: 'Device ID',
		deviceIndustrialDesign: 'Device industrial design',
		noActivationRequestDetail: 'No activation detail',
		phoneNumber: 'Phone number',
		bankIdElements: {
			bankIdAuthorizationErrorType: 'Authorization error',
			bankIdAuthorizationErrorDescription: 'Authorization error description',
			userImportTraceId: 'Trace ID verification',
			userVerificationProcess: 'ID number of the verifying bank',
			userSub: 'Client identifier',
			bankTitle: 'Bank used for login',
			consultantId: 'Referral code / Advisor ID',
		},
		identityVerificationElements: {
			identityDocumentProcessingErrorType: 'Document processing error',
			deviceBrand: 'Device brand',
			deviceId: 'Device ID',
		},
		filters: {
			titles: {
				lastUpdateDate: 'Last update date',
				activationType: 'Activation type',
				requestStatus: 'Activation status',
				phoneNumber: 'Phone number',
				deviceName: 'Device name',
				activationName: 'Activation name',
			},
			labels: {
				clientPidUuid: 'Client PID',
				onlyUncompletedActivations: 'Only uncompleted',
				manualIdentityVerificationAllowed: 'To manual verification',
				allActivationRequestStates: 'All',
				userSub: 'Client identifier in Bank iD process',
			},
			hints: {
				userSub: 'Can be used only for "Bank iD" activation types',
			},
		},
		processStates: {
			processCreated: 'Process created',
			activationInitialized: 'Activation initialized',
			activationCreated: 'Activation created',
			activationCommitted: 'Activation committed',
			documentsToSignPrepared: 'Documents prepared for signature',
			signedDocumentsProcessingFailed: 'Signature processing failed',
			signedDocumentsProcessingCompleted: 'Signature processing completed',
			processCompleted: 'Process completed',
			processCancelled: 'Process cancelled',
			userImported: 'User imported',
			bankIdAuthorizationFailed: 'Bank ID authorization failed',
			userUpsertInitiated: 'User upsert initiated',
			userUpsertCompleted: 'User upsert completed',
			userUpsertFailed: 'User upsert failed',
			identityVerificationInitiated: 'Identity verification initiated',
			identityVerificationCompleted: 'Identity verification completed',
			identityVerificationFailed: 'Identity verification failed',
			identityVerified: 'Identity verified',
			manualIdentityVerificationRequired: 'Manual identity verification',
			deviceApproved: 'Device approved',
			userPartiallyImported: 'User partially imported',
			userVerified: 'User verified',
			unknown: 'Unknown',
		},
		aggregatedProcessStates: {
			cancelled: 'Cancelled',
			completed: 'Completed',
			expired: 'Expired',
			initiated: 'Initiated',
			processingFailed: 'Processing failed',
			unknown: 'Unknown',
			identityVerified: 'Identity verified',
			identityVerificationFailed: 'Identity verification failed',
			manualIdentityVerificationRequired: 'To manual processing',
			userUpsertInitiated: 'User upsert initiated',
			userUpsertCompleted: 'User upsert completed',
			userUpsertFailed: 'User upsert failed',
			deviceApproved: 'Device approved',
		},
		processTypes: {
			bankIdOnboarding: 'Bank iD onboarding',
			bankIdActivation: 'Bank iD activation',
			idScanVerification: 'Identity verification',
			consultantQrActivation: 'Advisor QR code',
			deviceQrActivation: 'QR code from another device',
			childOnboarding: 'Child onboarding',

			// Old process types for backward compatibility
			bankId: 'Bank iD',
			identityVerification: 'Identity verification',
			qr: 'QR code',
		},
		approveActivationDialog: {
			title: 'Approve activation',
			description: 'Do you really want to approve this activation?',
			descriptionWithDeviceName:
				'Do you really want to approve activation on device "{deviceName}"?',
			snackbar: {
				success: 'Activation was approved',
				error: 'Activation could not be approved',
			},
			approveButton: 'Approve',
		},
		rejectActivationDialog: {
			title: 'Reject activation',
			description: 'Do you really want to reject this activation?',
			descriptionWithDeviceName:
				'Do you really want to reject activation on device "{deviceName}"?',
			snackbar: {
				success: 'Activation was rejected',
				error: 'Activation could not be rejected',
			},
			rejectButton: 'Reject',
		},
	},
	mobilityRequests: {
		noMobilityRequests: 'No mobility applications',
		failedToLoadMobilityRequests: 'Failed to load mobility applications',
		mobility: 'Mobility',
		mobilityContent: '{direction} from {accountNumber}',
		mobilityDate: 'Mobility date',
		accountCurrency: 'Account currency',
		accountInOtherBank: 'Account in other bank',
		accountInPartners: 'Account in Partners',
		additionalInformation: 'Additional information',
		applicationId: 'Application ID',
		accountUuid: 'Account ID',
		idDaktelaTicket: "Daktela's ticket ID",
		mobilitySource: 'Mobility source',
		mobilitySources: {
			clientMa: 'Client in mobile app',
			onboardingFip: 'Onboarding with advisor',
			userBo: 'Dashboard / BOA',
		},
		mobilityRequestsDialog: {
			title: 'Mobility requests',
			newMobilityRequestButton: 'New request',
			cancellationDate: 'Cancellation date',
			closingDate: 'Closing date',
			createdByUser: 'Created by',
			cancelledByUser: 'Cancelled by',
			closedByUser: 'Closed by',
			buttons: {
				cancel: 'Cancel',
				close: 'Close',
			},
			statusChangeDialog: {
				title: 'Change status',
				statusLabel: 'Status',
				statusHelper: 'New status',
				internalStatusLabel: 'Internal status clarification',
				internalStatusNoteLabel: 'Note for internal status clarification',
				amosIdLabel: 'AMOS number',
				notificationCheckboxLabel: 'Send status change notification to client',
				saveButton: 'Save',
				closeButton: 'Close',
				statusOptions: {
					keepInProgress: 'Keep "In progress"',
					close: 'Close',
					cancel: 'Cancel',
				},
				errorMessage:
					'Unexpected error. The mobility is in a state that does not allow changes. Please contact helpdesk.',
				snackBar: {
					success: 'Mobility updated',
					error: 'Failed to update mobility',
				},
				bulkSnackBar: {
					success: 'Mobilities updated',
					error: 'Failed to update mobilities',
					showList: 'Show list',
				},
				bulkWarning:
					'You are performing a bulk action. Changes will be saved to all requests and overwrite current values.',
				bulkErrorDialog: {
					title: 'Errors updating mobilities',
					errorCodes: {
						errorMobilityInWrongStatus: 'Mobility in wrong status',
						errorUpdateNotSaved: 'Update not saved',
						errorGetMobility: 'Failed to get mobility',
						errorUnexpected: 'Unexpected error',
						errorUnknown: 'Unknown error',
					},
				},
			},
			changeStatusButton: 'Change status',
			bulkActions: {
				button: 'Bulk action',
				downloadRequests: 'Download request documents',
				changeStatus: 'Change status',
				downloadConfirmation: {
					title: 'Download request documents?',
					description:
						'The "Payment account change request" documents will be downloaded in bulk for the selected mobilities.',
					downloadButton: 'Download',
					snackBar: {
						success: 'Requests downloaded',
						error: 'Failed to download requests',
					},
				},
			},
			internalStatuses: {
				pendingSendToOriginalBank: 'Pending send to original bank',
				sentToOriginalBank: 'Sent to original bank',
				fourEyesCheck: 'Checking payment orders setup',
				processed: 'Processed',
				rejectedByOriginalBank: 'Rejected by original bank',
				rejectedByPartnersBank: 'Rejected by Partners bank',
				cancelledByClient: 'Cancelled by client',
			},
			internalStatusBanner: {
				statusLine: 'Processing status clarification: {status}',
				noteLine: 'Note: {note}',
			},
		},
		uploadPaymentOrders: {
			button: 'Upload payment orders',
		},
		documentActions: {
			processPayment: {
				button: 'Start document extraction',
				successDialog: {
					title: 'Document was processed',
					description:
						'Payment orders have been submitted to the DCS application and are awaiting approval.',
					buttons: {
						openAccountInDCS: 'Open account in DCS',
					},
				},
				snackBar: {
					error: 'Failed to start document extraction',
				},
			},
			changeStatus: {
				button: 'Document processed manually',
				dialogTitle: 'Processing method',
				description: 'Select the processing method:',
				confirmButton: 'Save',
				options: {
					processedManually: 'Orders entered manually in DCS',
					canceled: 'Processing cancelled',
				},
				snackBar: {
					success: 'Document status has been changed',
					error: 'Failed to change document status',
				},
			},
		},
		documentStatus: {
			notProcessed: 'Awaiting processing',
			processingFailed: 'Cannot be processed',
			processed: 'Processed {date}',
			processingNotPossible: 'Processing failed. Please try again',
			alreadyProcessed: 'Duplicate of another document',
			invalidClientAccount: 'Cannot be processed, invalid account',
			processedManually: 'Processed manually {date}',
			canceled: 'Processing cancelled {date}',
			unknown: 'Unknown status',
		},
		filter: {
			hasDocumentsPendingProcessing: 'Pending processing',
		},
	},
	mobilityApplicationsPage: {
		title: 'Mobility applications',
		infoText: 'Number of found applications: {mobilityRequestAmount}',
		applicationStatus: 'Application status',
		mobilityDirection: 'Mobility direction',
		direction: 'Direction',
		scope: 'Scope',
		documents: 'Documents',
		pidUuid: 'Client PID',
		directions: {
			incoming: 'Incoming',
			outgoing: 'Outgoing',
		},
		statuses: {
			cancelled: 'Cancelled',
			closed: 'Finished',
			draft: 'Draft',
			expired: 'Expired',
			inProgress: 'In progress',
			signatureBankProcessing: 'Finishing application',
			signaturePreparation: 'Application preparation',
			signaturePreparationFailed: 'Application preparation failed',
			waitingForOnboardingApplication: 'Waiting for onboarding completion',
			waitingForSign: 'Waiting for sign',
			signatureBankProcessingFailed: 'Finishing application failed',
		},
		mobilityId: 'mobility ID',
		filters: {
			source: 'Source',
			sources: {
				userBo: 'BOA',
				clientMa: 'Client in MA',
				onboardingFip: 'FIP onboarding',
			},
			internalStatus: 'Internal processing status',
			documents: 'Documents',
		},
	},
	internalControlsPage: {
		title: 'Internal controls',
		noDocuments: 'No documents',
		noInternalControls: 'No internal controls',
		noInternalControlsWaitingForSign: 'No internal controls waiting for sign',
		failedToLoadInternalControls: 'Failed to load internal controls',
		infoText: 'Number of found controls: {internalControlsAmount}',
		request: 'Request',
		settlementAccount: 'Settlement account',
		account: 'Account',
		amount: 'Amount',
		approvalHistory: 'Approval history',
		additionalInfo: 'Additional info',
		terminationOrigin: 'Termination origin',
		clientPidUuid: 'Client PidUuid',
		expiration: 'Expiration date',
		terminatedAccountID: 'Terminated account ID',
		operationId: 'Operation ID',
		firstSign: 'First sign',
		secondSign: 'Second sign',
		all: 'All',
		approve: 'Approve',
		decline: 'Decline',
		documents: 'Documents',
		operationStatuses: {
			cancelled: 'Cancelled',
			cancelling: 'Cancelling',
			done: 'Done',
			expired: 'Expired',
			expiring: 'Expiring',
			processing: 'Processing',
			refused: 'Refused',
			waitingForSign: 'Waiting for sign',
		},
		operations: {
			terminateProductProtectedAccount4Eyes: 'Terminate protected account',
			terminateProductCurrentAccountBasic4Eyes: 'Terminate current account',
			terminateProductCurrentAccountSecondary4Eyes:
				'Terminate secondary current account',
			terminateProductCurrentAccountChild4Eyes: 'Terminate child account',
			terminateProductCurrentAccountShared4Eyes: 'Terminate shared account',
			terminateProductSavingsAccountBasic4Eyes: 'Terminate savings account',
			terminateProductSavingsAccountChild4Eyes:
				'Terminate child savings account',
			terminateProductSavingsAccountShared4Eyes:
				'Terminate shared savings account',
			terminateProductTermDeposit4Eyes: 'Terminate term deposit',
		},
		approveSnackBar: {
			success: 'Account was terminated',
			error: 'There was an error while terminating account',
		},
		declineSnackBar: {
			success: 'Account termination was declined',
			error: 'There was an error while declining account termination',
		},
	},
	nebankaProductsPage: {
		title: 'Financial data',
		subTitle: 'Financial products',
		navbar: {
			insurance: 'Insurance',
			bank: 'Bank',
			loan: 'Loan',
			investment: 'Investment',
		},
		header: {
			advisor: 'Advisor as seen in application',
			email: 'Email',
			phone: 'Phone',
			address: 'Advisor residence',
			contractSharing: 'Contract sharing',
			details: 'Extended client details',
		},
		relationships: {
			child: 'Child',
			other: 'Other',
			owner: 'Owner',
			partner: 'Partner',
		},
		notifications: {
			notificationLog: 'Notification log',
			kindColumn: 'Type',
			dateColumn: 'Date',
			statusColumn: 'State',
			status: {
				ok: 'Updated',
				nok: 'Failed',
			},
		},
		contractSharingDialog: {
			title: 'Are you sure you want to cancel sharing for {name}?',
			description:
				"If you cancel data sharing on the analysis, clients will not be able to see each other's contracts.",
			ticketPlaceholder: 'Daktela ticket number',
			confirmButton: 'Yes, cancel sharing',
			snackbarSuccess: 'Data sharing canceled succesfully.',
		},
		update: 'Update data',
		investments: {
			overview: 'Overview',
			contracts: 'Contracts',
			value: 'Value',
			contractNumber: 'Číslo smlouvy',
			absolute: 'Absolute',
			relative: 'Relative',
			owner: 'Majitel',
			withoutPartners: 'Investments outside of Partners',
			withoutPartnersInfo:
				'Investments arranged outside Partners are not included in the totals or in the appreciation.',
			summary: {
				currentValueSum: 'Total value of investments',
				asOfDate: 'As of date',
				appreciation: 'Appreciation',
				purchases: 'Purchases',
				sales: 'Sales',
				uninvestedAmount: 'Uninvested amount',
				uninvestedAmountInfo:
					'This amount is not included in the total value of investments',
				pendingSales: 'Pending Sales',
				pendingSalesInfo:
					'This amount is currently included in the total investment value and is not included in sales.',
				expectedRealizationDate: 'Expected realization date',
				contractNumber: 'Contract number',
				pendingSalesModalInfo:
					'The redemption process from Partners Reserve fund may take 2 to 9 days. Partners IS executes trades every Thursday, orders must be submitted by Tuesday.',
			},
			ocpWallets: {
				title: 'Investment accounts',
				titleDip: 'DIP - Investment accounts',
				currentBalance: 'Current balance',
				toggleDetail: 'Investment account details',
				tabs: {
					transactionHistory: 'Transaction history',
					orders: 'Orders',
					pendingOrders: 'Unexecuted orders ({count})',
					canceledOrders: 'Orders cancelled by client ({count})',
				},
				interest: 'Interest',
				transactionHistory: {
					blockedBalanceAmount: 'Blocked Amount',
					pendingPaymentHintTitle: 'Fee Due',
					pendingPaymentHintDescription:
						'Payment will be made automatically after the wallet is topped up',
					otherTransactionType: 'Monetary transaction',
					incomingPayment: 'Incoming payment',
					outgoingPayment: 'Outgoing payment',
					transactionType: {
						bCash: 'Deposit to investment account',
						bCashInterest: 'Interest',
						bCashSettl: 'Investment settlement',
						bCashTax: 'Interest Tax',
						bDepositCash: 'Deposit to investment account',
						caBidsCashInc: 'Redemption offer',
						caCouponCashInc: 'Coupon yield',
						caDecrCashDec: 'Dividend nominal value reduction',
						caDivCashInc: 'Dividend',
						caGainCashInc: 'Capital gain dividend',
						caInducementsCashInc: 'Incentive payout',
						caLiquidationCashInc: 'Liquidation',
						caOtherCashDec: 'Funds write-off',
						caOtherCashInc: 'Funds credit',
						caReinvCashInc: 'Reinvestment dividend',
						caShprtCashInc: 'Share premium dividend',
						caTendDtchCashInc: 'Takeover offer',
						feeAdmin: 'Administrative fee',
						sfkf: 'Ongoing fee',
						sCash: 'Withdrawal from investment account',
						sCashInterest: 'Interest',
						sCashSettl: 'Payment from order - {isin}',
						sCashTax: 'Interest Tax',
						sDepositCash: 'Withdrawal from investment account',
					},
				},
				orders: {
					paymentsInNextDays: 'Payments in the next {daysAmount} {{days}}',
					includingUnrealizedPayments:
						'Including unrealized payments in the amount of {amountFormatted}',
					ordersSectionTitle: 'Orders',
					oneTimeOrder: 'One-time order',
					regularOrderWithoutFrequency: 'Regular order',
					regularOrder: 'Regular order ({frequency})',
					instrumentsValueTitle: 'Value of instruments in the order',
					orderParameters: 'Order parameters',
					paymentDate: 'Payment date',
					value: 'Value',
					valueSectionTitle: 'Value of instruments in the order',
					parametersSectionTitle: 'Order parameters',
					nextPaymentDate: 'Next payment date',
					investmentHorizon: 'Investment horizon',
					paymentFrequency: 'Payment frequency',
					fundsList: 'List of funds',
					buttons: {
						pause: 'Pause order',
						changePayment: 'Change payment amount',
					},
					insufficientBalanceCaption:
						'Insufficient funds in the investment account',
					insufficientBalanceHint:
						'To execute the next instruction, there are not enough available funds in the investment account. The account needs to be topped up before the execution date.',
					pendingOrdersCaption: 'We record unrealized transactions',
					pendingOrdersHint:
						'Some payments could not be processed because there were insufficient funds in the investment account. After topping up the investment account, the pending payment will be processed first.',
				},
				pendingOrders: {
					hint: 'To settle outstanding instructions, you need to top up your account by {amount}.',
				},
				canceledOrders: {
					hint: 'Orders cancelled by client can no longer be paid.',
				},
			},
		},
	},
	accountSearchResultPage: {
		pageTitle: 'Search results',
		numberOfAccounts: 'Number of accounts found: {numberOfAccounts}',
		labels: {
			accountUuid: 'Account ID: ',
			iban: 'IBAN: ',
			swiftIban: 'SWIFT IBAN: ',
		},
		dispositionsCount: 'Number of clients with dispositions: {count}',
		hiddenClientsWarning:
			'You do not have sufficient permissions to view other clients.',
		account: 'Account',
	},
	searchResultPage: {
		pageTitle: 'Search results',
		numberOfClients: 'Number of clients found: {numberOfClients}',
		noClientFound: 'No client found',
		checkYourEntryOrChangeIt: 'Check your entry or change it.',
		tableColumns: {
			lastName: 'Last name',
			firstName: 'First name',
			birthday: 'Birthday',
			address: 'Address',
			contact: 'Contact',
		},
		showAllResults: 'Show all results',
	},
	eventsPage: {
		title: 'Events',
		infoText: 'Number of found events: {eventsAmount}',
		noEvents: 'No events',
		failedToLoadEvents: 'Failed to load events',
		horizontalMenuItems: {
			all: 'All',
			clientAndIdentity: 'Client and identity',
			mobileApplication: 'Mobile application',
			boaInternal: 'BOA internal',
			communication: 'Communication',
		},
		filter: {
			eventType: 'Event type',
			client: 'Client',
			clientPid: 'Client PID',
			user: 'User',
			userPid: 'User PID',
			userId: 'UserId',
			eventDate: 'Event date',
		},
		eventType: {
			addressChange: 'Address change',
			appActivationCancelled: 'Application activation cancelled',
			appActivationCompleted: 'Application activation completed',
			appActivationFailed: 'Application activation failed',
			appActivationInitiated: 'Application activation initiated',
			appActivationManuallyVerified: 'Application activation manually verified',
			appActivationStepCompleted: 'Application activation - step completed',
			clientDataHistoryShown: 'Client data history view',
			clientDataShown: 'Client data view',
			clientLoggedIn: 'Login to mobile application',
			clientLoggedOut: 'Logout from mobile application',
			consentChanged: 'Consent change',
			deviceActivated: 'Device activation',
			deviceBlocked: 'Device blocking',
			deviceRemoved: 'Device removal',
			deviceTemporarilyBlocked: 'Device temporary blocking',
			deviceUnblocked: 'Device unblocking',
			emailChanged: 'Email change',
			phoneChanged: 'Phone change',
			pfsAdvisorRoleSetChanged: 'Advisor role setting change',
			userLoggedIn: 'Login to BOA',
			userLoggedOut: 'Logout from BOA',
			notificationSent: 'Notification sending',
			smsSent: 'SMS sending',
			emailSent: 'Email sending',
			call: 'Call',
			maChat: 'Chat in mobile application',
			clientAnalysisCreationInPfs: 'Client analysis creation in PFS',
			clientDocumentationSendToPfs: 'Sending client documentation to PFS',
			unknown: 'Unknown event',
			noEventTypeChosen: 'No event type chosen',
		},
		eventId: {
			newPaymentToConfirm: 'Payment order pending approval',
			posPurchase: 'POS purchase',
			posPurchaseChild: 'POS purchase (child)',
			cardFrozen: 'Payment not processed due to frozen card',
			cardTerminated: 'Payment not processed due to terminated card',
			cardTerminated2: 'Payment not processed due to terminated card',
			atmWithdrawalDisabled: 'ATM withdrawal not allowed',
			eCommerceDisabled: 'E-commerce payments not allowed',
			contactlessDisabled: 'Contactless payments not allowed',
			magneticStripeDisabled: 'Magnetic stripe payments not allowed',
			mccBlocked: 'MCC payments not allowed',
			dailyLimitExceeded: 'Insufficient daily limit for payments',
			nonSufficientFunds: 'Insufficient funds',
			pinCounterExceeded: 'Card PIN blocked',
			cardFrozenFraud: 'Card frozen due to fraud suspicion',
			disposableCardTerminated: 'Disposable card terminated',
			cardCreatedVirtual: 'Virtual card created',
			cardCreatedPlastic: 'Plastic card in production',
			cardWithdrawal: 'Card withdrawal',
			cardWithdrawalChild: 'Card withdrawal (child)',
			moneyTransfer: 'Money transfer to card',
			moneyTransferChild: 'Money transfer to card (child)',
			dccBlocked: 'Card payment rejected due to unfavorable exchange rate',
			dccBlocked2: 'Card payment rejected due to unfavorable exchange rate',
			acsAuthenticate: 'Payment authentication at the merchant',
			incomingPayment: 'Incoming payment',
			outgoingPayment: 'Outgoing payment',
			incomingPaymentGeneral: 'Incoming payment',
			outgoingPaymentGeneral: 'Outgoing payment',
			paymentOrderCancellation: 'Payment order cancelled',
			paymentToSign: 'Child payment pending approval',
			childPaymentApproved: 'Payment approved (child)',
			cardRefund: 'Card refund',
			cardRefund2: 'Card refund',
			maximalLimitExceeded: 'Payment not processed due to exceeding the limit',
			cardNotRenewed: 'Card nearing expiration',
			cardUnactivated: 'Card must be activated',
			cashbackForeignCurrency: 'Foreign cashback not allowed',
			childPaymentCanceled: 'Payment cancelled (child)',
			childCardFrozen: 'Card frozen (child)',
			childAtmWithdrawalDisabled: 'ATM withdrawal disabled (child)',
			childECommerceDisabled: 'E-commerce payments not allowed (child)',
			childContactlessDisabled: 'Contactless payments not allowed (child)',
			childMagneticStripeDisabled:
				'Magnetic stripe payments not allowed (child)',
			childMccBlocked: 'MCC payments not allowed (child)',
			childDailyLimitExceeded: 'Insufficient daily limit for payments (child)',
			childNonSufficientFunds: 'Insufficient funds on account (child)',
			childPinCounterExceeded: 'Card PIN blocked (child)',
			childCardFrozenFraud: 'Card frozen due to fraud suspicion (child)',
			childCardUnactivated: 'Card must be activated (child)',
			childCashbackForeignCurrency: 'Cashback not allowed (child)',
			childPaymentExpiredParent: "Payment expired on the parent's side (child)",
			childPaymentExpired: 'Payment rejected (child)',
			newIndividualPaymentToConfirm: 'Payment pending approval',
			newStandingOrderToConfirm: 'Standing order pending approval',
			cardRefundAccepted: 'Card refund accepted',
			appChatMessage: 'App chat message',
			unexpectedProblem: 'Unexpected problem',
			unexpectedProblem2: 'Unexpected problem',
			cardHoldExpired: 'Returning funds',
			cardHoldExpired2: 'Returning funds',
			cardTokenActivated: 'Card token added to wallet',
			cardTokenActivated2: 'Card token added to wallet',
			cardUnactivatedTransactionOverLimit: 'Payment not processed',
			cardUnactivatedTransactionOverLimitChild: 'Payment not processed (child)',
			smsCheckCard: 'One-time code for adding card to wallet',
			smsCheckCard2: 'One-time code for adding card to wallet',
			treasuryTransactionCreated: 'Treasury transaction pending approval',
			tokenNotActivated: 'Payment not processed due to inactive token',
			cardTerminatedUndelivered: 'Card not delivered and terminated',
			childStandingOrderToConfirm: 'Standing order pending approval',
			childStandingOrderConfirmed: 'Standing order approved (child)',
			childStandingOrderExpiredParent:
				"Standing order expired on the parent's side (child)",
			childStandingOrderExpiredChild: 'Standing order rejected (child)',
			childStandingOrderRequestRejectedChild: 'Standing order rejected (child)',
			childDirectDebitToConfirm: 'Direct debit pending approval',
			childDirectDebitConfirmed: 'Direct debit approved (child)',
			childDirectDebitExpiredParent:
				"Direct debit expired on the parent's side (child)",
			childDirectDebitExpiredChild: 'Direct debit expired (child)',
			childDirectDebitRequestRejectedChild: 'Direct debit rejected (child)',
			cardCreatedDisposable: 'Disposable card created',
			childPaymentRequestRefused: 'Payment rejected (child)',
			outgoingPaymentSEPA: 'Outgoing SEPA payment',
			incomingPaymentSEPA: 'Incoming SEPA payment',
			cardPinNotAssigned: 'Incorrect card PIN',
			childCardPinNotAssigned: 'Incorrect card PIN (child)',
			cardWrongPin: 'Incorrect card PIN',
			childCardWrongPin: 'Incorrect card PIN (child)',
			cardWrongCVV2: 'Incorrect card CVV code',
			cardWrongExpiryDate: 'Incorrect card expiry date',
			cardFraudInvestigation: 'Payment not processed due to unexpected error',
			outgoingPaymentForeign: 'Outgoing foreign payment',
			incomingPaymentForeign: 'Incoming foreign payment',
			rejectedPayment: 'Payment rejected due to suspected fraud',
			paymentApprove3rdpartyToConfirm: 'Payment pending approval',
			newStandingOrder3rdpartyToConfirm: 'Standing order pending approval',
			nonrealizedFirstGeneral:
				'Payment not processed due to insufficient account balance',
			nonrealizedFirstTmpStandingOrder:
				'Standing order not processed due to insufficient account balance',
			odooTransactionCreated:
				'Foreign currency (odoo) transaction pending approval',
			click2PayOn: 'Click2Pay enabled on the card',
			click2PayOff: 'Click2Pay disabled on the card',
			superSavingsToConfirm: 'Super savings pending approval',
			superSavingsCreated: 'Super savings created',
			superSavingsRejected: 'Super savings rejected',
			superSavingsChangeToConfirm: 'Super savings change pending approval',
			superSavingsChanged: 'Super savings changed',
			superSavingsNotChanged: 'Super savings change rejected',
			superSavingsToCancel: 'Super savings cancellation pending approval',
			superSavingsCanceled: 'Super savings cancelled',
			superSavingsNotCanceled: 'Cancellation of super savings rejected',
			teenSuperSavingsToConfirm: 'Super savings pending approval (child)',
			teenSuperSavingsCreated: 'Super savings created (child)',
			teenSuperSavingsRejected: 'Super savings rejected (child)',
			teenSuperSavingsChangeToConfirm:
				'Super savings change pending approval (child)',
			teenSuperSavingsApproved: 'Super savings change approved (child)',
			teenSuperSavingsNotApproved: 'Change of super savings rejected (child)',
			teenSuperSavingsToCancel:
				'Super savings cancellation pending approval (child)',
			teenSuperSavingsCanceled: 'Super savings cancelled (child)',
			teenSuperSavingsNotCanceled:
				'Cancellation of super savings rejected (child)',
			childSuperSavingsCreated: 'Super savings created (child)',
			childSuperSavingsChanged: 'Super savings changed (child)',
			childSuperSavingsCanceled: 'Super savings cancelled (child)',
			standingOrder3rdpartyToChanged: 'Standing order change pending approval',
			standingOrder3rdpartyToCancelled:
				'Standing order cancellation pending approval',
			childIncomingPaymentSEPAConversion: 'Incoming SEPA payment (child)',
			pidActivationOtp: 'One-time code for application activation',
			incomingPaymentConversion: 'Incoming payment with conversion',
			incomingPaymentSEPAConversion: 'Incoming SEPA payment with conversion',
			incomingPaymentForeignAgg: 'Incoming foreign payment',
			incomingPaymentForeignConversionAgg:
				'Incoming foreign payment with conversion',
			incomingPaymentInterestBasicAgg:
				'Interest credited (individual savings accounts)',
			incomingPaymentInterestSharedAgg:
				'Interest credited (shared savings accounts)',
			incomingPaymentInterestChildAgg:
				'Interest credited (child savings accounts)',
			outgoingPaymentSEPAConversion: 'Outgoing SEPA payment with conversion',
			outgoingPaymentForeignAgg: 'Outgoing foreign payment',
			outgoingPaymentForeignConversionAgg:
				'Outgoing foreign payment with conversion',
			outgoingPaymentStandingOrder: 'Standing order processed',
			outgoingPaymentStandingOrderAgg: 'Standing orders processed',
			outgoingPaymentAgg: 'Planned payments processed',
			outgoingPaymentDirectDebit: 'Direct debit processed',
			outgoingPaymentSIPO: 'SIPO payment processed',
			outgoingIncomingPayment: 'Transfer between own accounts processed',
			outgoingIncomingPaymentConversion:
				'Transfer between own accounts with conversion processed',
			outgoingPaymentSweep: 'Minimum balance adjustment',
			outgoingPaymentConversion: 'Outgoing payment with conversion',
			nonrealizedFirstStandingOrder: 'Standing order not processed',
			nonrealizedFirstStandingOrderAgg: 'Standing orders not processed',
			nonrealizedFirstDirectDebit: 'Direct debit not processed',
			nonrealizedFirstSIPO: 'SIPO not processed',
			nonrealizedFirstFuture: 'Payment not processed',
			nonrealizedFirstFutureAgg: 'Payments not processed',
			nonrealizedExpiredStandingOrder:
				'Standing order not processed due to insufficient funds',
			nonrealizedExpiredStandingOrderAgg:
				'Standing orders not processed due to insufficient funds',
			nonrealizedExpiredDirectDebit:
				'Direct debit not processed due to insufficient funds',
			nonrealizedExpiredSIPO: 'SIPO not processed due to insufficient funds',
			nonrealizedExpiredFuture:
				'Payment not processed due to insufficient funds',
			nonrealizedExpiredFutureAgg:
				'Payments not processed due to insufficient funds',
			nonrealizedFraud: 'Payment not processed due to suspected fraud',
			nonrealizedLowLimitDirectDebit:
				'Direct debit not processed due to insufficient limit',
			nonrealizedLowLimitSIPO: 'SIPO not processed due to insufficient limit',
			nonrealizedSweep:
				'Minimum balance adjustment not processed due to insufficient funds',
			nonrealizedFirstInstalment:
				'Instalment not processed due to insufficient funds',
			productPackageFee: 'Package fee',
			childIncomingPaymentGeneral: 'Incoming payment (child)',
			childOutgoingPaymentGeneral: 'Outgoing payment (child)',
			childIncomingPaymentInterestChildAgg: 'Interest credited (child)',
			childOutgoingPaymentStandingOrder: 'Standing order processed (child)',
			childOutgoingPaymentStandingOrderAgg: 'Standing orders processed (child)',
			childOutgoingPaymentAgg: 'Planned payments processed (child)',
			childOutgoingPaymentDirectDebit: 'Direct debit processed (child)',
			childOutgoingIncomingPayment:
				'Transfer between own accounts processed (child)',
			childOutgoingIncomingPaymentConversion:
				'Transfer between own accounts with conversion processed (child)',
			childOutgoingPaymentConversion:
				'Outgoing payment with conversion processed (child)',
			childNonrealizedFirstStandingOrder:
				'Standing order not processed (child)',
			childNonrealizedFirstStandingOrderAgg:
				'Standing orders not processed (child)',
			childNonrealizedFirstDirectDebit: 'Direct debit not processed (child)',
			childNonrealizedFirstFuture: 'Payment not processed (child)',
			childNonrealizedFirstFutureAgg: 'Payments not processed (child)',
			childNonrealizedExpiredStandingOrder:
				'Standing order not processed due to insufficient funds (child)',
			childNonrealizedExpiredStandingOrderAgg:
				'Standing orders not processed due to insufficient funds (child)',
			childNonrealizedExpiredDirectDebit:
				'Direct debit not processed due to insufficient funds (child)',
			childNonrealizedExpiredFuture:
				'Payment not processed due to insufficient funds (child)',
			childNonrealizedExpiredFutureAgg:
				'Payments not processed due to insufficient funds (child)',
			childNonrealizedFraud:
				'Payment not processed due to suspected fraud (child)',
			childNonrealizedLowLimitDirectDebit:
				'Direct debit not processed due to insufficient limit (child)',
			disposableCardRenewed: 'Disposable card created',
			childDisposableCardRenewed: 'Disposable card created (child)',
			outgoingIncomingBalanceRealignment: 'Balance realignment',
		},
		table: {
			header: {
				event: 'Event',
				client: 'Client',
				user: 'User',
				date: 'Date',
			},
			detail: {
				clientDataShown: {
					viewReason: 'Reason for view',
					specifyingReason: 'Specifying reason',
				},
				clientDataShowFailed: {
					error: 'Error',
				},
				additionalData: {
					title: 'Additional data',
					applicationTitle: 'Application',
					application: {
						backoffice: 'BOA',
						mobileApplication: 'Mobile application',
						unknown: 'Unknown',
					},
					eventCategoryTitle: 'Category',
					eventCategory: {
						bankCards: 'Cards',
						bankDocuments: 'Documents',
						bankTransactions: 'Transactions',
						client: 'Client and identity',
						communication: 'Communication',
						internalApplication: 'Internal BOA',
						mobileApplication: 'Mobile application',
						unknown: 'Unknown',
					},
					processIdTitle: 'Process ID',
					clientPidTitle: 'Client PID',
					userPidTitle: 'User PID',
					userIdTitle: 'User ID',
				},
			},
		},
	},
	clickToPayPage: {
		title: 'Click To Pay',
		noCards: 'No cards with Click To Pay',
		infoText: 'Number of found cards: {cardsAmount}',
		visaStatus: {
			activating: 'Activation in progress',
			active: 'Active',
			disabling: 'Shutdown in progress',
			disabled: 'Off',
		},
		table: {
			header: {
				visaStatus: 'Click To Pay Status',
			},
			detail: {
				bankCardUuid: 'Card ID',
				status: 'Card status',
				showCardDetail: 'Show card detail',
			},
		},
		filter: {
			bankCardUuid: 'Card ID',
			visaStatus: 'Click To Pay Status',
			status: 'Card status',
		},
	},
	infoPage: {
		clientVersion: 'App version: {version}',
		infoGroup: 'App group: {group}',
		infoName: 'App name: {name}',
	},

	accessDeniedPage: {
		companyName: 'Partners',
		productName: 'Back Office',
		title: 'Access Denied',
		message:
			'The account you are logged in with in FIP does not have permission to access the Dashboard.',
		possibleReasonsLink: 'Possible reasons?',
		reasonsDialog: {
			title: 'Possible reasons for access denial',
			legalPersonReason: 'You are logged in under the legal entity account',
			legalPersonDescription:
				'Logging in under branch accounts, VIPCO, or other legal entities is not permitted. Please log in with your personal account.',
			blockedAccessReason: 'You have blocked access',
			blockedAccessDescription:
				'Your personal account has blocked access to the Dashboard for some reason. Please contact support for verification.',
			historicalAccessNote:
				'If you have historically worked in the Dashboard and now access does not work for an unknown reason, please contact support.',
			close: 'Close',
		},
	},
	profilePage: {
		noProfileData: 'No user data',
		noRoles: 'No assigned roles',
		roles: 'Roles',
		sections: {
			basicInfo: 'Basic information',
			roles: 'Assigned roles',
		},
		title: 'Profile',
		userName: 'User name',
		userEmail: 'Email',
		userPidUuid: 'PID',
	},
} satisfies Translation;

export default en;
